import { buildCatalogVarietyIndex } from '../utils/catalogIndex';
import { getPosCatalog } from '../api/posCatalogApi';
import { previewPosWeighing } from '../utils/posWeightPromotionPreview';
import { formatPesoAmount } from '../utils/money';
import useCatalogUpdateQueue from '../hooks/useCatalogUpdateQueue';
import { fetchCatalogDelta, mergeCatalogDelta, shareCatalogValue } from '../utils/catalogDelta';
import { posClientPricingContext, resolvePosPriceSnapshot, prunePosPriceSnapshots, retainUnavailableCartRows, preserveActiveLineDiscounts } from '../utils/posPriceSnapshots';
import useSaleRoundingSettings from '../hooks/useSaleRoundingSettings';
import { previewSaleRounding, saveWithSaleTotalConfirmation } from '../utils/saleRounding';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import CartPanel, { computeCartNonDiscountableSubtotal } from '../components/CartPanel';
import CategoryFilterBar from '../components/CategoryFilterBar';
import DocumentHeader from '../components/DocumentHeader';
import Modal from '../components/Modal';
import DiscountModal from '../components/DiscountModal';
import VarietyDetailModal from '../components/VarietyDetailModal';
import ModalClientRegistration from '../components/ModalClientRegistration';
import PurchaseDetailsModal from '../components/PurchaseDetailsModal';
import LoadingOverlay from '../components/LoadingOverlay';
import OrderDiscountModal from '../components/OrderDiscountModal';
import DiscountAuthorizationModal from '../components/DiscountAuthorizationModal';
import PendingSalesTabs from '../components/PendingSalesTabs';
import DocumentTypeModal from '../components/DocumentTypeModal';
import PosSessionModal from '../components/PosSessionModal';
import ProductGrid from '../components/ProductGrid';
import PosProductCard from '../components/PosProductCard';
import ProductViewToggle from '../components/ProductViewToggle';
import CancelPendingSaleModal from '../components/CancelPendingSaleModal';
import TaxExclusionModal from '../components/TaxExclusionModal';
import ScaleStatusPanel from '../components/ScaleStatusPanel';
import PosModeTabs from '../components/PosModeTabs';
import PosSlotGrid from '../components/PosSlotGrid';
import PosSlotPickerModal from '../components/PosSlotPickerModal';
import PosFavGestureGuide from '../components/PosFavGestureGuide';
import PosClientView from '../components/PosClientView';
import VarietyGroupTabs from '../components/VarietyGroupTabs';
import BarcodeCameraScanner from '../components/BarcodeCameraScanner';
import PosBarcodeSearchInput from '../components/PosBarcodeSearchInput';
import useCurrentUser from '../hooks/useCurrentUser';
import useConfirmDialog from '../hooks/useConfirmDialog';
import useMyPreferences from '../hooks/useMyPreferences';
import useProductViewPreference from '../hooks/useProductViewPreference';
import useProductSearchSettings from '../hooks/useProductSearchSettings';
import useProductEvents from '../hooks/useProductEvents';
import useCatalogRefreshState from '../hooks/useCatalogRefreshState';
import usePosNavbarCart from '../hooks/usePosNavbarCart';
import useScaleStream from '../hooks/useScaleStream';
import usePosSyncSession from '../hooks/usePosSyncSession';
import useActivePurchasePreview from '../hooks/useActivePurchasePreview';
import usePromotionClock from '../hooks/usePromotionClock';
import {
    getProducts,
    getCategories,
    registerPurchase,
    getClientById,
    getClientPricingDiscounts,
    updatePurchase,
    getPurchaseById,
    addPurchasePayment,
    getBranches,
    getCashRegisters,
    openCashRegisterSession,
    closeCashRegisterSession,
    getCashRegisterExpectedCash,
    getCashRegisterOpenSales,
    getBranchTaxes,
    getPosSoldOutDisplaySettings,
    convertPurchaseToQuotation,
    deletePendingPurchase,
    suspendPurchase,
    resumePurchase,
    resumePurchaseByCode,
    sendPrintRequest,
    getMyRegisterlessPosProfile,
    getBarcodePricingRule,
} from '../api';
import {
    decodeVariableBarcode,
    resolveBarcodeMode,
    buildScaleBarcodeLookupKeys,
    buildShortBarcodeLookupKeys,
} from '../utils/barcode';
import { getBarcodePrefixMappings } from '../utils/barcodeSettings';
import { normalizeClientType, resolvePriceClientType, getClientTypeLabel } from '../utils/clientType';
import useProductFormatOptions from '../hooks/useProductFormatOptions';
import { buildPosFormatFilterOptions, matchesPosFormatFilter, resolvePosFormatFilter } from '../utils/posFormatFilters';
import { hasPermission, isAdminUser } from '../utils/permissions';
import { normalizePaginatedResponse } from '../utils/pagination';
import { getPurchaseItems } from '../utils/purchaseItems';
import { mergeProductMatrix } from '../utils/productMatrix';
import { computeFormatOfferSubtotal, expandProductsWithFormatOffers, getFormatOfferId, getSaleOptionId, isFormatOfferPrice, isFormatOfferSelectable, getFormatOfferCartMessage, getFormatOfferLineIssue } from '../utils/formatOffers';
import { buildSettingsPath } from '../config/settingsNavigation';
import { getPrintMode, PRINT_MODE_REMOTE } from '../utils/printSettings';
import { normalizeQuantity } from '../utils/quantity';
import {
    MAX_PURCHASE_ITEM_QUANTITY,
    buildPurchaseItemQuantityLimitMessage,
    findInvalidPurchaseItemQuantity,
} from '../utils/purchaseItemLimits';
import { computeTaxAdjustedTotals } from '../utils/taxTotals';
import {
    applyPromotionsToLineItems,
    buildPromotionLineItem,
    computeLineItemsNonDiscountableSubtotal,
    computeLineItemsSubtotal,
    computeOrderTotalsFromSubtotal,
} from '../utils/promotionPricing';
import { collectCatalogPromotions } from '../utils/priceListPromotions';
import { applyClientPricingDiscountsToMatrices } from '../utils/clientPricingDiscounts';
import {
    REGISTERLESS_POST_SUBMIT_DESTINATION_NEW_SALE,
    getPosRegisterStorageKey,
    getPosRegisterlessStorageKey,
    getPosSessionStorageKey,
    resolveCurrentPosScope,
    resolveRegisterlessPostSubmitDestination,
    shouldDefaultToRegisterlessPosScope,
} from '../utils/posRegisterScope';
import {
    POS_VIEW_EDITOR,
    buildPosWindowName,
    buildPosViewSearchParams,
    normalizePosView,
} from '../utils/posSync';
import {
    TEMPORARY_POS_QUICK_FINALIZE_DOCUMENT_TYPE,
    TEMPORARY_POS_QUICK_FINALIZE_ENABLED,
    TEMPORARY_POS_QUICK_FINALIZE_PAYMENT_METHOD,
    buildTemporaryPosQuickFinalizePaymentUpdate,
    buildTemporaryPosQuickFinalizeCheckout,
    buildTemporaryPosQuickFinalizeReceiptPath,
    getTemporaryPosQuickFinalizeBalance,
} from '../utils/temporaryPosQuickFinalize';
import { getScaleDisplayWeight, isScaleInputPrice } from '../utils/scalePresentation';
import {
    buildScaleReadingOrderEntry,
    createScaleReadingLine,
    findDuplicateScaleReadingLine,
    getCartIdentityKey,
    isScaleReadingLine,
    lineMatchesPrice,
} from '../utils/posScaleReadingLines';
import {
    POS_FAV_TAP_ACTIONS,
    isPosFavScaleConnected,
    resolvePosFavTapAction,
} from '../utils/posFavInteraction';
import {
    buildLineKey,
    normalizeBarcodeValue,
    orderAndIndexLineItems as orderAndIndexDocumentLineItems,
    varietySupportsBarcodePriceTypeSelection as supportsBarcodePriceTypeSelection,
} from '../utils/documentLineItems';
import {
    buildDisallowedClientTypeMessage,
    collectDisallowedPosClientTypeIssues,
    isPriceAllowedForPosScope,
    normalizeAllowedClientTypesForPosScope,
    resolvePosLinePriceTypePayload,
} from '../utils/posClientTypeScope';
import {
    DEFAULT_BARCODE_PRICING_RULE,
    getResolvedPosAutoPriceLine,
    normalizeBarcodePricingRule,
    resolvePosAutoPriceSelection,
} from '../utils/posAutoPriceSelection';
import {
    EMPTY_POS_SALE_MESSAGE,
    buildPosClientAssignmentPayload,
    hasPosSaleItems,
    shouldResetDocumentTypeAfterClientModal,
} from '../utils/posSaleDraft';
import InlineLoading from '../components/InlineLoading';
import {
    FiArrowLeft,
    FiChevronLeft,
    FiChevronRight,
    FiPercent,
    FiPrinter,
    FiPauseCircle,
    FiCheckCircle,
    FiInfo,
    FiSearch,
    FiShoppingCart,
    FiX,
} from 'react-icons/fi';
import { notify } from '../utils/alertBus';
import {
    buildPosSlotLayoutScopeKey,
    buildPosSlotPickerEntries,
    buildResolvedPosSlots,
    getPosSlotLayout,
    setPosSlotLayout,
} from '../utils/posSlotLayouts';
import {
    DEFAULT_POS_SEARCH_SUGGESTION_VISIBILITY,
    POS_SOLD_OUT_MODE_NORMAL,
    filterProductsBySoldOutDisplay,
    filterVarietiesBySoldOutDisplay,
    isPosProductSoldOutForPrices,
    isPosVarietySoldOutForPrices,
    normalizePosSearchSuggestionVisibility,
    normalizePosSoldOutMode,
    orderBySoldOutDisplay,
    prepareProductsForSoldOutDisplay,
    prepareVarietiesForSoldOutDisplay,
} from '../utils/posSoldOutDisplay';
import { buildInventoryWarningText } from '../utils/inventoryFormatting';
import { ALL_VARIETY_GROUP_ID, filterVarietiesByGroup } from '../utils/varietyGroups';
import {
    fieldsMatchProductSearchTerms,
    getProductSearchMatch,
    tokenizeProductSearchQuery,
} from '../utils/productSearch';

const SUSPENDED_SALE_PREFIX = 'SUSP-';
const POS_PRODUCT_VIEW_STORAGE_KEY = 'posProductViewPreference';
const POS_PRODUCT_VIEWS = ['grid', 'slots'];
const POS_DESKTOP_MEDIA_QUERY = '(min-width: 768px)';

const isDesktopViewport = () => (
    typeof window !== 'undefined'
    && window.matchMedia(POS_DESKTOP_MEDIA_QUERY).matches
);

const getApiErrorMessage = (error, fallback) => (
    error?.response?.data?.message
    || error?.response?.data?.error
    || error?.message
    || fallback
);

const OnlineShop = () => {
    const productFormatOptions = useProductFormatOptions();
    const [searchParams, setSearchParams] = useSearchParams();
    const posView = normalizePosView(searchParams.get('posView'));
    const isEditorPosView = posView === POS_VIEW_EDITOR;
    const location = useLocation();
    const [modalVariety, setModalVariety] = useState(null);
    const [modalVarietyPrice, setModalVarietyPrice] = useState(null);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    const [categoriesError, setCategoriesError] = useState(null);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [searchVarietyResults, setSearchVarietyResults] = useState([]);
    const [suggestedSearchVarietyResults, setSuggestedSearchVarietyResults] = useState([]);
    const [quantities, setQuantities] = useState({});
    const [totalAmount, setTotalPrice] = useState(0);
    const [showPurchaseCard, setShowPurchaseCard] = useState(false);
    const [showPurchaseModal, setShowPurchaseModal] = useState(false);
    const [showDesktopCartPane, setShowDesktopCartPane] = useState(false);
    const [purchaseDetails, setPurchaseDetails] = useState(null);
    const [name, setName] = useState('');
    const [rut, setRut] = useState('');
    const [email, setEmail] = useState('');
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [phone, setPhone] = useState('');
    const [giro, setGiro] = useState('');
    const [clientId, setClientId] = useState(null);
    const clientIdRef = useRef(null);
    const [clientType, setClientType] = useState('detalle');
    const [showTypeModal, setShowTypeModal] = useState(false);
    const [typeSelection, setTypeSelection] = useState('detalle');
    const [purchaseId, setPurchaseId] = useState(null);
    const purchaseIdRef = useRef(null);
    const purchaseWriteQueueRef = useRef(Promise.resolve());
    const purchaseStateGenerationRef = useRef(0);
    const [modalProduct, setModalProduct] = useState(null);
    const [activeVarietyGroupId, setActiveVarietyGroupId] = useState(ALL_VARIETY_GROUP_ID);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingProcess, setLoadingProcess] = useState('Procesando venta…');
    const navigate = useNavigate();
    const branchId = useParams().branchId || localStorage.getItem('selectedBranchId');
    const saleRounding = useSaleRoundingSettings(branchId);
    const quickFinalizeInFlightRef = useRef(false);
    const searchInputRef = useRef(null);
    const desktopCartPaneRef = useRef(null);
    const pendingClientTypeRef = useRef(null);
    const openingSessionRequestRef = useRef(false);
    const draftStartedAtRef = useRef(new Date().toISOString());
    const [discounts, setDiscounts] = useState({});
    const [discountAuthorizations, setDiscountAuthorizations] = useState({});
    const [orderDiscountAuthorizationId, setOrderDiscountAuthorizationId] = useState(null);
    const [showDiscountModal, setShowDiscountModal] = useState(false);
    const [showDiscountAuthorizationModal, setShowDiscountAuthorizationModal] = useState(false);
    const [pendingDiscountAuthorization, setPendingDiscountAuthorization] = useState(null);
    const [pendingDiscountRequests, setPendingDiscountRequests] = useState({ items: {}, order: null });
    const [discountAuthorizationInitialState, setDiscountAuthorizationInitialState] = useState(null);
    const [addedOrder, setAddedOrder] = useState([]);
    const [itemsSubtotal, setItemsSubtotal] = useState(0);
    const [orderDiscountAmount, setOrderDiscountAmount] = useState(null);
    const [orderDiscountPercentage, setOrderDiscountPercentage] = useState(null);
    const [showOrderDiscountModal, setShowOrderDiscountModal] = useState(false);
    const [taxes, setTaxes] = useState([]);
    const [taxesLoading, setTaxesLoading] = useState(false);
    const [taxesError, setTaxesError] = useState(null);
    const [excludedTaxIds, setExcludedTaxIds] = useState([]);
    const [promotions, setPromotions] = useState([]);
    const [promotionClock, setPromotionClock] = useState(null);
    const showInventoryWarnings = useCallback((warnings) => {
        const warningText = buildInventoryWarningText(warnings);
        if (warningText) {
            notify(`Advertencia inventario:\n${warningText}`);
        }
    }, []);
    const [showTaxModal, setShowTaxModal] = useState(false);
    const [showDocumentTypeModal, setShowDocumentTypeModal] = useState(false);
    const [pendingFinishAfterClient, setPendingFinishAfterClient] = useState(false);
    const [pendingConversionAfterClient, setPendingConversionAfterClient] = useState(false);
    const [pendingConversionAfterType, setPendingConversionAfterType] = useState(false);
    const [pendingDocumentType, setPendingDocumentType] = useState(null);
    const [documentType, setDocumentType] = useState(null);
    const [cashRegisters, setCashRegisters] = useState([]);
    const [posModalBranches, setPosModalBranches] = useState([]);
    const [posModalRegistersByBranch, setPosModalRegistersByBranch] = useState({});
    const [posModalCategoriesByBranch, setPosModalCategoriesByBranch] = useState({});
    const [posModalLoading, setPosModalLoading] = useState(false);
    const [posModalError, setPosModalError] = useState('');
    const [cashRegisterId, setCashRegisterId] = useState(null);
    const [cashRegisterSessionId, setCashRegisterSessionId] = useState(null);
    const [registerlessProfile, setRegisterlessProfile] = useState(null);
    const [isRegisterlessMode, setIsRegisterlessMode] = useState(false);
    const [saleName, setSaleName] = useState('');
    const [pendingTabsScopeReady, setPendingTabsScopeReady] = useState(false);
    const [showPosSessionModal, setShowPosSessionModal] = useState(false);
    const [isOpeningSession, setIsOpeningSession] = useState(false);
    const [showCloseSessionModal, setShowCloseSessionModal] = useState(false);
    const [cancelSaleTarget, setCancelSaleTarget] = useState(null);
    const [expectedCash, setExpectedCash] = useState(null);
    const [expectedCashLoading, setExpectedCashLoading] = useState(false);
    const [expectedCashError, setExpectedCashError] = useState(null);
    const [openSessionSales, setOpenSessionSales] = useState([]);
    const [openSessionSalesLoading, setOpenSessionSalesLoading] = useState(false);
    const [openSessionSalesError, setOpenSessionSalesError] = useState(null);
    const [posError, setPosError] = useState('');
    const [linePriceMeta, setLinePriceMeta] = useState({});
    const salePriceSnapshotsRef = useRef(new Map());
    const [catalogPriceNotice, setCatalogPriceNotice] = useState('');
    useEffect(() => {
        if (!catalogPriceNotice) return undefined;
        const timer = setTimeout(() => setCatalogPriceNotice(''), 5000);
        return () => clearTimeout(timer);
    }, [catalogPriceNotice]);

    const [barcodePricingRule, setBarcodePricingRule] = useState(DEFAULT_BARCODE_PRICING_RULE);
    const [clientPricingDiscountMap, setClientPricingDiscountMap] = useState({});
    const [scaleReadingLines, setScaleReadingLines] = useState([]);
    useEffect(() => {
        prunePosPriceSnapshots(salePriceSnapshotsRef.current, products, quantities, scaleReadingLines);
    }, [products, quantities, scaleReadingLines]);
    const [showSlotPickerModal, setShowSlotPickerModal] = useState(false);
    const [slotModalState, setSlotModalState] = useState(null);
    const [activeScaleSelection, setActiveScaleSelection] = useState(null);
    const [lastStableScaleReading, setLastStableScaleReading] = useState(null);
    const [productView, setProductView] = useProductViewPreference({
        storageKey: POS_PRODUCT_VIEW_STORAGE_KEY,
        allowedViews: POS_PRODUCT_VIEWS,
    });
    const [showMobileSearch, setShowMobileSearch] = useState(false);
    const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedSubcategory, setSelectedSubcategory] = useState('all');
    const [soldOutDisplayMode, setSoldOutDisplayMode] = useState(POS_SOLD_OUT_MODE_NORMAL);
    const [posSearchSuggestionVisibility, setPosSearchSuggestionVisibility] = useState(
        DEFAULT_POS_SEARCH_SUGGESTION_VISIBILITY
    );
    const { user } = useCurrentUser();
    const { confirm } = useConfirmDialog();
    const cartSaleRounding = previewSaleRounding({
        totalAmount,
        settings: isRegisterlessMode ? { enabled: false } : saleRounding.settings,
        checkoutPaymentMethods: TEMPORARY_POS_QUICK_FINALIZE_ENABLED ? [TEMPORARY_POS_QUICK_FINALIZE_PAYMENT_METHOD] : [],
    });
    const {
        preferences: myPreferences,
        loading: preferencesLoading,
        savePreferences: saveMyPreferences,
    } = useMyPreferences();
    const { searchMode } = useProductSearchSettings();
    const isAdmin = isAdminUser(user);
    const canCreateSale = hasPermission(user, 'sales.create');
    const canUpdateSale = hasPermission(user, 'sales.update');
    const canDeleteSale = hasPermission(user, 'sales.delete');
    const canReadSales = hasPermission(user, 'sales.read');
    const canReadBranches = hasPermission(user, 'branch.read');
    const canOpenPos = hasPermission(user, 'pos.open');
    const canClosePos = hasPermission(user, 'pos.close');
    const canSell = hasPermission(user, 'pos.sell');
    const canManageRegisters = hasPermission(user, 'cash_register.manage');
    const canCreateClient = hasPermission(user, 'client.create');
    const canReadClient = hasPermission(user, 'client.read');
    const canConvertToQuotation =
        hasPermission(user, 'quotation.convert_from_sale') || hasPermission(user, 'quotation.create');
    const canExcludeTaxes = hasPermission(user, 'tax.exclude');
    const canEditSale = purchaseId ? canUpdateSale : canCreateSale;
    const canChangeClientType = canCreateSale || canUpdateSale;
    const canOverrideLinePriceType = hasPermission(user, 'branch.access.any');
    const isSellablePrice = (price) => price && price.price !== null && price.price !== undefined;
    // Invariant: one active UI line per (varietyId, priceIndex) slot.
    const lineKey = buildLineKey;
    const orderAndIndexLineItems = (items, orderData = addedOrder) =>
        orderAndIndexDocumentLineItems(items, orderData, getCartIdentityKey);
    const normalizePriceTypeValue = (value) => normalizeClientType(value) || '';
    const getPriceTypeLabel = (type) => getClientTypeLabel(type, productFormatOptions) || type;
    const varietySupportsBarcodePriceTypeSelection = (variety) => {
        return supportsBarcodePriceTypeSelection(variety, isSellablePrice);
    };
    const resolveLinePriceTypePayload = (variety, price, meta) => {
        return resolvePosLinePriceTypePayload({
            price,
            meta,
            allowedClientTypes,
            canSelectPriceType: varietySupportsBarcodePriceTypeSelection(variety),
        });
    };
    const applyBarcodeAutoPriceSelectionMeta = (variety, priceIndex) => {
        if (!Number.isInteger(variety?.id) || !Number.isInteger(priceIndex) || priceIndex < 0) return;
        const key = lineKey(variety.id, priceIndex);
        const price = variety?.VarietyPrices?.[priceIndex];
        const defaultPriceType = normalizePriceTypeValue(resolvePriceClientType(price));
        const autoPriceSelection = !isFormatOfferPrice(price) && varietySupportsBarcodePriceTypeSelection(variety);
        setLinePriceMeta((prev) => ({
            ...prev,
            [key]: {
                ...prev[key],
                priceType: (
                    normalizePriceTypeValue(prev[key]?.priceType) === defaultPriceType
                    && (!allowedClientTypes.length || allowedClientTypes.includes(defaultPriceType))
                )
                    ? normalizePriceTypeValue(prev[key]?.priceType)
                    : defaultPriceType,
                priceTypeOverride: Boolean(prev[key]?.priceTypeOverride)
                    && normalizePriceTypeValue(prev[key]?.priceType) === defaultPriceType
                    && (!allowedClientTypes.length || allowedClientTypes.includes(defaultPriceType)),
                autoPriceSelection,
            },
        }));
    };
    const currentAutoPriceResolution = useMemo(() => resolvePosAutoPriceSelection({
        products,
        quantities,
        scaleReadingLines,
        linePriceMeta,
        globalRule: barcodePricingRule,
        isSellablePrice,
        canSelectPriceType: varietySupportsBarcodePriceTypeSelection,
        lineKey,
    }), [products, quantities, scaleReadingLines, linePriceMeta, barcodePricingRule]);
    const resolveEffectivePriceForCurrentLine = useCallback(({ variety, priceIndex, line }) => (
        getResolvedPosAutoPriceLine(currentAutoPriceResolution, {
            varietyId: variety?.id,
            priceIndex,
            cartLineId: line?.cartLineId,
        })?.priceRow || null
    ), [currentAutoPriceResolution]);
    const isPriceAllowedByRegister = (price) => {
        return isPriceAllowedForPosScope(price, allowedClientTypes, isSellablePrice);
    };
    const isPriceAllowedForType = (type, price) => {
        if (!isPriceAllowedByRegister(price) || !isFormatOfferSelectable(price)) return false;
        return matchesPosFormatFilter(price, type, productFormatOptions);
    };
    const hasSellablePrice = (variety, type = clientType) =>
        (variety.VarietyPrices || []).some((price) => isPriceAllowedForType(type, price));
    const hasProductSellablePrice = (product, type = clientType) =>
        (product?.Varieties || []).some((variety) => hasSellablePrice(variety, type));
    const getVarietyPricesForFilterType = (variety, type = clientType) =>
        (variety.VarietyPrices || []).filter((price) => isPriceAllowedForType(type, price));
    const isPriceSelectableForType = (type, price) =>
        isPriceAllowedForType(type, price) && !price?.isSoldOut;
    const isVarietySoldOutForFilter = (variety, type = clientType) =>
        isPosVarietySoldOutForPrices(variety, (price) => isPriceAllowedForType(type, price));
    const isProductSoldOutForFilter = (product, type = clientType) =>
        isPosProductSoldOutForPrices(product, (price) => isPriceAllowedForType(type, price));

    const normalizeSuspendedCodeInput = (value) => {
        if (!value) return '';
        return String(value).trim().toUpperCase();
    };

    const posRegisterStorageKey = getPosRegisterStorageKey(branchId);
    const posSessionStorageKey = getPosSessionStorageKey(branchId);
    const posRegisterlessStorageKey = getPosRegisterlessStorageKey(branchId);
    const notifyPosSessionUpdate = useCallback(() => {
        window.dispatchEvent(new CustomEvent('pos-session-updated'));
    }, []);
    const activeCashRegister = cashRegisters.find(
        (register) => String(register.id) === String(cashRegisterId)
    );
    const activeRegisterlessProfile = isRegisterlessMode ? registerlessProfile : null;
    const activeSession = activeCashRegister?.activeSession || null;
    const isActiveSessionOwnedByUser = activeSession
        ? String(activeSession.openedBy) === String(user?.id)
        : false;
    const scaleState = useScaleStream({
        companyId: user?.selectedCompanyId,
        userId: user?.id,
        isEditor: isEditorPosView,
        branchId,
        cashRegisterId,
        cashRegisterSessionId,
        enabled: Boolean(
            branchId
            && cashRegisterId
            && cashRegisterSessionId
            && isActiveSessionOwnedByUser
            && canSell
        ),
    });
    const scaleDisplayWeightKg = getScaleDisplayWeight(scaleState);
    const posFavScaleConnected = isPosFavScaleConnected(scaleState);
    const posSyncEnabled = Boolean(
        branchId
        && cashRegisterId
        && cashRegisterSessionId
        && isActiveSessionOwnedByUser
        && canSell
    );
    const {
        connectionStatus: posSyncConnectionStatus,
        activePurchaseId: syncedActivePurchaseId,
        weighing: posSyncWeighing,
        preview: posSyncPreview,
        hasEditor: posSyncHasEditor,
        hasClient: posSyncHasClient,
        lastPurchaseChanged: posSyncLastPurchaseChanged,
        sessionClosed: posSyncSessionClosed,
        lastError: posSyncLastError,
        setActivePurchaseId: publishActivePurchaseId,
        setWeighing: publishSharedWeighing,
        setPreview: publishSharedPreview,
    } = usePosSyncSession({
        branchId,
        cashRegisterId,
        cashRegisterSessionId,
        userId: user?.id,
        enabled: posSyncEnabled,
        mode: posView,
    });
    useEffect(() => {
        const rawWeightKg = scaleState?.freshStableWeightKg ?? scaleState?.latestStableWeightKg;
        const weightKg = Number(rawWeightKg);
        if (rawWeightKg == null || !Number.isFinite(weightKg) || weightKg < 0) return;
        setLastStableScaleReading({
            weightKg,
            stableAt: scaleState?.freshStableAt || scaleState?.latestStableAt || new Date().toISOString(),
        });
    }, [
        scaleState?.freshStableAt,
        scaleState?.freshStableWeightKg,
        scaleState?.latestStableAt,
        scaleState?.latestStableWeightKg,
    ]);
    useEffect(() => {
        setLastStableScaleReading(null);
        setActiveScaleSelection(null);
    }, [branchId, cashRegisterId, cashRegisterSessionId]);
    const activePurchasePreviewRefreshToken =
        posSyncLastPurchaseChanged && Number(posSyncLastPurchaseChanged.purchaseId) === Number(syncedActivePurchaseId)
            ? `${posSyncLastPurchaseChanged.purchaseId}:${posSyncLastPurchaseChanged.updatedAt || ''}`
            : String(syncedActivePurchaseId || '');
    const {
        purchase: activePurchasePreview,
        loading: activePurchasePreviewLoading,
        error: activePurchasePreviewError,
    } = useActivePurchasePreview({
        branchId,
        activePurchaseId: syncedActivePurchaseId,
        enabled: posSyncEnabled && !isEditorPosView
            && !(posSyncPreview && String(posSyncPreview.id || '') === String(syncedActivePurchaseId || '')),
        refreshToken: activePurchasePreviewRefreshToken,
    });
    const helperSharedPurchase = posSyncPreview || activePurchasePreview || null;
    const helperSharedPurchaseLoading = !posSyncPreview && activePurchasePreviewLoading;
    const helperSharedPurchaseError = !posSyncPreview ? activePurchasePreviewError : null;
    const isSlotProductView = productView === 'slots';
    const normalizeLocalSearchTerm = useCallback((value) => String(value || '').toLowerCase(), []);
    const normalizeLocalCategory = useCallback((value) => {
        const normalized = String(value || '').trim();
        if (!normalized || normalized === 'null' || normalized === 'undefined') return 'all';
        return normalized.toLowerCase() === 'all' ? 'all' : normalized;
    }, []);
    const applySearch = useCallback((nextValue) => {
        const normalized = normalizeLocalSearchTerm(nextValue);
        setSearchTerm(normalized);
        return normalized;
    }, [normalizeLocalSearchTerm]);
    const handleSearch = useCallback((event) => {
        applySearch(event?.target?.value ?? '');
    }, [applySearch]);
    const focusPrimarySearchInput = useCallback((delay = 0) => {
        const focusInput = () => {
            searchInputRef.current?.focus();
            searchInputRef.current?.select();
        };
        if (delay > 0) {
            return setTimeout(focusInput, delay);
        }
        focusInput();
        return null;
    }, []);
    const handleCategorySelect = useCallback((category) => {
        setSelectedCategory(normalizeLocalCategory(category));
        setSelectedSubcategory('all');
    }, [normalizeLocalCategory]);
    const handleSubcategorySelect = useCallback((subcategory) => {
        const normalized = normalizeLocalCategory(subcategory);
        if (normalized === 'back') {
            setSelectedCategory('all');
            setSelectedSubcategory('all');
            return;
        }
        setSelectedSubcategory((current) => (String(current) === String(normalized) ? 'all' : normalized));
    }, [normalizeLocalCategory]);
    const hasActiveSearch = Boolean((searchTerm || '').trim());
    const mobileSearchVisible = showMobileSearch || hasActiveSearch;
    const allowedClientTypes = useMemo(() => {
        const sourceTypes = activeRegisterlessProfile?.allowedProductFormatCodes
            || activeRegisterlessProfile?.allowedClientTypes
            || activeCashRegister?.allowedProductFormatCodes
            || activeCashRegister?.allowedClientTypes;
        return normalizeAllowedClientTypesForPosScope(sourceTypes);
    }, [
        activeCashRegister?.allowedProductFormatCodes,
        activeCashRegister?.allowedClientTypes,
        activeRegisterlessProfile?.allowedProductFormatCodes,
        activeRegisterlessProfile?.allowedClientTypes,
    ]);
    const allowedClientTypeOptions = useMemo(() => {
        if (!allowedClientTypes.length) return productFormatOptions;
        return productFormatOptions.filter((option) => allowedClientTypes.includes(option.value));
    }, [allowedClientTypes, productFormatOptions]);
    const posFormatFilterOptions = useMemo(
        () => buildPosFormatFilterOptions(productFormatOptions, allowedClientTypes),
        [productFormatOptions, allowedClientTypes]
    );
    const resolveAllowedClientType = useCallback((value) => {
        const filter = resolvePosFormatFilter(value, productFormatOptions);
        if (!filter || !allowedClientTypes.length) return filter;
        return allowedClientTypes.some((code) => resolvePosFormatFilter(code, productFormatOptions) === filter)
            ? filter
            : posFormatFilterOptions[0]?.value || '';
    }, [allowedClientTypes, posFormatFilterOptions, productFormatOptions]);

    useEffect(() => {
        let isMounted = true;
        const loadBarcodePricingRule = async () => {
            try {
                const rule = await getBarcodePricingRule();
                if (isMounted) {
                    setBarcodePricingRule(normalizeBarcodePricingRule(rule, DEFAULT_BARCODE_PRICING_RULE));
                }
            } catch (error) {
                console.warn('No se pudo cargar la regla global de selección automática.', error);
            }
        };
        loadBarcodePricingRule();
        return () => {
            isMounted = false;
        };
    }, []);

    const availabilityForCategory = (category) =>
        category?.CategoryAvailabilities
        || category?.CategoryAvailability
        || [];
    const categoryById = useMemo(() => {
        const map = new Map();
        (categories || []).forEach((category) => {
            if (Number.isInteger(category?.id)) {
                map.set(category.id, category);
            }
        });
        return map;
    }, [categories]);
    const resolveProductCategoryId = useCallback((product) => {
        return product?.categoryId ?? product?.Category?.id ?? null;
    }, []);
    const resolveProductSubcategoryId = useCallback((product) => {
        return product?.subcategoryId ?? product?.Subcategory?.id ?? null;
    }, []);
    const resolveProductCategorySnapshot = useCallback((product) => {
        const categoryId = resolveProductCategoryId(product);
        const category = Number.isInteger(categoryId) ? categoryById.get(categoryId) : null;
        return {
            categoryId,
            categoryName: category?.name || product?.Category?.name || null,
        };
    }, [categoryById, resolveProductCategoryId]);
    const allowedCategoryIds = useMemo(() => {
        if (isRegisterlessMode) {
            const categoryIds = Array.isArray(registerlessProfile?.categoryIds)
                ? registerlessProfile.categoryIds
                : [];
            return new Set(categoryIds.map((id) => Number(id)).filter((id) => Number.isInteger(id)));
        }
        if (!cashRegisterId || !branchId || !(categories || []).length) return null;
        const resolvedBranchId = Number(branchId);
        const resolvedRegisterId = Number(cashRegisterId);
        if (!Number.isFinite(resolvedBranchId) || !Number.isFinite(resolvedRegisterId)) return null;
        const allowed = new Set();
        (categories || []).forEach((category) => {
            if (category?.isActive === false) return;
            const rows = availabilityForCategory(category);
            const hasAccess = rows.some((row) => {
                if (row?.isActive === false) return false;
                if (Number(row.branchId) !== resolvedBranchId) return false;
                if (row.cashRegisterId == null) return true;
                return Number(row.cashRegisterId) === resolvedRegisterId;
            });
            if (hasAccess) {
                allowed.add(category.id);
            }
        });
        return allowed;
    }, [branchId, cashRegisterId, categories, isRegisterlessMode, registerlessProfile?.categoryIds]);
    const availableProducts = useMemo(() => {
        if (!allowedCategoryIds) return products;
        if (allowedCategoryIds.size === 0) return [];
        return (products || []).filter((product) =>
            allowedCategoryIds.has(resolveProductCategoryId(product))
        );
    }, [products, allowedCategoryIds, resolveProductCategoryId]);
    const priceAvailableProducts = useMemo(
        () => (availableProducts || []).filter((product) => hasProductSellablePrice(product, clientType)),
        [availableProducts, clientType, allowedClientTypes, productFormatOptions]
    );
    const displayProducts = useMemo(
        () => filterProductsBySoldOutDisplay(priceAvailableProducts, soldOutDisplayMode, isProductSoldOutForFilter),
        [priceAvailableProducts, soldOutDisplayMode, clientType, allowedClientTypes, productFormatOptions]
    );
    const categoriesForDisplay = useMemo(() => {
        const active = (categories || []).filter((category) => category?.isActive !== false);
        const allowed = allowedCategoryIds
            ? active.filter((category) => allowedCategoryIds.has(category.id))
            : active;
        return allowed.map((category) => ({
            ...category,
            CategorySubcategories: (category?.CategorySubcategories || [])
                .slice()
                .sort((a, b) =>
                    Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0)
                    || String(a?.name || '').localeCompare(String(b?.name || ''), 'es', { sensitivity: 'base' })
                ),
        }));
    }, [categories, allowedCategoryIds]);
    const categoryIdsWithProducts = useMemo(() => {
        const ids = new Set();
        (displayProducts || []).forEach((product) => {
            const id = resolveProductCategoryId(product);
            if (Number.isInteger(id)) ids.add(id);
        });
        return ids;
    }, [displayProducts, resolveProductCategoryId]);
    const displayCategories = useMemo(
        () => categoriesForDisplay.filter((category) => categoryIdsWithProducts.has(category.id)),
        [categoriesForDisplay, categoryIdsWithProducts]
    );
    const subcategoryIdsWithProducts = useMemo(() => {
        const ids = new Set();
        if (selectedCategory === 'all') return ids;
        (displayProducts || []).forEach((product) => {
            if (String(resolveProductCategoryId(product)) !== String(selectedCategory)) return;
            const id = Number(resolveProductSubcategoryId(product));
            if (Number.isInteger(id)) ids.add(id);
        });
        return ids;
    }, [displayProducts, resolveProductCategoryId, resolveProductSubcategoryId, selectedCategory]);
    const displaySubcategories = useMemo(() => {
        if (selectedCategory === 'all') return [];
        const category = displayCategories.find(
            (entry) => String(entry.id) === String(selectedCategory)
        );
        return (category?.CategorySubcategories || [])
            .filter((subcategory) => subcategoryIdsWithProducts.has(subcategory.id))
            .map((subcategory) => ({
                ...subcategory,
                label: subcategory.name,
            }));
    }, [displayCategories, selectedCategory, subcategoryIdsWithProducts]);
    const categoriesToRender = useMemo(() => {
        if (searchTerm || selectedCategory === 'all') return displayCategories;
        return displayCategories.filter(
            (category) => String(category.id) === String(selectedCategory)
        );
    }, [displayCategories, searchTerm, selectedCategory]);
    const loadOpenSessionSales = useCallback(async () => {
        if (!branchId || !activeCashRegister || !activeSession || !isActiveSessionOwnedByUser) {
            setOpenSessionSales([]);
            setOpenSessionSalesLoading(false);
            if (activeSession && !isActiveSessionOwnedByUser) {
                setOpenSessionSalesError('Caja abierta por otro usuario.');
            }
            return;
        }
        setOpenSessionSalesLoading(true);
        setOpenSessionSalesError(null);
        try {
            const response = await getCashRegisterOpenSales(branchId, activeCashRegister.id);
            const sales = Array.isArray(response?.openSales) ? response.openSales : [];
            const filtered = sales.filter((sale) => sale.saleStatus === 'open');
            setOpenSessionSales(filtered);
        } catch (error) {
            console.error('Error fetching open session sales:', error);
            setOpenSessionSales([]);
            setOpenSessionSalesError('No se pudo obtener las ventas abiertas.');
        } finally {
            setOpenSessionSalesLoading(false);
        }
    }, [branchId, activeCashRegister, activeSession, isActiveSessionOwnedByUser]);

    useEffect(() => {
        if (!showCloseSessionModal) {
            setExpectedCash(null);
            setExpectedCashLoading(false);
            setExpectedCashError(null);
            return;
        }
        if (!activeCashRegister || !activeSession || !isActiveSessionOwnedByUser) {
            setExpectedCash(null);
            setExpectedCashLoading(false);
            if (activeSession && !isActiveSessionOwnedByUser) {
                setExpectedCashError('Caja abierta por otro usuario.');
            }
            return;
        }
        let isMounted = true;
        const loadExpectedCash = async () => {
            setExpectedCashLoading(true);
            setExpectedCashError(null);
            try {
                const response = await getCashRegisterExpectedCash(branchId, activeCashRegister.id);
                if (!isMounted) return;
                const parsed = Number(response?.expectedCash);
                setExpectedCash(Number.isFinite(parsed) ? parsed : null);
            } catch (error) {
                if (!isMounted) return;
                console.error('Error fetching expected cash:', error);
                setExpectedCash(null);
                setExpectedCashError('No se pudo obtener el efectivo esperado.');
            } finally {
                if (!isMounted) return;
                setExpectedCashLoading(false);
            }
        };
        loadExpectedCash();
        return () => {
            isMounted = false;
        };
    }, [showCloseSessionModal, activeCashRegister?.id, activeSession?.id, branchId, isActiveSessionOwnedByUser]);

    useEffect(() => {
        if (!showCloseSessionModal) {
            setOpenSessionSales([]);
            setOpenSessionSalesLoading(false);
            setOpenSessionSalesError(null);
            return;
        }
        loadOpenSessionSales();
    }, [showCloseSessionModal, loadOpenSessionSales]);

    const varietyTaxProfileById = useMemo(() => {
        if (!canExcludeTaxes || !excludedTaxIds.length || !taxes.length) {
            return new Map();
        }
        const activeTaxes = (taxes || []).filter(
            (tax) => tax && tax.isActive !== false && tax.branchActive !== false
        );
        const taxesById = new Map(
            activeTaxes
                .map((tax) => [Number(tax.id), tax])
                .filter(([id]) => Number.isFinite(id))
        );
        const defaultTaxIds = activeTaxes
            .filter((tax) => tax.applyToAllProducts)
            .map((tax) => Number(tax.id))
            .filter((id) => Number.isFinite(id));
        const excludedSet = new Set(
            (excludedTaxIds || [])
                .map((id) => Number(id))
                .filter((id) => Number.isFinite(id))
        );
        const sumRate = (list) =>
            list.reduce((sum, tax) => {
                const rate = Number(tax?.rate);
                return sum + (Number.isFinite(rate) ? rate : 0);
            }, 0);

        const map = new Map();
        (products || []).forEach((product) => {
            const useDefaultTaxes = product?.useDefaultTaxes !== false;
            let taxIds = useDefaultTaxes ? defaultTaxIds : (product?.taxIds || []);
            taxIds = (taxIds || [])
                .map((id) => Number(id))
                .filter((id) => taxesById.has(id));
            if (!useDefaultTaxes && taxIds.length === 0) {
                taxIds = defaultTaxIds;
            }
            const fullTaxes = taxIds.map((id) => taxesById.get(id)).filter(Boolean);
            const appliedTaxes = excludedSet.size
                ? fullTaxes.filter((tax) => !excludedSet.has(Number(tax.id)))
                : fullTaxes;
            const fullRate = sumRate(fullTaxes) / 100;
            const remainingRate = sumRate(appliedTaxes) / 100;
            const appliesTaxExclusion = excludedSet.size
                && fullTaxes.length
                && appliedTaxes.length !== fullTaxes.length
                && fullRate > 0;

            (product?.Varieties || []).forEach((variety) => {
                if (!Number.isInteger(variety?.id)) return;
                map.set(variety.id, {
                    fullRate,
                    remainingRate,
                    appliesTaxExclusion,
                });
            });
        });

        return map;
    }, [products, taxes, excludedTaxIds, canExcludeTaxes]);

    const resolveCartItemPricing = useCallback((item, variety, quantityValue) => {
        const quantity = normalizeQuantity(quantityValue ?? item?.quantity ?? 0);
        const price = Number(item?.price);
        const unitPrice = Number.isFinite(price) ? price : 0;
        const discount = getFormatOfferId(item) ? 0 : Number(item?.discount);
        const unitDiscount = Number.isFinite(discount) ? discount : 0;
        const explicitSubtotal = Number(item?.subtotal);
        const hasExplicitSubtotal = Number.isFinite(explicitSubtotal) && explicitSubtotal >= 0;
        const discountedUnitPrice = hasExplicitSubtotal && quantity > 0
            ? explicitSubtotal / quantity
            : unitPrice - unitDiscount;
        const baseGross = getFormatOfferId(item)
            ? computeFormatOfferSubtotal(unitPrice, quantity) : Math.floor(unitPrice * quantity);
        const discountedGross = hasExplicitSubtotal
            ? Math.floor(explicitSubtotal)
            : getFormatOfferId(item) ? baseGross : Math.floor(discountedUnitPrice * quantity);
        const eligibleGrossValue = Number(item?.orderDiscountEligibleSubtotal);
        const hasEligibleGross = item?.orderDiscountEligibleSubtotal !== null
            && item?.orderDiscountEligibleSubtotal !== undefined
            && item?.orderDiscountEligibleSubtotal !== '';
        const orderDiscountEligibleGross = getFormatOfferId(item)
            ? 0
            : hasEligibleGross && Number.isFinite(eligibleGrossValue)
                ? Math.min(Math.max(Math.floor(eligibleGrossValue), 0), discountedGross)
                : discountedGross;
        const varietyId = Number.isInteger(item?.varietyId) ? item.varietyId : variety?.id;
        const profile = varietyTaxProfileById.get(varietyId);
        if (!profile?.appliesTaxExclusion) {
            return {
                unitPrice,
                discountedUnitPrice,
                subtotal: discountedGross,
                orderDiscountEligibleSubtotal: orderDiscountEligibleGross,
                taxFreeApplied: false,
            };
        }

        const adjustGross = (grossValue) => {
            const netFromFull = Math.round(grossValue / (1 + profile.fullRate));
            return Math.round(netFromFull * (1 + profile.remainingRate));
        };

        const adjustedGross = adjustGross(baseGross);
        const adjustedDiscountedGross = adjustGross(discountedGross);
        const adjustedOrderDiscountEligibleGross = adjustGross(orderDiscountEligibleGross);
        return {
            unitPrice: quantity > 0 ? adjustedGross / quantity : unitPrice,
            discountedUnitPrice: quantity > 0 ? adjustedDiscountedGross / quantity : discountedUnitPrice,
            subtotal: adjustedDiscountedGross,
            orderDiscountEligibleSubtotal: adjustedOrderDiscountEligibleGross,
            taxFreeApplied: true,
        };
    }, [varietyTaxProfileById]);

    const resolveVarietyPriceDisplay = useCallback((variety, varietyPrice, unitDiscount = 0) => {
        const basePriceRaw = Number(varietyPrice?.price);
        const basePrice = Number.isFinite(basePriceRaw) ? basePriceRaw : 0;
        const discountRaw = isFormatOfferPrice(varietyPrice) ? 0 : Number(unitDiscount);
        const resolvedDiscount = Number.isFinite(discountRaw) ? discountRaw : 0;
        const effectivePrice = basePrice - resolvedDiscount;
        const profile = varietyTaxProfileById.get(variety?.id);
        if (!profile?.appliesTaxExclusion) {
            return { price: effectivePrice, taxFreeApplied: false };
        }
        const grossValue = Math.floor(effectivePrice);
        const netFromFull = Math.round(grossValue / (1 + profile.fullRate));
        const adjustedGross = Math.round(netFromFull * (1 + profile.remainingRate));
        return { price: adjustedGross, taxFreeApplied: true };
    }, [varietyTaxProfileById]);
    const posSlotLayoutScopeKey = useMemo(
        () => buildPosSlotLayoutScopeKey(branchId, cashRegisterId),
        [branchId, cashRegisterId]
    );
    const currentPosSlotLayout = useMemo(
        () => getPosSlotLayout(myPreferences?.posSlotLayouts, branchId, cashRegisterId),
        [branchId, cashRegisterId, myPreferences?.posSlotLayouts]
    );
    const saveCurrentPosSlotLayout = useCallback(async (nextLayout) => {
        if (!posSlotLayoutScopeKey) {
            notify('Selecciona una caja activa antes de guardar favs.');
            return;
        }
        const nextLayouts = setPosSlotLayout(
            myPreferences?.posSlotLayouts,
            branchId,
            cashRegisterId,
            nextLayout
        );
        await saveMyPreferences({ posSlotLayouts: nextLayouts });
    }, [
        branchId,
        cashRegisterId,
        myPreferences?.posSlotLayouts,
        posSlotLayoutScopeKey,
        saveMyPreferences,
    ]);
    const posSlotPickerEntries = useMemo(() => buildPosSlotPickerEntries({
        products: priceAvailableProducts,
        isPriceAllowedByRegister: (price) => isPriceAllowedForType(clientType, price),
        resolveDisplayPrice: resolveVarietyPriceDisplay,
    }).map((entry) => ({
        ...entry,
        categoryId: resolveProductCategoryId(entry.product),
    })), [priceAvailableProducts, clientType, allowedClientTypes, resolveProductCategoryId, resolveVarietyPriceDisplay]);
    const resolvedPosSlots = useMemo(() => buildResolvedPosSlots({
        products: availableProducts,
        slotLayout: currentPosSlotLayout,
        quantities,
        discounts,
        scaleReadingLines,
        isPriceAllowedByRegister: (price) => isPriceAllowedForType(clientType, price),
        resolveDisplayPrice: resolveVarietyPriceDisplay,
    }), [
        availableProducts,
        allowedClientTypes,
        productFormatOptions,
        clientType,
        currentPosSlotLayout,
        discounts,
        quantities,
        resolveVarietyPriceDisplay,
        scaleReadingLines,
    ]);
    const filteredResolvedPosSlots = useMemo(() => {
        const visibleSlots = resolvedPosSlots.filter(
            (slot) => slot.product
                && slot.variety
                && slot.price
                && isPriceAllowedForType(clientType, slot.price)
        );
        const searchTokens = tokenizeProductSearchQuery(searchTerm);
        if (searchTokens.length) {
            return visibleSlots.filter((slot) => fieldsMatchProductSearchTerms([
                slot.productName,
                slot.varietyName,
                slot.metricUnit,
                slot.description,
                slot.price?.barcode,
            ], searchTokens, { searchMode }));
        }
        let nextSlots = visibleSlots;
        if (selectedCategory !== 'all') {
            nextSlots = nextSlots.filter(
            (slot) => String(resolveProductCategoryId(slot.product)) === String(selectedCategory)
            );
        }
        if (selectedSubcategory !== 'all') {
            nextSlots = nextSlots.filter(
                (slot) => String(resolveProductSubcategoryId(slot.product)) === String(selectedSubcategory)
            );
        }
        return nextSlots;
    }, [
        resolvedPosSlots,
        resolveProductCategoryId,
        resolveProductSubcategoryId,
        searchMode,
        searchTerm,
        selectedCategory,
        selectedSubcategory,
        clientType,
        allowedClientTypes,
    ]);

    const computeTotals = (
        productsData,
        quantitiesData,
        discountsData,
        overrideOrderDiscountAmount,
        overrideOrderDiscountPercentage,
        scaleReadingLinesData
    ) => {
        const resolvedProducts = Array.isArray(productsData) ? productsData : products;
        const resolvedQuantities = quantitiesData ?? quantities;
        const resolvedDiscounts = discountsData ?? discounts;
        const resolvedScaleReadingLines = scaleReadingLinesData ?? scaleReadingLines;
        const resolvedOrderDiscountAmount =
            overrideOrderDiscountAmount !== undefined ? overrideOrderDiscountAmount : orderDiscountAmount;
        const resolvedOrderDiscountPercentage =
            overrideOrderDiscountPercentage !== undefined
                ? overrideOrderDiscountPercentage
                : orderDiscountPercentage;
        const lineItems = buildPurchaseItemsPayload({
            productsData: resolvedProducts,
            quantitiesData: resolvedQuantities,
            discountsData: resolvedDiscounts,
            discountAuthorizationsData: {},
            scaleReadingLinesData: resolvedScaleReadingLines,
            orderDiscountAmountData: resolvedOrderDiscountAmount,
            orderDiscountPercentageData: resolvedOrderDiscountPercentage,
        });

        if (canExcludeTaxes && taxes.length) {
            return computeTaxAdjustedTotals({
                products: resolvedProducts,
                quantities: resolvedQuantities,
                discounts: resolvedDiscounts,
                lineItems,
                taxes,
                excludedTaxIds,
                orderDiscountAmount: resolvedOrderDiscountAmount,
                orderDiscountPercentage: resolvedOrderDiscountPercentage,
                isPriceAllowed: (price) => isPriceAllowedByRegister(price),
                promotions,
                promotionContext: 'sale',
                promotionNow,
            });
        }

        const subtotal = computeLineItemsSubtotal(lineItems);
        return computeOrderTotalsFromSubtotal({
            subtotal,
            nonDiscountableSubtotal: computeLineItemsNonDiscountableSubtotal(lineItems),
            orderDiscountAmount: resolvedOrderDiscountAmount,
            orderDiscountPercentage: resolvedOrderDiscountPercentage,
        });
    };

    useEffect(() => {
        if (!canExcludeTaxes) return;
        const totals = computeTotals(
            products,
            quantities,
            discounts,
            orderDiscountAmount,
            orderDiscountPercentage
        );
        setItemsSubtotal(totals.subtotal);
        setTotalPrice(totals.total);
    }, [excludedTaxIds, taxes, promotions, scaleReadingLines, barcodePricingRule, linePriceMeta]);

    useEffect(() => {
        if (canExcludeTaxes) return;
        const totals = computeTotals(
            products,
            quantities,
            discounts,
            orderDiscountAmount,
            orderDiscountPercentage
        );
        setItemsSubtotal(totals.subtotal);
        setTotalPrice(totals.total);
    }, [promotions, scaleReadingLines, barcodePricingRule, linePriceMeta]);

    useEffect(() => {
        if (!purchaseId || !canExcludeTaxes || !canEditSale) return;
        if (!purchaseDetails) return;
        const totals = computeTotals(
            products,
            quantities,
            discounts,
            orderDiscountAmount,
            orderDiscountPercentage
        );
        handlePurchaseSave(
            quantities,
            totals.total,
            discounts,
            discountAuthorizations,
            orderDiscountAuthorizationId,
            orderDiscountAmount,
            orderDiscountPercentage
        );
    }, [excludedTaxIds]);

    const fetchClientPricingMap = useCallback(async () => {
        if (!clientId || !branchId) return {};
        const data = await getClientPricingDiscounts(clientId, { branchId });
        return data?.discountsByVarietyPriceId || {};
    }, [branchId, clientId]);

    const [catalogLoad, setCatalogLoad] = useState({ message: '', error: '' });
    const catalogRefreshScopeKey = `${branchId || ''}:${clientId || ''}`;
    const {
        begin: beginCatalogRefresh,
        capture: captureCatalogRefresh,
        latest: latestCatalogState,
    } = useCatalogRefreshState(catalogRefreshScopeKey, {
        products, quantities, discounts, discountAuthorizations, linePriceMeta,
        orderDiscountAmount, orderDiscountPercentage, computeTotals, scaleReadingLines, clientPricingDiscountMap,
    });
    const refreshProducts = useCallback(async (event) => {
        if (!branchId || !isEditorPosView) return;
        const request = beginCatalogRefresh(catalogRefreshScopeKey);
        if (!request) return;
        try {
            const [data, effectivePricingMap] = await Promise.all([
                fetchCatalogDelta((params) => Object.keys(params).length
                    ? getProducts(branchId, { pageSize: 0, ...params })
                    : getPosCatalog(branchId, message => {
                        if (latestCatalogState(request)) setCatalogLoad({ message, error: '' });
                    }), event, latestCatalogState(request)?.products || []),
                fetchClientPricingMap(),
            ]);
            const latest = latestCatalogState(request);
            if (!latest) return;
            setPromotionClock(data?.promotionClock || null);
            setCatalogLoad({ message: '', error: '' });
            const normalized = normalizePaginatedResponse(data);
            const merged = mergeCatalogDelta(latest.products, normalized.data || [], data?.deltaProductIds);
            const productsData = shareCatalogValue(latest.products, retainUnavailableCartRows(
                expandProductsWithFormatOffers(merged, latest.products), latest.products, latest.quantities, latest.scaleReadingLines
            ));
            if (productsData !== latest.products && salePriceSnapshotsRef.current.size) setCatalogPriceNotice('Catálogo actualizado. La venta conserva sus precios; la disponibilidad está actualizada.');
            const nextQuantities = mergeProductMatrix(productsData, latest.products, latest.quantities, 0);
            const mergedDiscounts = mergeProductMatrix(productsData, latest.products, latest.discounts, 0);
            const nextAuthorizations = mergeProductMatrix(productsData, latest.products, latest.discountAuthorizations, null);
            const pricingResult = applyClientPricingDiscountsToMatrices({
                products: productsData,
                discounts: mergedDiscounts,
                linePriceMeta: latest.linePriceMeta,
                discountsByVarietyPriceId: effectivePricingMap,
            });
            const nextDiscounts = event ? preserveActiveLineDiscounts({ previousProducts: latest.products,
                products: productsData, quantities: latest.quantities, previousDiscounts: latest.discounts,
                discounts: pricingResult.discounts }) : pricingResult.discounts;

            setProducts(productsData);
            setFilteredProducts(productsData);
            setPromotions(collectCatalogPromotions(productsData));
            setQuantities(nextQuantities);
            setDiscounts(nextDiscounts);
            setDiscountAuthorizations(nextAuthorizations);
            setLinePriceMeta(pricingResult.linePriceMeta);
            setClientPricingDiscountMap(effectivePricingMap);

            const totals = latest.computeTotals(
                productsData,
                nextQuantities,
                nextDiscounts,
                latest.orderDiscountAmount,
                latest.orderDiscountPercentage
            );
            setItemsSubtotal(totals.subtotal);
            setTotalPrice(totals.total);
        } catch (error) {
            if (!latestCatalogState(request)) return;
            setCatalogLoad({ message: '', error: 'No se pudo actualizar el catálogo. Revisa la conexión e intenta nuevamente.' });
            if (event) throw error;
            if (error.response?.status === 401) {
                navigate('/'); // Redirect to login if unauthorized
            }
        }
    }, [
        branchId,
        isEditorPosView,
        catalogRefreshScopeKey,
        products,
        quantities,
        discounts,
        discountAuthorizations,
        orderDiscountAmount,
        orderDiscountPercentage,
        promotions,
        fetchClientPricingMap,
        navigate,
        beginCatalogRefresh,
        latestCatalogState,
    ]);

    const scheduleCatalogRefresh = useCatalogUpdateQueue(catalogRefreshScopeKey, refreshProducts);

    const buildEmptyMatrices = () => {
        return products.reduce((acc, product) => {
            product.Varieties.forEach((variety) => {
                acc[variety.id] = new Array(variety.VarietyPrices.length).fill(0);
            });
            return acc;
        }, {});
    };

    useEffect(() => {
        setPromotions([]);
        setPromotionClock(null);
    }, [branchId]);

    const promotionBoundaries = useMemo(() => (
        promotionClock?.nextBoundaryAt
            ? [...promotions, { startsAt: promotionClock.nextBoundaryAt }]
            : promotions
    ), [promotions, promotionClock?.nextBoundaryAt]);
    const promotionNow = usePromotionClock(promotionBoundaries, () => {
        refreshProducts();
    }, promotionClock);

    const resetSaleState = () => {
        salePriceSnapshotsRef.current.clear();
        setCatalogPriceNotice('');
        const emptyMatrices = buildEmptyMatrices();
        draftStartedAtRef.current = new Date().toISOString();
        purchaseStateGenerationRef.current += 1;
        purchaseIdRef.current = null;
        clientIdRef.current = null;

        setQuantities(emptyMatrices);
        setDiscounts(emptyMatrices);
        setDiscountAuthorizations({});
        setAddedOrder([]);
        setScaleReadingLines([]);
        setPurchaseId(null);
        setPurchaseDetails(null);
        setItemsSubtotal(0);
        setTotalPrice(0);
        setOrderDiscountAmount(null);
        setOrderDiscountPercentage(null);
        setOrderDiscountAuthorizationId(null);
        setExcludedTaxIds([]);
        setLinePriceMeta({});
        setSaleName('');

        setClientId(null);
        setName('');
        setRut('');
        setEmail('');
        setAddress('');
        setCity('');
        setPhone('');
        setGiro('');
        const defaultType = resolveAllowedClientType('detalle');
        setClientType(defaultType);
        setTypeSelection(defaultType);

        setShowPurchaseCard(false);
        setModalProduct(null);
        setModalVariety(null);
        setModalVarietyPrice(null);
        setSlotModalState(null);
        setActiveScaleSelection(null);
        setShowDocumentTypeModal(false);
        setPendingFinishAfterClient(false);
        setPendingConversionAfterClient(false);
        setPendingConversionAfterType(false);
        setPendingDocumentType(null);
        setDocumentType(null);
        setShowTypeModal(false);

        setSearchTerm('');
        setSelectedCategory('all');
        setSelectedSubcategory('all');
        filterProducts('', 'all', 'all');
    };

    const clearClientData = () => {
        clientIdRef.current = null;
        setClientId(null);
        setName('');
        setRut('');
        setEmail('');
        setAddress('');
        setCity('');
        setPhone('');
        setGiro('');
    };

    useEffect(() => {
        clientIdRef.current = clientId;
    }, [clientId]);

    const closeModalVariety = () => {
        setModalVariety(null);
        setSlotModalState(null);
        setActiveScaleSelection(null);
        applySearch('');
    };

    const closeModal = () => {
        setModalVarietyPrice(null);
        setSlotModalState(null);
        applySearch('');
    };

    const closeAllProductModals = () => {
        setModalVarietyPrice(null);
        setModalVariety(null);
        setModalProduct(null);
        setSlotModalState(null);
        setActiveScaleSelection(null);
        setShowDiscountModal(false);
        setShowSlotPickerModal(false);
        applySearch('');
        setTimeout(() => {
            searchInputRef?.current?.focus();
        }, 100);
    };

    const clientIdParam = searchParams.get('clientId') || null;
    const typeParam = searchParams.get('type') || '';
    const currentSearchQuery = searchParams.toString();
    const buildPathWithCurrentSearch = useCallback((pathname) => (
        currentSearchQuery ? `${pathname}?${currentSearchQuery}` : pathname
    ), [currentSearchQuery]);
    const posWindowName = useMemo(() => buildPosWindowName({
        branchId,
        cashRegisterId,
        cashRegisterSessionId,
        mode: posView,
    }), [branchId, cashRegisterId, cashRegisterSessionId, posView]);
    useEffect(() => {
        if (typeof window === 'undefined') return;
        window.name = posWindowName;
    }, [posWindowName]);
    const handlePosViewChange = useCallback((nextView) => {
        if (nextView === posView) return;
        const nextParams = buildPosViewSearchParams(searchParams, nextView);
        const nextSearch = nextParams.toString();
        const targetPath = `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`;
        const targetWindowName = buildPosWindowName({
            branchId,
            cashRegisterId,
            cashRegisterSessionId,
            mode: nextView,
        });
        const targetWindow = window.open(targetPath, targetWindowName);
        targetWindow?.focus();
    }, [branchId, cashRegisterId, cashRegisterSessionId, location.pathname, posView, searchParams]);

    const handleProductEvent = useCallback((event) => isEditorPosView && scheduleCatalogRefresh(event), [isEditorPosView, scheduleCatalogRefresh]);

    useProductEvents(isEditorPosView ? branchId : null, handleProductEvent);

    useEffect(() => {
        refreshProducts();
    }, [branchId, isEditorPosView]);

    useEffect(() => {
        let isMounted = true;
        const loadSoldOutDisplaySettings = async () => {
            try {
                const settings = await getPosSoldOutDisplaySettings();
                if (!isMounted) return;
                setSoldOutDisplayMode(normalizePosSoldOutMode(settings?.soldOutMode));
                setPosSearchSuggestionVisibility(
                    normalizePosSearchSuggestionVisibility(settings?.searchSuggestions)
                );
            } catch (error) {
                if (!isMounted) return;
                setSoldOutDisplayMode(POS_SOLD_OUT_MODE_NORMAL);
                setPosSearchSuggestionVisibility(DEFAULT_POS_SEARCH_SUGGESTION_VISIBILITY);
            }
        };
        loadSoldOutDisplaySettings();
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        const getClientInfoById = async () => {
            try {
                if (clientIdParam) {
                    const data = await getClientById(clientIdParam);
                    setAddress(data.address);
                    setCity(data.city);
                    setName(data.name);
                    setClientId(data.id);
                    setPhone(data.phone);
                    setEmail(data.email);
                    setRut(data.rut);
                    setGiro(data.giro || '');
                    setShowPurchaseCard(false);
                }
            } catch (error) {
                if (error.response?.status === 401) {
                    navigate('/');
                }
            }
        };
        getClientInfoById();
    }, [clientIdParam, navigate]);

    useEffect(() => {
        let mounted = true;
        const request = captureCatalogRefresh(catalogRefreshScopeKey);
        if (!request) return undefined;
        const loadClientPricingDiscounts = async () => {
            try {
                const data = await fetchClientPricingMap();
                if (!mounted || !latestCatalogState(request)) return;
                setClientPricingDiscountMap(data);
            } catch (error) {
                if (mounted && latestCatalogState(request)) setClientPricingDiscountMap({});
            }
        };
        loadClientPricingDiscounts();
        return () => {
            mounted = false;
        };
    }, [
        catalogRefreshScopeKey,
        captureCatalogRefresh,
        fetchClientPricingMap,
        latestCatalogState,
    ]);

    useEffect(() => {
        const result = applyClientPricingDiscountsToMatrices({
            products,
            discounts,
            linePriceMeta,
            discountsByVarietyPriceId: clientPricingDiscountMap,
        });
        if (!result.changed) return;
        setDiscounts(result.discounts);
        setLinePriceMeta(result.linePriceMeta);
        calculateTotal(quantities, result.discounts);
    }, [products, quantities, clientPricingDiscountMap]);

    useEffect(() => {
        if (!typeParam) return;
        const resolvedType = resolveAllowedClientType(typeParam);
        if (resolvedType !== clientType) {
            setClientType(resolvedType);
            setTypeSelection(resolvedType);
        }
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('type');
        setSearchParams(nextParams, { replace: true });
    }, [typeParam, resolveAllowedClientType, clientType, searchParams, setSearchParams]);

    useEffect(() => {
        const fetchCategories = async () => {
            if (!branchId) return;
            setCategoriesLoading(true);
            setCategoriesError(null);
            try {
                const data = await getCategories({ includeAvailability: true, branchId });
                setCategories(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Error fetching categories:', error);
                setCategories([]);
                setCategoriesError('No se pudieron cargar las categorías.');
            } finally {
                setCategoriesLoading(false);
            }
        };
        fetchCategories();
    }, [branchId]);

    useEffect(() => {
        if (!branchId || !canExcludeTaxes) {
            setTaxes([]);
            setTaxesLoading(false);
            setTaxesError(null);
            setExcludedTaxIds([]);
            return;
        }
        let isMounted = true;
        const loadTaxes = async () => {
            setTaxesLoading(true);
            setTaxesError(null);
            try {
                const data = await getBranchTaxes(branchId);
                if (!isMounted) return;
                const normalized = Array.isArray(data) ? data : [];
                const active = normalized.filter(
                    (tax) => tax && tax.isActive !== false && tax.branchActive !== false
                );
                setTaxes(active);
                const activeIds = new Set(active.map((tax) => Number(tax.id)));
                setExcludedTaxIds((prev) =>
                    (prev || []).filter((id) => activeIds.has(Number(id)))
                );
            } catch (error) {
                if (!isMounted) return;
                console.error('Error fetching taxes:', error);
                setTaxes([]);
                setTaxesError('No se pudieron cargar los impuestos.');
            } finally {
                if (!isMounted) return;
                setTaxesLoading(false);
            }
        };
        loadTaxes();
        return () => {
            isMounted = false;
        };
    }, [branchId, canExcludeTaxes]);

    useEffect(() => {
        if (selectedCategory === 'all') return;
        const isStillValid = displayCategories.some(
            (category) => String(category.id) === String(selectedCategory)
        );
        if (!isStillValid) {
            setSelectedCategory('all');
            setSelectedSubcategory('all');
        }
    }, [displayCategories, selectedCategory]);

    useEffect(() => {
        if (selectedCategory === 'all' || selectedSubcategory === 'all') return;
        const isStillValid = displaySubcategories.some(
            (subcategory) => String(subcategory.id) === String(selectedSubcategory)
        );
        if (!isStillValid) {
            setSelectedSubcategory('all');
        }
    }, [displaySubcategories, selectedCategory, selectedSubcategory]);

    useEffect(() => {
        if (!clientType) return;
        const resolvedType = resolveAllowedClientType(clientType);
        if (resolvedType !== clientType) {
            setClientType(resolvedType);
            setTypeSelection(resolvedType);
        }
    }, [allowedClientTypes, clientType, resolveAllowedClientType]);

    const loadCashRegisters = async () => {
        if (!branchId) return;
        setPendingTabsScopeReady(false);
        try {
            const [data, registerlessAccess] = await Promise.all([
                getCashRegisters(branchId, { pageSize: 0 }),
                getMyRegisterlessPosProfile(branchId).catch(() => ({ hasAccess: false, profile: null })),
            ]);
            const normalized = normalizePaginatedResponse(data);
            const registers = normalized.data || [];
            setCashRegisters(registers);
            const nextRegisterlessProfile = registerlessAccess?.hasAccess ? registerlessAccess.profile : null;
            setRegisterlessProfile(nextRegisterlessProfile);

            const storedRegisterless = localStorage.getItem(posRegisterlessStorageKey) === 'true';
            if (storedRegisterless && nextRegisterlessProfile) {
                setIsRegisterlessMode(true);
                setCashRegisterId(null);
                setCashRegisterSessionId(null);
                localStorage.removeItem(posSessionStorageKey);
                setShowPosSessionModal(false);
                notifyPosSessionUpdate();
                return;
            }
            if (storedRegisterless && !nextRegisterlessProfile) {
                localStorage.removeItem(posRegisterlessStorageKey);
                setIsRegisterlessMode(false);
            }

            const storedRegisterId = localStorage.getItem(posRegisterStorageKey);
            const storedSessionId = localStorage.getItem(posSessionStorageKey);
            if (shouldDefaultToRegisterlessPosScope({
                registerlessProfile: nextRegisterlessProfile,
                storedRegisterless,
                storedRegisterId,
                storedSessionId,
            })) {
                setIsRegisterlessMode(true);
                setCashRegisterId(null);
                setCashRegisterSessionId(null);
                localStorage.setItem(posRegisterlessStorageKey, 'true');
                localStorage.removeItem(posRegisterStorageKey);
                localStorage.removeItem(posSessionStorageKey);
                setShowPosSessionModal(false);
                notifyPosSessionUpdate();
                return;
            }
            const { selectedRegister, session } = resolveCurrentPosScope({
                registers,
                userId: user?.id,
                storedRegisterId,
                storedSessionId,
            });

            if (selectedRegister) {
                setCashRegisterId(selectedRegister.id);
            }

            if (session) {
                setIsRegisterlessMode(false);
                localStorage.removeItem(posRegisterlessStorageKey);
                setCashRegisterSessionId(session.id);
                localStorage.setItem(posRegisterStorageKey, selectedRegister.id);
                localStorage.setItem(posSessionStorageKey, session.id);
                setShowPosSessionModal(false);
                notifyPosSessionUpdate();
            } else {
                setIsRegisterlessMode(false);
                setCashRegisterSessionId(null);
                localStorage.removeItem(posSessionStorageKey);
                setShowPosSessionModal(true);
                notifyPosSessionUpdate();
            }
        } catch (error) {
            console.error('Error loading cash registers:', error);
            setShowPosSessionModal(true);
        } finally {
            setPendingTabsScopeReady(true);
        }
    };

    useEffect(() => {
        loadCashRegisters();
    }, [branchId, user?.id]);

    useEffect(() => {
        const receivedMatchingClose = posSyncSessionClosed?.cashRegisterSessionId
            && String(posSyncSessionClosed.cashRegisterSessionId) === String(cashRegisterSessionId);
        const sessionNoLongerAvailable = posSyncLastError === 'No open cash register session found';
        if (!receivedMatchingClose && !sessionNoLongerAvailable) return;

        setCashRegisterSessionId(null);
        localStorage.removeItem(posSessionStorageKey);
        setShowCloseSessionModal(false);
        setShowPosSessionModal(true);
        notifyPosSessionUpdate();
        notify(
            posSyncSessionClosed?.reason === 'administrative_close'
                ? 'Un administrador cerro tu caja. Debes abrir una nueva sesion para continuar.'
                : 'La caja fue cerrada. Debes abrir una nueva sesion para continuar.'
        );
        loadCashRegisters();
    }, [posSyncLastError, posSyncSessionClosed?.closedAt]);

    useEffect(() => {
        if (!showPosSessionModal) return;
        let isMounted = true;
        const loadPosModalData = async () => {
            setPosModalLoading(true);
            setPosModalError('');
            try {
                let branchList = [];
                if (canReadBranches) {
                    const branchData = await getBranches({ pageSize: 0 });
                    branchList = normalizePaginatedResponse(branchData).data || [];
                }
                if (!branchList.length && branchId) {
                    branchList = [
                        {
                            id: Number(branchId) || branchId,
                            branchName: `Sucursal ${branchId}`,
                        },
                    ];
                }
                const registerMap = {};
                const categoryMap = {};
                await Promise.all(
                    branchList.map(async (branch) => {
                        const branchKey = branch.id;
                        if (String(branchKey) === String(branchId)) {
                            registerMap[branchKey] = cashRegisters;
                            categoryMap[branchKey] = categories;
                            return;
                        }
                        try {
                            const [registerData, categoryData] = await Promise.all([
                                getCashRegisters(branchKey, { pageSize: 0 }),
                                getCategories({ includeAvailability: true, branchId: branchKey }),
                            ]);
                            registerMap[branchKey] = normalizePaginatedResponse(registerData).data || [];
                            categoryMap[branchKey] = Array.isArray(categoryData) ? categoryData : [];
                        } catch (error) {
                            registerMap[branchKey] = [];
                            categoryMap[branchKey] = [];
                        }
                    })
                );
                if (!isMounted) return;
                setPosModalBranches(branchList);
                setPosModalRegistersByBranch(registerMap);
                setPosModalCategoriesByBranch(categoryMap);
            } catch (error) {
                if (!isMounted) return;
                setPosModalError(error.message || 'No se pudieron cargar las cajas.');
            } finally {
                if (isMounted) setPosModalLoading(false);
            }
        };
        loadPosModalData();
        return () => {
            isMounted = false;
        };
    }, [showPosSessionModal, canReadBranches, branchId, cashRegisters, categories]);

    useEffect(() => {
        if (!activeSession || !isActiveSessionOwnedByUser) return;
        if (!cashRegisterSessionId || String(activeSession.id) !== String(cashRegisterSessionId)) {
            setCashRegisterSessionId(activeSession.id);
            localStorage.setItem(posSessionStorageKey, activeSession.id);
            notifyPosSessionUpdate();
        }
    }, [activeSession, cashRegisterSessionId, isActiveSessionOwnedByUser, notifyPosSessionUpdate]);

    const handleSelectSession = (register, session, branchOverride) => {
        if (!register || !session) return;
        if (String(session.openedBy) !== String(user?.id)) {
            notify('Caja abierta por otro usuario.');
            return;
        }
        const targetBranchId = branchOverride || branchId;
        localStorage.setItem(getPosRegisterStorageKey(targetBranchId), register.id);
        localStorage.setItem(getPosSessionStorageKey(targetBranchId), session.id);
        localStorage.removeItem(getPosRegisterlessStorageKey(targetBranchId));
        if (String(targetBranchId) !== String(branchId)) {
            localStorage.setItem('selectedBranchId', targetBranchId);
            setShowPosSessionModal(false);
            navigate(`/branches/${targetBranchId}/purchases/new`);
            return;
        }
        setIsRegisterlessMode(false);
        setCashRegisterId(register.id);
        setCashRegisterSessionId(session.id);
        setShowPosSessionModal(false);
        notifyPosSessionUpdate();
    };

    const handleSelectRegisterless = (profile) => {
        if (!profile?.id) {
            notify('No tienes acceso a venta sin caja en esta sucursal.');
            return;
        }
        localStorage.setItem(posRegisterlessStorageKey, 'true');
        localStorage.removeItem(posRegisterStorageKey);
        localStorage.removeItem(posSessionStorageKey);
        setRegisterlessProfile(profile);
        setIsRegisterlessMode(true);
        setCashRegisterId(null);
        setCashRegisterSessionId(null);
        setShowPosSessionModal(false);
        notifyPosSessionUpdate();
    };

    const handleOpenSession = async (register, openingCash, branchOverride) => {
        if (openingSessionRequestRef.current) return;
        if (!canOpenPos) return;
        if (!register) return;
        if (register.activeSession
            && String(register.activeSession.openedBy) !== String(user?.id)) {
            notify('Caja abierta por otro usuario.');
            return;
        }
        const parsed = Number(openingCash);
        if (!Number.isFinite(parsed)) {
            notify('Debes ingresar un fondo inicial válido.');
            return;
        }
        openingSessionRequestRef.current = true;
        setIsOpeningSession(true);
        try {
            const targetBranchId = branchOverride || branchId;
            const session = await openCashRegisterSession(targetBranchId, register.id, parsed);
            localStorage.setItem(getPosRegisterStorageKey(targetBranchId), register.id);
            localStorage.setItem(getPosSessionStorageKey(targetBranchId), session.id);
            localStorage.removeItem(getPosRegisterlessStorageKey(targetBranchId));
            if (String(targetBranchId) !== String(branchId)) {
                localStorage.setItem('selectedBranchId', targetBranchId);
                setShowPosSessionModal(false);
                navigate(`/branches/${targetBranchId}/purchases/new`);
                return;
            }
            setIsRegisterlessMode(false);
            setCashRegisterId(register.id);
            setCashRegisterSessionId(session.id);
            setShowPosSessionModal(false);
            notifyPosSessionUpdate();
            await loadCashRegisters();
        } catch (error) {
            console.error('Error opening cash register:', error);
            notify(error.response?.data?.message || 'No se pudo abrir la caja.');
        } finally {
            openingSessionRequestRef.current = false;
            setIsOpeningSession(false);
        }
    };

    const handleCloseSession = async (closingCash, closeReason) => {
        if (!canClosePos) return;
        if (!activeCashRegister) return;
        if (!isActiveSessionOwnedByUser) {
            notify('Caja abierta por otro usuario.');
            return;
        }
        const parsed = closingCash === '' ? Number.NaN : Number(closingCash);
        if (!Number.isFinite(parsed)) {
            notify('Debes ingresar el efectivo contado.');
            return;
        }
        if (openSessionSales.length > 0) {
            notify('No puedes cerrar la caja mientras existan ventas abiertas.');
            return;
        }
        const resolvedExpectedCash = Number.isFinite(Number(expectedCash)) ? Number(expectedCash) : null;
        const cashDifference = resolvedExpectedCash !== null ? parsed - resolvedExpectedCash : null;
        if (cashDifference !== null && cashDifference !== 0 && !String(closeReason || '').trim()) {
            notify('Debes indicar el motivo de la diferencia.');
            return;
        }
        try {
            await closeCashRegisterSession(branchId, activeCashRegister.id, {
                closingCash: parsed,
                closeReason: closeReason || null,
            });
            setCashRegisterSessionId(null);
            localStorage.removeItem(posSessionStorageKey);
            setShowCloseSessionModal(false);
            setShowPosSessionModal(true);
            notifyPosSessionUpdate();
            await loadCashRegisters();
        } catch (error) {
            console.error('Error closing cash register:', error);
            notify(error.response?.data?.message || 'No se pudo cerrar la caja.');
        }
    };

    const handleOpenSaleFromClose = (sale) => {
        if (!canUpdateSale) return;
        if (!sale?.id) return;
        setShowCloseSessionModal(false);
        navigate(`/branches/${branchId}/purchases/${sale.id}/edit`);
    };

    const resolvePendingSaleLabel = (sale) => {
        const type = (sale?.documentType || '').toLowerCase();
        if (type === 'boleta' || type === 'factura') {
            return type.charAt(0).toUpperCase() + type.slice(1);
        }
        return 'Venta';
    };

    const handleCancelOpenSale = (sale) => {
        if (!canDeleteSale) return;
        if (!sale?.id || !branchId) return;
        setCancelSaleTarget(sale);
    };

    const handleConfirmCancelOpenSale = async (cancelPayload) => {
        if (!canDeleteSale) return;
        if (!branchId || !cancelSaleTarget?.id) {
            throw new Error('No se pudo identificar la venta.');
        }
        try {
            await deletePendingPurchase(branchId, cancelSaleTarget.id, cancelPayload || {});
            if (cancelSaleTarget.id === purchaseId) {
                resetSaleState();
            }
            await loadOpenSessionSales();
        } catch (error) {
            console.error('Error deleting pending purchase:', error);
            throw new Error(
                error?.response?.data?.message || 'No se pudo anular la venta pendiente.'
            );
        }
    };

    useEffect(() => {
        const timer = focusPrimarySearchInput(300);
        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [focusPrimarySearchInput]);

    useEffect(() => {
        if (hasActiveSearch) {
            setShowMobileSearch(true);
        }
    }, [hasActiveSearch]);

    const toggleMobileSearch = () => {
        if (mobileSearchVisible && hasActiveSearch) return;
        setShowMobileSearch((prev) => {
            const next = !prev;
            if (next) {
                setTimeout(() => {
                    searchInputRef.current?.focus();
                    searchInputRef.current?.select();
                }, 0);
            }
            return next;
        });
    };

    const handleResumeSuspendedSale = async (rawCode) => {
        if (!branchId) return;
        if (!canUpdateSale) {
            notify('No tienes permisos para reanudar ventas.');
            return;
        }
        if (isRegisterlessMode || !cashRegisterSessionId || !isActiveSessionOwnedByUser) {
            notify('Debes usar una caja activa para reanudar y cobrar una venta suspendida.');
            return;
        }
        const normalized = normalizeSuspendedCodeInput(rawCode);
        if (!normalized) return;
        setLoadingProcess('Recuperando venta…');
        setIsLoading(true);
        try {
            const response = await resumePurchaseByCode(branchId, normalized, {
                cashRegisterId,
                cashRegisterSessionId,
            });
            const resumed = response?.purchase;
            if (resumed?.id) {
                applySearch('');
                navigate(`/branches/${branchId}/purchases/${resumed.id}/edit`);
                return;
            }
            notify('No se pudo reanudar la venta.');
        } catch (error) {
            console.error('Error resuming suspended sale:', error);
            notify(error?.response?.data?.message || 'No se pudo reanudar la venta.');
        } finally {
            setIsLoading(false);
            focusPrimarySearchInput();
        }
    };

    const handleSuspendSale = async () => {
        if (!canUpdateSale) {
            notify('No tienes permisos para suspender ventas.');
            return;
        }
        const hasItems = scaleReadingLines.length > 0 || Object.values(quantities || {}).some(
            (list) =>
                Array.isArray(list)
                && list.some((qty) => normalizeQuantity(qty) > 0)
        );
        if (!hasItems) {
            notify('Agrega productos antes de suspender la venta.');
            return;
        }
        const confirmed = await confirm({
            title: 'Suspender venta',
            message: '¿Suspender esta venta? Se generara un voucher con codigo de barras.',
            confirmLabel: 'Suspender',
            cancelLabel: 'Cancelar',
            tone: 'warning',
        });
        if (!confirmed) return;
        setLoadingProcess('Guardando y suspendiendo venta…');
        setIsLoading(true);
        try {
            const savedId = await savePurchase();
            if (!savedId) return;
            await suspendPurchase(branchId, savedId);
            resetSaleState();
            navigate(`/branches/${branchId}/purchases/${savedId}/receipt`);
        } catch (error) {
            console.error('Error suspending sale:', error);
            notify(error?.response?.data?.message || 'No se pudo suspender la venta.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleTabSelect = async (selectedId, selectedPurchase = null) => {
        if (selectedId) {
            if (!canUpdateSale) return;
            if (
                selectedPurchase?.registerlessProfileId
                && !isRegisterlessMode
                && cashRegisterId
                && cashRegisterSessionId
                && isActiveSessionOwnedByUser
            ) {
                setLoadingProcess('Guardando venta y preparando comprobante…');
                setIsLoading(true);
                try {
                    await resumePurchase(branchId, selectedId, {
                        cashRegisterId,
                        cashRegisterSessionId,
                    });
                } catch (error) {
                    console.error('Error attaching registerless sale to cash register:', error);
                    notify(error?.response?.data?.message || 'No se pudo reanudar la venta sin caja.');
                    return;
                } finally {
                    setIsLoading(false);
                }
            }
            navigate(buildPathWithCurrentSearch(`/branches/${branchId}/purchases/${selectedId}/edit`));
            return;
        }
        if (!canCreateSale) return;

        const hasItems = scaleReadingLines.length > 0 || Object.values(quantities || {}).some(
            (list) =>
                Array.isArray(list)
                && list.some((qty) => normalizeQuantity(qty) > 0)
        );

        const shouldSave = hasItems || purchaseId;
        let canReset = !shouldSave;

        if (shouldSave) {
            setLoadingProcess('Guardando venta y preparando comprobante…');
            setIsLoading(true);
            try {
                const savedId = await savePurchase();
                canReset = !!savedId;
            } catch (error) {
                console.error('Error al guardar la venta antes de reiniciar:', error);
                canReset = false;
            } finally {
                setIsLoading(false);
            }
        }

        if (canReset) {
            resetSaleState();
        }
    };

    const filterProducts = (search, category, subcategory = 'all') => {
        let filtered = displayProducts;
        let varietyResults = [];
        let suggestedVarietyResults = [];

        const searchTokens = tokenizeProductSearchQuery(search);

        if (searchTokens.length) {
            const seenVarietyKeys = new Set();
            const searchEntries = displayProducts
                .map((product) => {
                    const visibleVarieties = filterVarietiesBySoldOutDisplay(
                        product.Varieties || [],
                        soldOutDisplayMode,
                        isVarietySoldOutForFilter
                    ).filter((variety) => hasSellablePrice(variety, clientType));
                    const match = getProductSearchMatch(product, searchTokens, {
                        varieties: visibleVarieties,
                        searchMode,
                    });
                    return { product, match };
                })
                .filter(({ match }) => match.matches);

            const exactEntries = searchEntries.filter(({ match }) => match.exactMatches);
            const suggestedEntries = searchEntries.filter(({ match }) =>
                !match.exactMatches && match.suggestedMatches
            );

            filtered = exactEntries.map(({ product }) => product);
            varietyResults = exactEntries.flatMap(({ product, match }) =>
                match.matchingVarieties
                    .map((variety) => ({ product, variety }))
                    .filter((entry) => {
                        const key = `${entry.product?.id || 'x'}-${entry.variety?.id || 'x'}`;
                        if (seenVarietyKeys.has(key)) return false;
                        seenVarietyKeys.add(key);
                        return true;
                    })
            );
            varietyResults = orderBySoldOutDisplay(
                varietyResults,
                soldOutDisplayMode,
                ({ variety }) => isVarietySoldOutForFilter(variety)
            );
            suggestedVarietyResults = suggestedEntries.flatMap(({ product, match }) =>
                match.suggestedMatchingVarieties
                    .map((variety) => ({ product, variety }))
                    .filter((entry) => {
                        const key = `${entry.product?.id || 'x'}-${entry.variety?.id || 'x'}`;
                        if (seenVarietyKeys.has(key)) return false;
                        seenVarietyKeys.add(key);
                        return true;
                    })
            );
            suggestedVarietyResults = orderBySoldOutDisplay(
                suggestedVarietyResults,
                soldOutDisplayMode,
                ({ variety }) => isVarietySoldOutForFilter(variety)
            );

        } else {
            if (category !== 'all') {
                filtered = filtered.filter(
                (product) => String(resolveProductCategoryId(product)) === String(category)
                );
            }
            if (subcategory !== 'all') {
                filtered = filtered.filter(
                    (product) => String(resolveProductSubcategoryId(product)) === String(subcategory)
                );
            }
        }

        setFilteredProducts(filtered);
        setSearchVarietyResults(varietyResults);
        setSuggestedSearchVarietyResults(suggestedVarietyResults);
    };

    const totalProducts = filteredProducts.length;
    const totalVisibleResults = totalProducts
        + (hasActiveSearch ? searchVarietyResults.length + suggestedSearchVarietyResults.length : 0);

    const handleVarietySelection = ({ variety, product }) => {
        if (isVarietySoldOut(variety)) {
            notify('Esta variedad está agotada.');
            return;
        }
        const sellablePrices = getVarietyPricesForFilterType(variety);
        if (sellablePrices.length === 0) {
            if (clientType) {
                notify(`No hay precios para el filtro ${getPriceTypeLabel(clientType)}.`);
            } else {
                notify('No hay precios disponibles para esta variedad.');
            }
            return;
        }
        setSlotModalState(null);
        setModalVariety(variety);
        setModalVarietyPrice(null);
    };

    const findVarietyPriceByBarcode = (lookupKeys) => {
        if (!Array.isArray(lookupKeys) || lookupKeys.length === 0) return null;
        const keySet = new Set(lookupKeys.filter(Boolean));

        for (const product of availableProducts) {
            for (const variety of product.Varieties || []) {
                for (let index = 0; index < (variety.VarietyPrices || []).length; index += 1) {
                    const price = variety.VarietyPrices[index];
                    if (!isPriceSelectableForType(clientType, price)) continue;
                    const barcode = price?.barcode == null ? null : String(price.barcode).trim();
                    if (barcode && keySet.has(barcode)) {
                        return { variety, varietyPrice: price, priceIndex: index };
                    }
                }
            }
        }

        return null;
    };

    const findAnyVarietyPriceByBarcode = (lookupKeys) => {
        if (!Array.isArray(lookupKeys) || lookupKeys.length === 0) return null;
        const keySet = new Set(lookupKeys.filter(Boolean));
        let fallbackMatch = null;

        for (const product of availableProducts) {
            for (const variety of product.Varieties || []) {
                for (let index = 0; index < (variety.VarietyPrices || []).length; index += 1) {
                    const price = variety.VarietyPrices[index];
                    if (!isPriceAllowedByRegister(price)) continue;
                    const barcode = price?.barcode == null ? null : String(price.barcode).trim();
                    if (barcode && keySet.has(barcode)) {
                        const match = { variety, varietyPrice: price, priceIndex: index };
                        if (isPriceAllowedForType(clientType, price)) return match;
                        if (!fallbackMatch) fallbackMatch = match;
                    }
                }
            }
        }

        return fallbackMatch;
    };

    const notifyBlockedBarcodeMatch = (blockedMatch) => {
        if (!blockedMatch) return;
        const matchedType = resolvePriceClientType(blockedMatch.varietyPrice);
        if (blockedMatch.varietyPrice?.isSoldOut) {
            notify(clientType
                ? `El precio ${getPriceTypeLabel(clientType)} está agotado para este código.`
                : 'Este código está agotado.');
            return;
        }
        if (clientType && matchesPosFormatFilter(blockedMatch.varietyPrice, '', productFormatOptions)
            && !matchesPosFormatFilter(blockedMatch.varietyPrice, clientType, productFormatOptions)) {
            notify(`Este código corresponde a ${getPriceTypeLabel(matchedType)}. Cambia el formato para agregarlo.`);
            return;
        }
        notify('Este código no está disponible para el formato activo.');
    };

    const processQuickEntry = async (value) => {
        const trimmed = String(value || '').trim();
        if (!trimmed) return;
        const normalized = normalizeSuspendedCodeInput(trimmed);
        if (normalized.startsWith(SUSPENDED_SALE_PREFIX)) {
            await handleResumeSuspendedSale(normalized);
            return;
        }
        if (!/^\d+$/.test(trimmed)) return;

        let match = null;
        let anyMatch = null;

        if (trimmed.length === 13) {
            const mappings = getBarcodePrefixMappings();
            const mode = resolveBarcodeMode(trimmed, mappings);
            if (mode) {
                const decoded = decodeVariableBarcode(trimmed, mode);
                if (decoded.isValid && decoded.isScale) {
                    const keys = buildScaleBarcodeLookupKeys(decoded);
                    match = findVarietyPriceByBarcode(keys);
                    if (!match) {
                        anyMatch = findAnyVarietyPriceByBarcode(keys);
                    }
                    if (match) {
                        applyBarcodeAutoPriceSelectionMeta(match.variety, match.priceIndex);
                        const currentQty = normalizeQuantity(
                            quantities[match.variety.id]?.[match.priceIndex] || 0
                        );
                        if (mode === 'weight') {
                            const nextQty = normalizeQuantity(currentQty + decoded.weightKg, 3);
                            handleQuantityChange(match.variety.id, match.priceIndex, nextQty);
                        } else if (mode === 'amount') {
                            const nextQty = normalizeQuantity(currentQty + decoded.amount, 3);
                            handleQuantityChange(match.variety.id, match.priceIndex, nextQty);
                        } else if (mode === 'price') {
                            const unitPrice = match.varietyPrice?.price || 0;
                            if (unitPrice > 0) {
                                const qtyDelta = normalizeQuantity(decoded.price / unitPrice, 3);
                                const nextQty = normalizeQuantity(currentQty + qtyDelta, 3);
                                handleQuantityChange(match.variety.id, match.priceIndex, nextQty);
                            }
                        }
                    } else if (anyMatch) {
                        notifyBlockedBarcodeMatch(anyMatch);
                    }
                }
            }
        }

        if (!match && trimmed.length <= 7) {
            const keys = buildShortBarcodeLookupKeys(trimmed);
            match = findVarietyPriceByBarcode(keys);
            if (!match) {
                anyMatch = findAnyVarietyPriceByBarcode(keys);
            }
            if (match) {
                applyBarcodeAutoPriceSelectionMeta(match.variety, match.priceIndex);
                handleQuantityIncrement(match.variety.id, match.priceIndex, 1);
            } else if (anyMatch) {
                notifyBlockedBarcodeMatch(anyMatch);
            }
        }

        if (match) {
            applySearch('');
            focusPrimarySearchInput();
        }
    };

    const handleQuickEntryKeyDown = async (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        await processQuickEntry(searchTerm);
    };

    const handleBarcodeScannerDetected = async (value) => {
        const scannedValue = String(value || '').trim();
        if (!scannedValue) return;
        applySearch(scannedValue);
        await processQuickEntry(scannedValue);
    };

    useEffect(() => {
        filterProducts(searchTerm, selectedCategory, selectedSubcategory);
    }, [
        searchTerm,
        selectedCategory,
        selectedSubcategory,
        displayProducts,
        soldOutDisplayMode,
        clientType,
        resolveProductCategoryId,
        resolveProductSubcategoryId,
        allowedClientTypes,
        searchMode,
    ]);

    const setDataForPurchaseDetails = ({ includeEmpty = false } = {}) => {
        const orderedItems = buildPurchaseItemsPayload();
        if (!orderedItems.length && !purchaseId && !includeEmpty) {
            setPurchaseDetails(null);
            return null;
        }

        const nextPurchaseDetails = {
            Client: { name, rut, email, phone, address, city },
            purchaseItems: orderedItems,
            itemsSubtotal,
            orderDiscountAmount,
            orderDiscountPercentage,
            totalAmount,
            id: purchaseId,
            documentType,
            createdAt: draftStartedAtRef.current,
        };

        setPurchaseDetails(nextPurchaseDetails);
        return nextPurchaseDetails;
    };

    useEffect(() => {
        setDataForPurchaseDetails();
    }, [quantities, discounts, orderDiscountAmount, orderDiscountPercentage, totalAmount, itemsSubtotal, linePriceMeta, scaleReadingLines, addedOrder, documentType, purchaseId, name, rut, email, phone, address, city, promotions, resolveProductCategorySnapshot, barcodePricingRule]);

    const openPurchaseModal = () => {
        setDataForPurchaseDetails({ includeEmpty: true });
        setShowPurchaseModal(true);
        setShowDesktopCartPane(false);
        setModalProduct(null);
        setModalVariety(null);
        setModalVarietyPrice(null);
        setSlotModalState(null);
    };
    const openNavbarCart = () => {
        if (!isDesktopViewport()) {
            openPurchaseModal();
            return;
        }
        setDataForPurchaseDetails({ includeEmpty: true });
        setShowDesktopCartPane(true);
        setShowPurchaseModal(false);
        setModalProduct(null);
        setModalVariety(null);
        setModalVarietyPrice(null);
        setSlotModalState(null);
    };
    usePosNavbarCart({ enabled: isEditorPosView, totalAmount: cartSaleRounding.totalAmount, onOpen: openNavbarCart });

    useEffect(() => {
        if (!showDesktopCartPane || typeof document === 'undefined') return undefined;
        const handlePointerDown = (event) => {
            if (!isDesktopViewport()) return;
            if (desktopCartPaneRef.current?.contains(event.target)) return;
            setShowDesktopCartPane(false);
        };
        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
        };
    }, [showDesktopCartPane]);

    const handleCancelOpenSession = () => {
        setShowPosSessionModal(false);
        navigate('/home');
    };

    const handlePrint = async () => {
        if (!purchaseId) return;
        try {
            if (!branchId) {
                notify("Sucursal no definida.");
                return;
            }
            const printMode = getPrintMode();
            const useRemotePrint = printMode === PRINT_MODE_REMOTE;
            if (useRemotePrint) {
                sendPrintRequest(branchId, purchaseId);
            } else {
                const printUrl = `/branches/${branchId}/purchases/${purchaseId}/receipt/preview?autoprint=1`;
                const printWindow = window.open(printUrl, "_blank", "noopener,noreferrer");
                if (!printWindow) {
                    notify("No se pudo abrir la impresion local. Revisa el bloqueador de ventanas emergentes.");
                    return;
                }
            }
            const notification = document.createElement("div");
            notification.textContent = useRemotePrint
                ? "Imprimiendo comprobante..."
                : "Abriendo comprobante en el navegador...";
            notification.style.position = "fixed";
            notification.style.bottom = "20px";
            notification.style.right = "20px";
            notification.style.backgroundColor = "#4caf50";
            notification.style.color = "white";
            notification.style.padding = "10px";
            notification.style.borderRadius = "5px";
            notification.style.boxShadow = "0px 4px 6px rgba(0, 0, 0, 0.1)";
            notification.style.zIndex = "1010";
            document.body.appendChild(notification);

            setTimeout(() => {
                document.body.removeChild(notification);
            }, 3000);
        } catch (error) {
            console.error("Error al imprimir.", error);
            notify("Error al imprimir.");
        }
    };

    const handleRemoveItem = async (itemToRemove) => {
        if (!canUpdateSale) return;
        if (!purchaseId) return;

        setLoadingProcess('Quitando producto de la venta…');
        setIsLoading(true);

        const variety = findVarietyForItem(itemToRemove);
        const resolvedPriceIndex = resolveItemPriceIndex(variety, itemToRemove);
        if (!Number.isInteger(resolvedPriceIndex) || resolvedPriceIndex < 0) {
            console.warn('Cannot remove item without a valid price index.', itemToRemove);
            notify('Advertencia: item sin índice válido. No se puede eliminar de forma segura.');
            setIsLoading(false);
            return;
        }

        if (isScaleReadingLine(itemToRemove)) {
            const nextScaleReadingLines = scaleReadingLines.filter(
                (line) => line.cartLineId !== itemToRemove.cartLineId
            );
            const nextAddedOrder = addedOrder.filter(
                (entry) => getCartIdentityKey(entry) !== getCartIdentityKey(itemToRemove)
            );
            const updatedTotals = computeTotals(
                products,
                quantities,
                discounts,
                orderDiscountAmount,
                orderDiscountPercentage,
                nextScaleReadingLines
            );
            const updatedItems = buildPurchaseItemsPayload({
                scaleReadingLinesData: nextScaleReadingLines,
                addedOrderData: nextAddedOrder,
            });
            setScaleReadingLines(nextScaleReadingLines);
            setAddedOrder(nextAddedOrder);
            setItemsSubtotal(updatedTotals.subtotal);
            setTotalPrice(updatedTotals.total);
            setPurchaseDetails((prev) => ({
                ...prev,
                purchaseItems: updatedItems,
                itemsSubtotal: updatedTotals.subtotal,
                totalAmount: updatedTotals.total,
            }));
            try {
                const response = await updatePurchase(branchId, purchaseId, {
                    purchaseItems: updatedItems,
                    totalAmount: updatedTotals.total,
                    clientId,
                    order: nextAddedOrder,
                    orderDiscountAmount,
                    orderDiscountPercentage,
                    ...(canExcludeTaxes ? { excludedTaxIds } : {}),
                });
            } catch (error) {
                console.error("Error updating purchase:", error);
            } finally {
                setIsLoading(false);
            }
            return;
        }

        const updatedQuantities = { ...quantities };
        const updatedDiscounts = { ...discounts };

        if (variety && resolvedPriceIndex !== -1) {
            updatedQuantities[variety.id] = [...(updatedQuantities[variety.id] || [])];
            updatedQuantities[variety.id][resolvedPriceIndex] = 0;

            updatedDiscounts[variety.id] = [...(updatedDiscounts[variety.id] || [])];
            updatedDiscounts[variety.id][resolvedPriceIndex] = 0;
        }

        const updatedOrder = addedOrder.filter(
            i => !(i.varietyId === itemToRemove.varietyId && i.priceIndex === resolvedPriceIndex)
        );

        const updatedItems = buildPurchaseItemsPayload({
            quantitiesData: updatedQuantities,
            discountsData: updatedDiscounts,
            addedOrderData: updatedOrder,
        });

        const updatedTotals = computeTotals(
            products,
            updatedQuantities,
            updatedDiscounts,
            orderDiscountAmount,
            orderDiscountPercentage
        );
        const updatedItemsSubtotal = updatedTotals.subtotal;
        const updatedTotal = updatedTotals.total;
        setItemsSubtotal(updatedTotals.subtotal);
        setTotalPrice(updatedTotals.total);

        setQuantities(updatedQuantities);
        setDiscounts(updatedDiscounts);
        setAddedOrder(updatedOrder);
        setLinePriceMeta((prev) => {
            const next = { ...prev };
            delete next[lineKey(itemToRemove.varietyId, resolvedPriceIndex)];
            return next;
        });
        setPurchaseDetails((prev) => ({
            ...prev,
            purchaseItems: updatedItems,
            itemsSubtotal: updatedItemsSubtotal,
            totalAmount: updatedTotal
        }));

        try {
            const response = await updatePurchase(branchId, purchaseId, {
                purchaseItems: updatedItems,
                totalAmount: updatedTotal,
                clientId,
                order: updatedOrder,
                orderDiscountAmount,
                orderDiscountPercentage,
                ...(canExcludeTaxes ? { excludedTaxIds } : {}),
            });
        } catch (error) {
            console.error("Error updating purchase:", error);
        }
        finally {
            setIsLoading(false);
        }
    };

    const handleQuantityChange = async (varietyId, priceIndex, value) => {
        if (!canEditSale) return;
        const quantity = normalizeQuantity(value);
        if (quantity > MAX_PURCHASE_ITEM_QUANTITY) {
            notify(`La cantidad excede el maximo permitido (${MAX_PURCHASE_ITEM_QUANTITY}).`);
            return;
        }

        const variety = products.flatMap((product) => product.Varieties || []).find((item) => item.id === varietyId);
        const price = variety?.VarietyPrices?.[priceIndex];
        if (!isPriceAllowedByRegister(price)) {
            return;
        }

        setQuantities((prevQuantities) => {
            const newQuantities = { ...prevQuantities };
            newQuantities[varietyId] = [...(newQuantities[varietyId] || [])];
            newQuantities[varietyId][priceIndex] = quantity;

            // ✅ Add if quantity > 0 and not already present
            if (quantity > 0 && !addedOrder.some(i => i.varietyId === varietyId && i.priceIndex === priceIndex)) {
                setAddedOrder((prev) => [{ varietyId, priceIndex }, ...prev,]);
            }

            // ✅ Remove if quantity is 0 and was in the list
            if (quantity === 0) {
                setAddedOrder((prev) =>
                    prev.filter(i => !(i.varietyId === varietyId && i.priceIndex === priceIndex))
                );
            }

            const total = calculateTotal(newQuantities);
            handlePurchaseSave(
                newQuantities,
                total,
                discounts,
                discountAuthorizations,
                orderDiscountAuthorizationId
            );
            return newQuantities;
        });
    };

    const handleQuantityIncrement = async (varietyId, priceIndex, increment) => {
        if (!canEditSale) return;
        const variety = products.flatMap((product) => product.Varieties || []).find((item) => item.id === varietyId);
        const price = variety?.VarietyPrices?.[priceIndex];
        if (!isPriceAllowedByRegister(price)) {
            return;
        }

        setQuantities((prevQuantities) => {
            const newQuantities = { ...prevQuantities };
            newQuantities[varietyId] = [...(newQuantities[varietyId] || [])];
            const currentQty = normalizeQuantity(newQuantities[varietyId][priceIndex] || 0);
            const quantity = normalizeQuantity(Math.max(0, currentQty + increment));
            if (quantity > MAX_PURCHASE_ITEM_QUANTITY) {
                notify(`La cantidad excede el maximo permitido (${MAX_PURCHASE_ITEM_QUANTITY}).`);
                return prevQuantities;
            }
            newQuantities[varietyId][priceIndex] = quantity;

            // ✅ Add if quantity > 0 and not already present
            if (quantity > 0 && !addedOrder.some(i => i.varietyId === varietyId && i.priceIndex === priceIndex)) {
                setAddedOrder((prev) => [{ varietyId, priceIndex }, ...prev]);
            }

            // ✅ Remove if quantity is 0 and was in the list
            if (quantity === 0) {
                setAddedOrder((prev) =>
                    prev.filter(i => !(i.varietyId === varietyId && i.priceIndex === priceIndex))
                );
            }

            const total = calculateTotal(newQuantities);
            handlePurchaseSave(
                newQuantities,
                total,
                discounts,
                discountAuthorizations,
                orderDiscountAuthorizationId
            );
            return newQuantities;
        });
    };

    const resolveModalPriceIndex = useCallback(() => {
        if (!modalVariety?.VarietyPrices?.length || !modalVarietyPrice) return -1;
        return modalVariety.VarietyPrices.findIndex((entry) => (
            modalVarietyPrice?.id != null ? entry?.id === modalVarietyPrice.id : entry === modalVarietyPrice
        ));
    }, [modalVariety, modalVarietyPrice]);

    const buildActiveScaleSelection = useCallback((variety, price, priceIndex) => ({
        variety,
        price,
        priceIndex,
        varietyId: variety?.id,
        varietyPriceId: price?.id,
    }), []);

    const isSameActiveScaleSelection = useCallback((selection, varietyId, priceIndex, price) => {
        if (!selection) return false;
        if (Number(selection.varietyId) !== Number(varietyId)) return false;
        if (Number.isInteger(selection.priceIndex) && Number(selection.priceIndex) === Number(priceIndex)) {
            return true;
        }
        return selection.varietyPriceId != null && Number(selection.varietyPriceId) === Number(price?.id);
    }, []);

    const getLatestScaleWeightKg = useCallback(() => {
        if (!posFavScaleConnected) return null;
        const rawWeightKg = scaleState?.latestStableWeightKg;
        if (scaleState?.latestReadingWeightKg != null
            && Number(scaleState.latestReadingWeightKg) !== Number(rawWeightKg)) return null;
        const stableAtMs = scaleState?.latestStableAt
            ? new Date(scaleState.latestStableAt).getTime()
            : Number.NaN;
        const freshnessMs = Number(scaleState?.freshnessMs);
        if (
            !Number.isFinite(stableAtMs)
            || !Number.isFinite(freshnessMs)
            || Date.now() - stableAtMs > freshnessMs
        ) return null;
        const weightKg = Number(rawWeightKg);
        return rawWeightKg != null && Number.isFinite(weightKg) && weightKg >= 0 ? weightKg : null;
    }, [
        posFavScaleConnected,
        scaleState?.freshnessMs,
        scaleState?.latestStableAt,
        scaleState?.latestStableWeightKg,
        scaleState?.latestReadingWeightKg,
    ]);

    const saveScaleReadingLines = useCallback(async (nextScaleReadingLines, nextAddedOrder = addedOrder) => {
        setScaleReadingLines(nextScaleReadingLines);
        setAddedOrder(nextAddedOrder);
        const nextTotal = calculateTotal(
            quantities,
            discounts,
            undefined,
            undefined,
            undefined,
            nextScaleReadingLines
        );
        await handlePurchaseSave(
            quantities,
            nextTotal,
            discounts,
            discountAuthorizations,
            orderDiscountAuthorizationId,
            undefined,
            undefined,
            undefined,
            nextScaleReadingLines,
            nextAddedOrder
        );
    }, [
        addedOrder,
        calculateTotal,
        discountAuthorizations,
        discounts,
        handlePurchaseSave,
        orderDiscountAuthorizationId,
        quantities,
    ]);

    const appendScaleReadingLine = useCallback(async (variety, price, priceIndex, weightKg) => {
        const quantity = normalizeQuantity(weightKg, 3);
        if (quantity <= 0) return;
        if (quantity > MAX_PURCHASE_ITEM_QUANTITY) {
            notify(`La cantidad excede el maximo permitido (${MAX_PURCHASE_ITEM_QUANTITY}).`);
            return;
        }
        const duplicateLine = findDuplicateScaleReadingLine(scaleReadingLines, variety.id, quantity);
        if (duplicateLine) {
            const weightLabel = `${quantity.toLocaleString('de-DE', {
                maximumFractionDigits: 3,
            })} kg`;
            const confirmed = await confirm({
                title: 'Peso repetido',
                message: `Ya existe ${variety?.varietyName || 'esta variedad'} con exactamente ${weightLabel}. Si lo agregas otra vez, quedarán dos líneas iguales en la venta. Confirma solo si corresponde a otro pesaje.`,
                confirmLabel: 'Agregar igual',
                cancelLabel: 'Revisar',
                tone: 'warning',
            });
            if (!confirmed) return;
        }
        const nextLine = createScaleReadingLine({
            varietyId: variety.id,
            varietyPriceId: price?.id ?? null,
            priceIndex,
            quantity,
        });
        const nextScaleReadingLines = [nextLine, ...scaleReadingLines];
        const nextAddedOrder = [buildScaleReadingOrderEntry(nextLine), ...addedOrder];
        await saveScaleReadingLines(nextScaleReadingLines, nextAddedOrder);
    }, [addedOrder, confirm, saveScaleReadingLines, scaleReadingLines]);

    const removeLatestScaleReadingLineForPrice = useCallback(async (varietyId, priceIndex, price) => {
        const target = scaleReadingLines.find((line) =>
            lineMatchesPrice(line, varietyId, priceIndex, price)
        );
        if (!target) return false;
        const nextScaleReadingLines = scaleReadingLines.filter(
            (line) => line.cartLineId !== target.cartLineId
        );
        const nextAddedOrder = addedOrder.filter(
            (entry) => getCartIdentityKey(entry) !== getCartIdentityKey(target)
        );
        await saveScaleReadingLines(nextScaleReadingLines, nextAddedOrder);
        return true;
    }, [addedOrder, saveScaleReadingLines, scaleReadingLines]);

    const updateScaleReadingLineQuantity = useCallback(async (cartLineId, value) => {
        const quantity = normalizeQuantity(value, 3);
        if (quantity <= 0) return false;
        if (quantity > MAX_PURCHASE_ITEM_QUANTITY) {
            notify(`La cantidad excede el maximo permitido (${MAX_PURCHASE_ITEM_QUANTITY}).`);
            return false;
        }
        const nextScaleReadingLines = scaleReadingLines.map((line) =>
            line.cartLineId === cartLineId ? { ...line, quantity } : line
        );
        await saveScaleReadingLines(nextScaleReadingLines, addedOrder);
        return true;
    }, [addedOrder, saveScaleReadingLines, scaleReadingLines]);

    const openSlotConfirmModal = useCallback((slotEntry, options = {}) => {
        if (!slotEntry?.variety || !slotEntry?.price || slotEntry?.disabled) return;
        const quantityMode = options?.quantityMode === 'set' ? 'set' : 'add';
        const currentQuantity = normalizeQuantity(
            quantities?.[slotEntry.varietyId]?.[slotEntry.priceIndex] || 0
        );
        applyBarcodeAutoPriceSelectionMeta(slotEntry.variety, slotEntry.priceIndex);
        setShowPurchaseModal(false);
        setModalProduct(null);
        setModalVariety(slotEntry.variety);
        setModalVarietyPrice(slotEntry.price);
        setSlotModalState({
            initialQuantity: quantityMode === 'set' ? currentQuantity : 0,
            baseQuantity: currentQuantity,
            source: 'slot',
            quantityMode,
        });
    }, [applyBarcodeAutoPriceSelectionMeta, quantities]);

    const handleConfirmSlotQuantity = useCallback(async (nextRawQuantity) => {
        const resolvedPriceIndex = resolveModalPriceIndex();
        if (!modalVariety || resolvedPriceIndex < 0) return false;
        if (slotModalState?.source === 'scaleReadingLine') {
            const updated = await updateScaleReadingLineQuantity(
                slotModalState.cartLineId,
                nextRawQuantity
            );
            if (!updated) return false;
            setSlotModalState(null);
            return true;
        }
        const currentQty = normalizeQuantity(
            slotModalState?.baseQuantity ?? quantities?.[modalVariety.id]?.[resolvedPriceIndex] ?? 0
        );
        const nextQty = slotModalState?.quantityMode === 'set'
            ? normalizeQuantity(nextRawQuantity)
            : normalizeQuantity(currentQty + nextRawQuantity);
        if (nextQty > MAX_PURCHASE_ITEM_QUANTITY) {
            await handleQuantityChange(modalVariety.id, resolvedPriceIndex, nextQty);
            return false;
        }
        await handleQuantityChange(modalVariety.id, resolvedPriceIndex, nextQty);
        setSlotModalState(null);
        return true;
    }, [
        handleQuantityChange,
        modalVariety,
        quantities,
        resolveModalPriceIndex,
        slotModalState?.baseQuantity,
        slotModalState?.cartLineId,
        slotModalState?.quantityMode,
        slotModalState?.source,
        updateScaleReadingLineQuantity,
    ]);

    const handleModalQuantityChange = useCallback((varietyId, priceIndex, value) => {
        if (!slotModalState || !modalVariety || Number(modalVariety.id) !== Number(varietyId)) {
            return handleQuantityChange(varietyId, priceIndex, value);
        }
        if (slotModalState?.source === 'scaleReadingLine') {
            return updateScaleReadingLineQuantity(slotModalState.cartLineId, value);
        }

        const rawQuantity = normalizeQuantity(value);
        const baseQuantity = normalizeQuantity(
            slotModalState?.baseQuantity ?? quantities?.[varietyId]?.[priceIndex] ?? 0
        );
        const nextQty = slotModalState?.quantityMode === 'set'
            ? rawQuantity
            : normalizeQuantity(baseQuantity + rawQuantity);
        if (nextQty > MAX_PURCHASE_ITEM_QUANTITY) {
            return handleQuantityChange(varietyId, priceIndex, nextQty);
        }
        return handleQuantityChange(varietyId, priceIndex, nextQty);
    }, [
        handleQuantityChange,
        modalVariety,
        quantities,
        slotModalState,
        updateScaleReadingLineQuantity,
    ]);

    const handleAddPosSlot = useCallback(() => {
        if (!posSlotLayoutScopeKey) {
            notify('Selecciona una caja activa antes de agregar favs.');
            return;
        }
        setShowSlotPickerModal(true);
    }, [posSlotLayoutScopeKey]);

    const handleSlotAddSelection = useCallback(async (entry) => {
        try {
            await saveCurrentPosSlotLayout([
                ...currentPosSlotLayout,
                {
                    varietyId: entry?.varietyId,
                    varietyPriceId: entry?.varietyPriceId,
                    ...(getFormatOfferId(entry) ? { formatOfferId: getFormatOfferId(entry) } : {}),
                },
            ]);
            setShowSlotPickerModal(false);
        } catch (error) {
            notify(error?.message || 'No se pudo guardar el fav.');
        }
    }, [currentPosSlotLayout, saveCurrentPosSlotLayout]);

    const handlePriceCardIncrement = useCallback(async (price, priceIndex) => {
        if (!modalVariety || !isPriceAllowedByRegister(price)) return;
        applyBarcodeAutoPriceSelectionMeta(modalVariety, priceIndex);
        const usesScale = isScaleInputPrice(price);
        const weightKg = usesScale ? getLatestScaleWeightKg() : null;
        const action = resolvePosFavTapAction({
            direction: 'increment',
            usesScale,
            scaleConnected: posFavScaleConnected,
            scaleWeightKg: weightKg,
        });
        if (action === POS_FAV_TAP_ACTIONS.INCREMENT_ONE) {
            if (usesScale) setActiveScaleSelection(null);
            await handleQuantityIncrement(modalVariety.id, priceIndex, 1);
            return;
        }

        setActiveScaleSelection(buildActiveScaleSelection(modalVariety, price, priceIndex));
        if (action === POS_FAV_TAP_ACTIONS.WAIT_FOR_SCALE_READING) {
            notify('Espera una lectura estable mayor a 0 kg antes de confirmar el pesaje.');
            return;
        }

        await appendScaleReadingLine(modalVariety, price, priceIndex, weightKg);
    }, [
        applyBarcodeAutoPriceSelectionMeta,
        appendScaleReadingLine,
        buildActiveScaleSelection,
        getLatestScaleWeightKg,
        handleQuantityIncrement,
        isPriceAllowedByRegister,
        modalVariety,
        posFavScaleConnected,
    ]);

    const handlePriceCardDecrement = useCallback(async (price, priceIndex) => {
        if (!modalVariety || !isPriceAllowedByRegister(price)) return;
        applyBarcodeAutoPriceSelectionMeta(modalVariety, priceIndex);
        const usesScale = isScaleInputPrice(price);
        const action = resolvePosFavTapAction({
            direction: 'decrement',
            usesScale,
            scaleConnected: posFavScaleConnected,
        });
        if (action === POS_FAV_TAP_ACTIONS.DECREMENT_ONE) {
            if (usesScale) setActiveScaleSelection(null);
            await handleQuantityIncrement(modalVariety.id, priceIndex, -1);
            return;
        }

        if (isSameActiveScaleSelection(activeScaleSelection, modalVariety.id, priceIndex, price)) {
            setActiveScaleSelection(null);
        }
        const removedScaleLine = await removeLatestScaleReadingLineForPrice(modalVariety.id, priceIndex, price);
        if (!removedScaleLine) {
            await handleQuantityChange(modalVariety.id, priceIndex, 0);
        }
    }, [
        applyBarcodeAutoPriceSelectionMeta,
        activeScaleSelection,
        handleQuantityChange,
        handleQuantityIncrement,
        isPriceAllowedByRegister,
        isSameActiveScaleSelection,
        modalVariety,
        posFavScaleConnected,
        removeLatestScaleReadingLineForPrice,
    ]);

    const handlePriceCardKeypad = useCallback((price, priceIndex) => {
        if (!modalVariety || !isPriceAllowedByRegister(price)) return;
        if (isScaleInputPrice(price)) {
            setActiveScaleSelection(null);
        }
        openSlotConfirmModal({
            variety: modalVariety,
            price,
            priceIndex,
            varietyId: modalVariety.id,
            varietyPriceId: price?.id,
        }, { quantityMode: 'set' });
    }, [isPriceAllowedByRegister, modalVariety, openSlotConfirmModal]);

    const handleFavoritePriceIncrement = useCallback(async (slotEntry) => {
        const { variety, price, priceIndex } = slotEntry || {};
        if (!canEditSale || !canSell || slotEntry?.disabled || !variety || !price || !isPriceAllowedByRegister(price)) return;
        applyBarcodeAutoPriceSelectionMeta(variety, priceIndex);
        const usesScale = isScaleInputPrice(price);
        const weightKg = usesScale ? getLatestScaleWeightKg() : null;
        const action = resolvePosFavTapAction({
            direction: 'increment',
            usesScale,
            scaleConnected: posFavScaleConnected,
            scaleWeightKg: weightKg,
        });
        if (action === POS_FAV_TAP_ACTIONS.INCREMENT_ONE) {
            if (usesScale) setActiveScaleSelection(null);
            await handleQuantityIncrement(variety.id, priceIndex, 1);
            return;
        }

        setActiveScaleSelection(buildActiveScaleSelection(variety, price, priceIndex));
        if (action === POS_FAV_TAP_ACTIONS.WAIT_FOR_SCALE_READING) {
            notify('Espera una lectura estable mayor a 0 kg antes de confirmar el pesaje.');
            return;
        }
        await appendScaleReadingLine(variety, price, priceIndex, weightKg);
    }, [
        appendScaleReadingLine,
        applyBarcodeAutoPriceSelectionMeta,
        buildActiveScaleSelection,
        canEditSale,
        canSell,
        getLatestScaleWeightKg,
        handleQuantityIncrement,
        isPriceAllowedByRegister,
        posFavScaleConnected,
    ]);

    const handleFavoritePriceDecrement = useCallback(async (slotEntry) => {
        const { variety, price, priceIndex } = slotEntry || {};
        if (!canEditSale || !canSell || slotEntry?.disabled || !variety || !price || !isPriceAllowedByRegister(price)) return;
        applyBarcodeAutoPriceSelectionMeta(variety, priceIndex);
        const usesScale = isScaleInputPrice(price);
        const action = resolvePosFavTapAction({
            direction: 'decrement',
            usesScale,
            scaleConnected: posFavScaleConnected,
        });
        if (action === POS_FAV_TAP_ACTIONS.DECREMENT_ONE) {
            if (usesScale) setActiveScaleSelection(null);
            await handleQuantityIncrement(variety.id, priceIndex, -1);
            return;
        }

        if (isSameActiveScaleSelection(activeScaleSelection, variety.id, priceIndex, price)) {
            setActiveScaleSelection(null);
        }
        const removedScaleLine = await removeLatestScaleReadingLineForPrice(
            variety.id,
            priceIndex,
            price
        );
        if (!removedScaleLine) {
            await handleQuantityChange(variety.id, priceIndex, 0);
        }
    }, [
        activeScaleSelection,
        applyBarcodeAutoPriceSelectionMeta,
        canEditSale,
        canSell,
        handleQuantityChange,
        handleQuantityIncrement,
        isPriceAllowedByRegister,
        isSameActiveScaleSelection,
        posFavScaleConnected,
        removeLatestScaleReadingLineForPrice,
    ]);

    const handleFavoritePriceKeypad = useCallback((slotEntry) => {
        const { price } = slotEntry || {};
        if (!canEditSale || !canSell || slotEntry?.disabled || !slotEntry?.variety || !price || !isPriceAllowedByRegister(price)) return;
        if (isScaleInputPrice(price)) setActiveScaleSelection(null);
        openSlotConfirmModal(slotEntry, { quantityMode: 'set' });
    }, [canEditSale, canSell, isPriceAllowedByRegister, openSlotConfirmModal]);

    const handlePosFavGuideAcknowledge = useCallback(() => (
        saveMyPreferences({ posFavGesturesSeen: true })
    ), [saveMyPreferences]);

    const handleDiscountChange = async (varietyId, priceIndex, value) => {
        if (!canEditSale || !canSell) return;
        const offerPrice = products.flatMap((product) => product.Varieties || []).find((variety) => Number(variety.id) === Number(varietyId))?.VarietyPrices?.[priceIndex];
        if (isFormatOfferPrice(offerPrice)) {
            notify('La oferta tiene un precio final fijo y no admite descuentos adicionales.');
            return;
        }
        const parsedValue = parseInt(value);
        const discount = isNaN(parsedValue) || parsedValue < 0 ? 0 : parsedValue;

        if (discount > 0) {
            const variety = products
                .flatMap((product) => product.Varieties || [])
                .find((item) => item.id === varietyId);
            const label = variety?.varietyName || null;
            const opened = openDiscountAuthorization({
                type: 'item',
                varietyId,
                priceIndex,
                discountAmount: discount,
                discountPercent: null,
                label,
            });
            if (opened) return;
        }

        applyItemDiscount(varietyId, priceIndex, discount);
    }

    const applyItemDiscount = (varietyId, priceIndex, discount, authorizationId = null) => {
        const key = lineKey(varietyId, priceIndex);
        const nextLinePriceMeta = {
            ...linePriceMeta,
            [key]: {
                ...(linePriceMeta[key] || {}),
                discountSource: discount > 0 ? 'manual' : 'none',
                manualDiscountCandidate: discount > 0 ? discount : null,
            },
        };
        setLinePriceMeta(nextLinePriceMeta);
        setDiscounts((prevDiscounts) => {
            const newDiscounts = { ...prevDiscounts };
            newDiscounts[varietyId] = [...(newDiscounts[varietyId] || [])];
            newDiscounts[varietyId][priceIndex] = discount;

            setDiscountAuthorizations((prevAuthorizations) => {
                const nextAuthorizations = { ...prevAuthorizations };
                if (!nextAuthorizations[varietyId]) nextAuthorizations[varietyId] = [];
                nextAuthorizations[varietyId][priceIndex] = discount > 0 ? authorizationId : null;
                const total = calculateTotal(quantities, newDiscounts);
                handlePurchaseSave(
                    quantities,
                    total,
                    newDiscounts,
                    nextAuthorizations,
                    orderDiscountAuthorizationId,
                    undefined,
                    undefined,
                    nextLinePriceMeta,
                );
                return nextAuthorizations;
            });

            return newDiscounts;
        });
    };

    const getPendingDiscountKey = (payload) =>
        payload?.type === 'item'
            ? `${payload.varietyId}-${payload.priceIndex}`
            : 'order';

    const openDiscountAuthorization = (payload) => {
        if (!canSell) return false;
        if (isRegisterlessMode) {
            if (!activeRegisterlessProfile?.id) {
                notify('Debes seleccionar venta sin caja antes de aplicar descuentos.');
                return false;
            }
        } else if (!cashRegisterId) {
            notify('Debes seleccionar una caja antes de aplicar descuentos.');
            return false;
        }
        if (isAdmin) return false;
        setPendingDiscountAuthorization(payload);
        setDiscountAuthorizationInitialState(null);
        setShowDiscountAuthorizationModal(true);
        return true;
    };

    const handleDiscountRequested = (response) => {
        if (!pendingDiscountAuthorization) return;
        const entry = {
            ...pendingDiscountAuthorization,
            requested: true,
            expiresAt: response?.expiresAt || null,
            attemptsRemaining:
                typeof response?.attemptsRemaining === 'number'
                    ? response.attemptsRemaining
                    : null,
        };
        if (entry.type === 'item') {
            const key = getPendingDiscountKey(entry);
            setPendingDiscountRequests((prev) => ({
                ...prev,
                items: { ...prev.items, [key]: entry },
            }));
        } else {
            setPendingDiscountRequests((prev) => ({ ...prev, order: entry }));
        }
    };

    const clearPendingDiscount = (payload) => {
        if (!payload) return;
        if (payload.type === 'item') {
            const key = getPendingDiscountKey(payload);
            setPendingDiscountRequests((prev) => {
                if (!prev.items?.[key]) return prev;
                const nextItems = { ...prev.items };
                delete nextItems[key];
                return { ...prev, items: nextItems };
            });
        } else {
            setPendingDiscountRequests((prev) => ({ ...prev, order: null }));
        }
    };

    const resumeDiscountAuthorization = (payload) => {
        if (!payload) return;
        setPendingDiscountAuthorization(payload);
        setDiscountAuthorizationInitialState({
            requested: true,
            expiresAt: payload.expiresAt || null,
            attemptsRemaining:
                typeof payload.attemptsRemaining === 'number' ? payload.attemptsRemaining : null,
        });
        setShowDiscountAuthorizationModal(true);
    };

    const handleOrderDiscountApply = (amount, percentage) => {
        if (!canEditSale || !canSell) return;
        const discountAmount = (() => {
            if (typeof percentage === 'number' && percentage > 0) {
                return Math.floor((itemsSubtotal || 0) * (percentage / 100));
            }
            if (typeof amount === 'number') return amount;
            return 0;
        })();
        if (discountAmount > 0) {
            const opened = openDiscountAuthorization({
                type: 'order',
                discountAmount,
                discountPercent: typeof percentage === 'number' ? percentage : null,
                orderAmount: amount,
                orderPercent: percentage,
                label: 'Descuento de orden',
            });
            if (opened) return;
        }
        applyOrderDiscount(amount, percentage);
    };

    const applyOrderDiscount = (amount, percentage, authorizationId = null) => {
        const hasDiscount = (typeof percentage === 'number' && percentage > 0)
            || (typeof amount === 'number' && amount > 0);
        const nextAuthorizationId = hasDiscount ? authorizationId : null;
        setOrderDiscountAmount(percentage != null ? null : amount);
        setOrderDiscountPercentage(percentage != null ? percentage : null);
        setOrderDiscountAuthorizationId(nextAuthorizationId);
        const total = calculateTotal(quantities, discounts, amount, percentage);
        handlePurchaseSave(
            quantities,
            total,
            discounts,
            discountAuthorizations,
            nextAuthorizationId,
            amount,
            percentage
        );
    };

    function calculateTotal(
        quantitiesData,
        discountsData,
        overrideOrderDiscountAmount,
        overrideOrderDiscountPercentage,
        productsOverride,
        scaleReadingLinesOverride
    ) {
        const totals = computeTotals(
            Array.isArray(productsOverride) ? productsOverride : products,
            quantitiesData,
            discountsData,
            overrideOrderDiscountAmount,
            overrideOrderDiscountPercentage,
            scaleReadingLinesOverride
        );
        setItemsSubtotal(totals.subtotal);
        setTotalPrice(totals.total);
        return totals.total;
    }

    const catalogVarietyIndex = useMemo(() => buildCatalogVarietyIndex(products), [products]);

    const buildPurchaseItemsPayload = ({
        productsData = products,
        quantitiesData = quantities,
        discountsData = discounts,
        discountAuthorizationsData = discountAuthorizations,
        linePriceMetaData = linePriceMeta,
        scaleReadingLinesData = scaleReadingLines,
        addedOrderData = addedOrder,
        orderDiscountAmountData = orderDiscountAmount,
        orderDiscountPercentageData = orderDiscountPercentage,
    } = {}) => {
        const autoPriceResolution = resolvePosAutoPriceSelection({
            products: productsData,
            quantities: quantitiesData,
            scaleReadingLines: scaleReadingLinesData,
            linePriceMeta: linePriceMetaData,
            globalRule: barcodePricingRule,
            isSellablePrice,
            canSelectPriceType: varietySupportsBarcodePriceTypeSelection,
            lineKey,
        });
        const resolveLinePriceSelection = ({ variety, price, priceIndex, line = null }) => {
            const resolvedLine = getResolvedPosAutoPriceLine(autoPriceResolution, {
                varietyId: variety?.id,
                priceIndex,
                cartLineId: line?.cartLineId,
            });
            const effectivePriceIndex = Number.isInteger(resolvedLine?.selectedPriceIndex)
                ? resolvedLine.selectedPriceIndex
                : priceIndex;
            const meta = linePriceMetaData[lineKey(variety.id, priceIndex)] || {};
            const snapshotPrice = resolvePosPriceSnapshot(salePriceSnapshotsRef.current, {
                variety, sourcePrice: price, selectedPrice: resolvedLine?.priceRow || price,
                clientId, clientPricingContext: posClientPricingContext(meta), now: promotionNow,
                cartLineId: line?.cartLineId, selection: `${meta.priceType || ''}:${Boolean(meta.priceTypeOverride)}`,
            });
            const snapshotIndex = variety.VarietyPrices.findIndex((entry) => String(entry.id) === String(snapshotPrice.id));
            return {
                price: snapshotPrice,
                priceIndex: snapshotIndex >= 0 ? snapshotIndex : effectivePriceIndex,
                sourcePriceIndex: priceIndex,
                sourceVarietyPriceId: price?.parentVarietyPriceId ?? price?.id ?? null,
            };
        };
        const rawItems = (productsData || []).reduce((acc, product) => {
            const productItems = product.Varieties.flatMap((variety) =>
                (variety.VarietyPrices || []).map((price, index) => {
                    const quantity = normalizeQuantity(quantitiesData?.[variety.id]?.[index] || 0);
                    if (quantity <= 0) return null;
                    const meta = linePriceMetaData[lineKey(variety.id, index)] || null;
                    const lineDiscount = discountsData?.[variety.id]?.[index] || 0;
                    const priceSelection = resolveLinePriceSelection({ variety, price, priceIndex: index });
                    if (!isPriceAllowedByRegister(price) && !isPriceAllowedByRegister(priceSelection.price)) return null;
                    const priceTypePayload = resolveLinePriceTypePayload(variety, priceSelection.price, meta);
                    const categorySnapshot = resolveProductCategorySnapshot(product);
                    return buildPromotionLineItem({
                        product,
                        variety,
                        price: priceSelection.price,
                        priceIndex: priceSelection.priceIndex,
                        quantity,
                        discount: lineDiscount,
                        extra: {
                            sourcePriceIndex: priceSelection.sourcePriceIndex,
                            sourceVarietyPriceId: priceSelection.sourceVarietyPriceId,
                            discountAuthorizationId: discountAuthorizationsData?.[variety.id]?.[index] ?? null,
                            discountSource: meta?.discountSource || (lineDiscount > 0 ? 'manual' : 'none'),
                            clientPricingDiscountType: meta?.clientPricingDiscountType,
                            clientPricingDiscountValue: meta?.clientPricingDiscountValue,
                            clientPricingCandidateRules: meta?.clientPricingCandidateRules,
                            clientPricingCategoryId: meta?.clientPricingCategoryId || null,
                            clientPricingCategoryName: meta?.clientPricingCategoryName || null,
                            clientPricingRuleType: meta?.clientPricingRuleType,
                            clientPricingMarginReductionPercent: meta?.clientPricingMarginReductionPercent,
                            clientPricingReferenceCost: meta?.clientPricingReferenceCost,
                            clientPricingSnapshot: meta?.clientPricingSnapshot,
                            manualDiscount: meta?.manualDiscountCandidate
                                ?? (meta?.discountSource === 'manual' ? lineDiscount : 0),
                            ...categorySnapshot,
                            priceType: priceTypePayload.priceType,
                            priceTypeOverride: priceTypePayload.priceTypeOverride,
                            ...(priceTypePayload.autoPriceSelection ? { autoPriceSelection: true } : {}),
                        },
                    });
                }).filter(Boolean)
            );
            return acc.concat(productItems);
        }, []);
        const varietyIndex = productsData === products ? catalogVarietyIndex : buildCatalogVarietyIndex(productsData);
        const scaleLineItems = (scaleReadingLinesData || []).map((line) => {
            const { product, variety } = varietyIndex.get(Number(line?.varietyId)) || {};
            if (!product || !variety) return null;
            const priceIndex = Number.isInteger(line?.priceIndex)
                ? line.priceIndex
                : (variety.VarietyPrices || []).findIndex(
                    (price) => Number(price?.id) === Number(line?.varietyPriceId)
                );
            const price = variety.VarietyPrices?.[priceIndex];
            const quantity = normalizeQuantity(line?.quantity || 0);
            if (quantity <= 0) return null;
            const meta = linePriceMetaData[lineKey(variety.id, priceIndex)] || null;
            const lineDiscount = discountsData?.[variety.id]?.[priceIndex] || 0;
            const priceSelection = resolveLinePriceSelection({ variety, price, priceIndex, line });
            if (!isPriceAllowedByRegister(price) && !isPriceAllowedByRegister(priceSelection.price)) return null;
            const priceTypePayload = resolveLinePriceTypePayload(variety, priceSelection.price, meta);
            const categorySnapshot = resolveProductCategorySnapshot(product);
            return buildPromotionLineItem({
                product,
                variety,
                price: priceSelection.price,
                priceIndex: priceSelection.priceIndex,
                quantity,
                discount: lineDiscount,
                extra: {
                    cartLineId: line.cartLineId,
                    isScaleReadingLine: true,
                    source: line.source || 'scale',
                    createdAt: line.createdAt || null,
                    sourcePriceIndex: priceSelection.sourcePriceIndex,
                    sourceVarietyPriceId: priceSelection.sourceVarietyPriceId,
                    discountAuthorizationId: discountAuthorizationsData?.[variety.id]?.[priceIndex] ?? null,
                    discountSource: meta?.discountSource || (lineDiscount > 0 ? 'manual' : 'none'),
                    clientPricingDiscountType: meta?.clientPricingDiscountType,
                    clientPricingDiscountValue: meta?.clientPricingDiscountValue,
                    clientPricingCandidateRules: meta?.clientPricingCandidateRules,
                    clientPricingCategoryId: meta?.clientPricingCategoryId || null,
                    clientPricingCategoryName: meta?.clientPricingCategoryName || null,
                    clientPricingRuleType: meta?.clientPricingRuleType,
                    clientPricingMarginReductionPercent: meta?.clientPricingMarginReductionPercent,
                    clientPricingReferenceCost: meta?.clientPricingReferenceCost,
                    clientPricingSnapshot: meta?.clientPricingSnapshot,
                    manualDiscount: meta?.manualDiscountCandidate
                        ?? (meta?.discountSource === 'manual' ? lineDiscount : 0),
                    ...categorySnapshot,
                    priceType: priceTypePayload.priceType,
                    priceTypeOverride: priceTypePayload.priceTypeOverride,
                    ...(priceTypePayload.autoPriceSelection ? { autoPriceSelection: true } : {}),
                },
            });
        }).filter(Boolean);
        return applyPromotionsToLineItems(
            orderAndIndexLineItems([...rawItems, ...scaleLineItems], addedOrderData),
            promotions,
            'sale',
            {
                orderDiscountAmount: orderDiscountAmountData,
                orderDiscountPercentage: orderDiscountPercentageData,
                taxProfileByVarietyId: varietyTaxProfileById,
                now: promotionNow,
            }
        );
    };

    const requireSaleItems = (message = EMPTY_POS_SALE_MESSAGE) => {
        const offerIssue = getFormatOfferCartMessage(products, quantities, scaleReadingLines);
        if (offerIssue) { notify(offerIssue); return false; }
        if (hasPosSaleItems(buildPurchaseItemsPayload())) return true;
        notify(message);
        return false;
    };

    const persistPurchasePayload = (payload, options = {}) => {
        const { resolveClientId = false, onError } = options;
        const writeGeneration = purchaseStateGenerationRef.current;
        const queuedWrite = purchaseWriteQueueRef.current
            .catch(() => undefined)
            .then(async () => {
                try {
                    if (writeGeneration !== purchaseStateGenerationRef.current) {
                        return { response: null, purchaseId: null };
                    }

                    const resolvedPayload = resolveClientId
                        ? { ...payload, clientId: clientIdRef.current }
                        : payload;
                    const currentPurchaseId = purchaseIdRef.current;
                    if (currentPurchaseId) {
                        const response = await updatePurchase(
                            branchId,
                            currentPurchaseId,
                            resolvedPayload
                        );
                        return { response, purchaseId: currentPurchaseId };
                    }

                    const response = await registerPurchase(branchId, resolvedPayload);
                    const createdPurchaseId = response?.purchase?.id;
                    if (!createdPurchaseId) {
                        throw new Error('No se pudo identificar la venta creada.');
                    }
                    if (writeGeneration === purchaseStateGenerationRef.current) {
                        purchaseIdRef.current = createdPurchaseId;
                        setPurchaseId(createdPurchaseId);
                    }
                    return { response, purchaseId: createdPurchaseId };
                } catch (error) {
                    if (typeof onError === 'function') onError(error);
                    throw error;
                }
            });

        purchaseWriteQueueRef.current = queuedWrite.then(
            () => undefined,
            () => undefined
        );
        return queuedWrite;
    };

    const savePurchase = async (idClient, overrideDocumentType, options = {}) => {
        const currentPurchaseId = purchaseIdRef.current;
        const canSave = currentPurchaseId ? canUpdateSale : canCreateSale;
        if (!canSave) {
            notify('No tienes permisos para guardar la venta.');
            return null;
        }
        const isRegisterlessSave = Boolean(isRegisterlessMode && activeRegisterlessProfile?.id);
        const resolvedSaleName = String(options.saleName ?? saleName ?? '').trim();
        if (isRegisterlessMode) {
            if (!activeRegisterlessProfile?.id) {
                throw new Error('Debes seleccionar venta sin caja antes de guardar.');
            }
            if (!resolvedSaleName) {
                notify('Debes ingresar un nombre para enviar la venta a caja.');
                return null;
            }
        }
        const clientTypeIssues = collectDisallowedPosClientTypeIssues({
            products,
            quantities,
            scaleReadingLines,
            allowedClientTypes,
            isSellablePrice,
            resolveEffectivePrice: resolveEffectivePriceForCurrentLine,
        });
        if (clientTypeIssues.length) {
            notify(buildDisallowedClientTypeMessage(
                clientTypeIssues,
                isRegisterlessMode ? 'venta enviada a caja' : 'caja'
            ));
            return null;
        }
        if (!isRegisterlessMode && (!cashRegisterSessionId || !isActiveSessionOwnedByUser)) {
            setShowPosSessionModal(true);
            if (!isActiveSessionOwnedByUser && activeSession) {
                throw new Error('Caja abierta por otro usuario.');
            }
            throw new Error('Debe abrir una caja antes de guardar la venta.');
        }
        const saleScopePayload = isRegisterlessSave
            ? {
                registerlessProfileId: activeRegisterlessProfile.id,
                saleName: resolvedSaleName,
                saleStatus: 'suspended',
            }
            : (
                cashRegisterId
                    ? { cashRegisterId, cashRegisterSessionId }
                    : {}
            );
        const taxExclusionPayload = canExcludeTaxes ? { excludedTaxIds } : {};
        const resolvedDocumentType = isRegisterlessSave
            ? null
            : (overrideDocumentType !== undefined ? overrideDocumentType : documentType);
        const items = buildPurchaseItemsPayload();
        if (isRegisterlessSave && !items.length) {
            notify('No hay productos permitidos para enviar a caja.');
            return null;
        }
        const computedTotals = computeTotals(
            products,
            quantities,
            discounts,
            orderDiscountAmount,
            orderDiscountPercentage
        );
        const parsedTotal = Number(totalAmount);
        const resolvedTotalAmount = Number.isFinite(parsedTotal)
            ? parsedTotal
            : computedTotals.total;
        const invalidQuantityItem = findInvalidPurchaseItemQuantity(items);
        if (invalidQuantityItem) {
            notify(buildPurchaseItemQuantityLimitMessage(invalidQuantityItem));
            return null;
        }
        const effectiveClientId = idClient !== undefined ? idClient : clientIdRef.current;
        const clientAssignmentPayload = options.clientAssignment
            ? buildPosClientAssignmentPayload({
                clientId: effectiveClientId,
                purchaseId: currentPurchaseId,
                isRegisterlessMode,
            })
            : null;
        if (!currentPurchaseId && !items.length && !clientAssignmentPayload) {
            return null;
        }
        let response;
        let persistedPurchaseId = null;
        try {
            const persisted = await saveWithSaleTotalConfirmation({
                save: (payload) => persistPurchasePayload(payload, {
                    resolveClientId: idClient === undefined,
                    onError: options.onPersistenceError,
                }),
                confirm,
                onTotalChanged: async (preview) => {
                    setTotalPrice(Number(preview.totalBeforeRounding ?? (Number(preview.totalAmount) + Number(preview.roundingDiscountAmount || 0))));
                    await saleRounding.refresh();
                },
                payload: {
                    purchaseItems: items,
                    totalAmount: resolvedTotalAmount,
                    clientId: effectiveClientId,
                    order: addedOrder,
                    orderDiscountAmount,
                    orderDiscountPercentage,
                    orderDiscountAuthorizationId,
                    documentType: resolvedDocumentType,
                    ...(!isRegisterlessSave ? { saleStatus: options.finalizeAsCash ? 'completed' : 'open' } : {}),
                    ...(options.finalizeAsCash ? buildTemporaryPosQuickFinalizeCheckout(
                        resolvedTotalAmount, options.roundingSettings || saleRounding.settings
                    ) : {}),
                    ...saleScopePayload,
                    ...taxExclusionPayload,
                    ...(clientAssignmentPayload || {}),
                },
            });
            if (!persisted) return null;
            response = persisted.response;
            persistedPurchaseId = persisted.purchaseId;
            setDocumentType(response?.purchase?.documentType || resolvedDocumentType || null);
            showInventoryWarnings(response?.inventoryWarnings);
            setPosError('');
        } catch (error) {
            const message =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                'No se pudo guardar la venta.';
            setPosError(message);
        } finally {
            return persistedPurchaseId || response?.purchase?.id;
        }
    };

    const applyTemporaryQuickFinalizePayment = async (idPurchase) => {
        const savedPurchase = await getPurchaseById(branchId, idPurchase);
        const balance = getTemporaryPosQuickFinalizeBalance(savedPurchase);
        if (balance > 0) {
            await addPurchasePayment(branchId, idPurchase, {
                amount: balance,
                method: TEMPORARY_POS_QUICK_FINALIZE_PAYMENT_METHOD,
            });
        }
        await updatePurchase(
            branchId,
            idPurchase,
            buildTemporaryPosQuickFinalizePaymentUpdate(balance)
        );
    };

    const handleTemporaryQuickFinalize = async () => {
        if (quickFinalizeInFlightRef.current || !requireSaleItems()) return;
        quickFinalizeInFlightRef.current = true;
        setLoadingProcess('Guardando venta y preparando comprobante…');
        setIsLoading(true);
        setPendingDocumentType(TEMPORARY_POS_QUICK_FINALIZE_DOCUMENT_TYPE);
        setDocumentType(TEMPORARY_POS_QUICK_FINALIZE_DOCUMENT_TYPE);
        setShowDocumentTypeModal(false);
        let idPurchase;
        try {
            const roundingSettings = await saleRounding.refresh();
            if (!roundingSettings) { notify('No se pudo cargar la configuración de redondeo. Intenta nuevamente.'); return; }
            idPurchase = await savePurchase(
                clientId || null,
                TEMPORARY_POS_QUICK_FINALIZE_DOCUMENT_TYPE,
                { finalizeAsCash: true, roundingSettings }
            );
            if (!idPurchase) {
                notify("Error al generar el recibo.");
                return;
            }
            await applyTemporaryQuickFinalizePayment(idPurchase);
            navigate(buildTemporaryPosQuickFinalizeReceiptPath(branchId, idPurchase));
        } catch (error) {
            console.error('Error in temporary POS quick finalize:', error);
            notify(error?.response?.data?.message || error?.message || 'Error al finalizar la venta.');
            if (idPurchase) navigate(`/branches/${branchId}/purchases/${idPurchase}/receipt`);
        } finally {
            quickFinalizeInFlightRef.current = false;
            setPendingDocumentType(null);
            setIsLoading(false);
        }
    };

    const handleFinishPurchase = async () => {
        if (!canEditSale) return;
        if (!requireSaleItems()) return;
        if (isRegisterlessMode) {
            const requestedName = window.prompt('Nombre para identificar la venta enviada a caja', saleName || name || '');
            if (requestedName === null) return;
            const resolvedSaleName = requestedName.trim();
            if (!resolvedSaleName) {
                notify('Debes ingresar un nombre para enviar la venta a caja.');
                return;
            }
            setSaleName(resolvedSaleName);
            setLoadingProcess('Guardando venta y preparando comprobante…');
            setIsLoading(true);
            try {
                const idPurchase = await savePurchase(clientId || null, null, { saleName: resolvedSaleName });
                if (!idPurchase) {
                    return;
                }
                resetSaleState();
                const postSubmitDestination = resolveRegisterlessPostSubmitDestination({
                    registerlessProfile: activeRegisterlessProfile,
                });
                if (postSubmitDestination === REGISTERLESS_POST_SUBMIT_DESTINATION_NEW_SALE) {
                    notify('Venta enviada a caja.');
                    navigate(`/branches/${branchId}/purchases/new`);
                } else {
                    navigate(`/branches/${branchId}/purchases/${idPurchase}/receipt`);
                }
            } catch (error) {
                console.error('Error finalizing registerless sale:', error);
                notify(error?.response?.data?.message || error?.message || 'No se pudo enviar la venta a caja.');
            } finally {
                setIsLoading(false);
            }
            return;
        }
        if (TEMPORARY_POS_QUICK_FINALIZE_ENABLED) {
            await handleTemporaryQuickFinalize();
            return;
        }
        setPendingDocumentType(null);
        setShowDocumentTypeModal(true);
    };

    const handleDocumentTypeSelect = async (type) => {
        if (!requireSaleItems()) {
            setPendingDocumentType(null);
            setShowDocumentTypeModal(false);
            return;
        }
        setPendingDocumentType(type);
        if (type === 'quotation') {
            setShowDocumentTypeModal(false);
            const confirmed = await confirm({
                title: 'Convertir a cotización',
                message: '¿Convertir esta venta a cotización? La venta será eliminada.',
                confirmLabel: 'Convertir',
                cancelLabel: 'Cancelar',
                tone: 'warning',
            });
            if (!confirmed) {
                setPendingDocumentType(null);
                return;
            }
            await handleConvertToQuotation();
            setPendingDocumentType(null);
            return;
        }
        const confirmMsg = type === 'boleta'
            ? 'Finalizar como Boleta (cliente opcional)?'
            : 'Finalizar como Factura (requiere cliente). ¿Continuar?';

        const confirmed = await confirm({
            title: 'Finalizar venta',
            message: confirmMsg,
            confirmLabel: 'Continuar',
            cancelLabel: 'Cancelar',
            tone: 'warning',
        });
        if (!confirmed) {
            setPendingDocumentType(null);
            return;
        }

        setDocumentType(type);
        setShowDocumentTypeModal(false);

        if (type === 'boleta') {
            await handlePurchase(clientId || null, 'boleta');
            setPendingDocumentType(null);
            return;
        }

        if (clientId) {
            await handlePurchase(clientId, type);
            setPendingDocumentType(null);
            return;
        }

        setPendingFinishAfterClient(true);
        setShowPurchaseCard(true);
    };

    const handleClientSelected = async (client, options = {}) => {
        const selectionSource = options?.source || 'manual';
        const isAutomaticSelection = selectionSource === 'auto' || selectionSource === 'auto-clear';
        const applyClientMetadata = () => {
            setGiro(client?.giro || '');
            pendingClientTypeRef.current = clientType || '';
        };

        if (pendingConversionAfterClient) {
            if (isAutomaticSelection) return false;
            const selectedClientId = client?.id || clientId;
            if (!selectedClientId) return false;
            if (!requireSaleItems('Agrega productos antes de convertir la venta a cotización.')) {
                return false;
            }
            const converted = await handleConvertToQuotation(selectedClientId);
            if (!converted) return false;
            setPendingConversionAfterClient(false);
            setPendingDocumentType(null);
            applyClientMetadata();
            return true;
        }
        if (pendingFinishAfterClient && pendingDocumentType === 'factura') {
            if (isAutomaticSelection) return false;
            const selectedClientId = client?.id || clientId;
            if (!selectedClientId) return false;
            if (!requireSaleItems()) {
                return false;
            }
            const saved = await handlePurchase(selectedClientId, 'factura');
            if (!saved) return false;
            setPendingFinishAfterClient(false);
            setPendingDocumentType(null);
            applyClientMetadata();
            return true;
        }
        applyClientMetadata();
        return true;
    };

    const handleClientTypeChange = async (newType) => {
        if (!newType) {
            setClientType('');
            setTypeSelection('');
            return;
        }
        const resolvedType = resolveAllowedClientType(newType);
        if (!resolvedType) return;
        setClientType(resolvedType);
        setTypeSelection(resolvedType);
    };

    const handleCloseClientModal = (open) => {
        setShowPurchaseCard(open);
        if (!open) {
            const shouldResetDocumentType = shouldResetDocumentTypeAfterClientModal({
                pendingFinishAfterClient,
                pendingConversionAfterClient,
                pendingConversionAfterType,
            });
            setPendingFinishAfterClient(false);
            setPendingConversionAfterClient(false);
            setPendingConversionAfterType(false);
            setPendingDocumentType(null);
            if (shouldResetDocumentType) setDocumentType(null);
            pendingClientTypeRef.current = null;
        }
    };

    const handleCloseDocumentTypeModal = (open) => {
        setShowDocumentTypeModal(open);
        if (!open) {
            setPendingDocumentType(null);
            setPendingFinishAfterClient(false);
            setPendingConversionAfterClient(false);
            setPendingConversionAfterType(false);
        }
    };

    const handleCloseTypeModal = () => {
        setShowTypeModal(false);
        setPendingConversionAfterType(false);
    };

    const handleConvertToQuotation = async (overrideClientId) => {
        if (!canConvertToQuotation) return false;
        if (!requireSaleItems('Agrega productos antes de convertir la venta a cotización.')) return false;
        if (isRegisterlessMode) {
            notify('Debes usar una caja activa para convertir la venta a cotización.');
            return false;
        }
        if (!cashRegisterSessionId) {
            setShowPosSessionModal(true);
            return false;
        }
        const resolvedClientId = overrideClientId ?? clientId;
        if (!resolvedClientId) {
            setPendingConversionAfterClient(true);
            setShowPurchaseCard(true);
            return false;
        }
        try {
            const idPurchase = await savePurchase(resolvedClientId, null);
            if (!idPurchase) {
                notify('Error al preparar la venta para convertir.');
                return false;
            }
            const response = await convertPurchaseToQuotation(branchId, idPurchase, {
                clientId: resolvedClientId,
            });
            resetSaleState();
            if (response?.quotation?.id) {
                navigate(`/branches/${branchId}/quotations/${response.quotation.id}`);
            } else {
                navigate(`/branches/${branchId}/quotations`);
            }
            return true;
        } catch (error) {
            const data = error?.response?.data;
            if (data?.issues?.length) {
                const details = data.issues
                    .map((issue) => issue.varietyName
                        ? `${issue.varietyName}: ${issue.reason}`
                        : issue.reason)
                    .join('\n');
                notify(`${data.message || 'No se pudo convertir la venta.'}\n${details}`);
                return false;
            }
            notify(data?.message || error.message || 'No se pudo convertir la venta.');
            return false;
        }
    };

    async function handlePurchaseSave(
        newQuantities,
        total,
        newDiscounts,
        discountAuthorizationsOverride,
        orderDiscountAuthorizationOverride,
        orderDiscountAmountOverride,
        orderDiscountPercentageOverride,
        linePriceMetaOverride,
        scaleReadingLinesOverride,
        addedOrderOverride,
    ) {
        const currentPurchaseId = purchaseIdRef.current;
        const canSave = currentPurchaseId ? canUpdateSale : canCreateSale;
        if (!canSave) return;
        const isRegisterlessSave = Boolean(isRegisterlessMode && activeRegisterlessProfile?.id);
        const resolvedSaleName = String(saleName || '').trim();
        if (isRegisterlessMode) {
            if (!activeRegisterlessProfile?.id) return;
            if (!currentPurchaseId || !resolvedSaleName) return;
        } else if (!cashRegisterSessionId) {
            setShowPosSessionModal(true);
            return;
        }
        const saleScopePayload = isRegisterlessSave
            ? {
                registerlessProfileId: activeRegisterlessProfile.id,
                saleName: resolvedSaleName,
                saleStatus: 'suspended',
            }
            : (
                cashRegisterId
                    ? { cashRegisterId, cashRegisterSessionId }
                    : {}
            );
        const taxExclusionPayload = canExcludeTaxes ? { excludedTaxIds } : {};
        const resolvedDiscounts = newDiscounts || discounts;
        const resolvedDiscountAuthorizations = discountAuthorizationsOverride || discountAuthorizations;
        const resolvedOrderDiscountAmount = orderDiscountAmountOverride !== undefined
            ? orderDiscountAmountOverride
            : orderDiscountAmount;
        const resolvedOrderDiscountPercentage = orderDiscountPercentageOverride !== undefined
            ? orderDiscountPercentageOverride
            : orderDiscountPercentage;
        const resolvedOrderDiscountAuthorizationId = orderDiscountAuthorizationOverride !== undefined
            ? orderDiscountAuthorizationOverride
            : orderDiscountAuthorizationId;
        const resolvedQuantities = { ...quantities, ...(newQuantities || {}) };
        const resolvedScaleReadingLines = scaleReadingLinesOverride ?? scaleReadingLines;
        const resolvedAddedOrder = addedOrderOverride ?? addedOrder;
        const resolvedLinePriceMeta = linePriceMetaOverride || linePriceMeta;
        const resolvedAutoPriceResolution = resolvePosAutoPriceSelection({
            products,
            quantities: resolvedQuantities,
            scaleReadingLines: resolvedScaleReadingLines,
            linePriceMeta: resolvedLinePriceMeta,
            globalRule: barcodePricingRule,
            isSellablePrice,
            canSelectPriceType: varietySupportsBarcodePriceTypeSelection,
            lineKey,
        });
        const offerIssue = getFormatOfferCartMessage(products, resolvedQuantities, resolvedScaleReadingLines);
        if (offerIssue) { notify(offerIssue); return false; }
        const clientTypeIssues = collectDisallowedPosClientTypeIssues({
            products,
            quantities: resolvedQuantities,
            scaleReadingLines: resolvedScaleReadingLines,
            allowedClientTypes,
            isSellablePrice,
            resolveEffectivePrice: ({ variety, priceIndex, line }) =>
                getResolvedPosAutoPriceLine(resolvedAutoPriceResolution, {
                    varietyId: variety?.id,
                    priceIndex,
                    cartLineId: line?.cartLineId,
                })?.priceRow || null,
        });
        if (clientTypeIssues.length) {
            notify(buildDisallowedClientTypeMessage(
                clientTypeIssues,
                isRegisterlessMode ? 'venta enviada a caja' : 'caja'
            ));
            return;
        }
        const computedTotals = computeTotals(
            products,
            resolvedQuantities,
            resolvedDiscounts,
            resolvedOrderDiscountAmount,
            resolvedOrderDiscountPercentage,
            resolvedScaleReadingLines
        );
        const parsedTotal = Number(total);
        const resolvedTotalAmount = Number.isFinite(parsedTotal)
            ? parsedTotal
            : computedTotals.total;
        const items = buildPurchaseItemsPayload({
            quantitiesData: resolvedQuantities,
            discountsData: resolvedDiscounts,
            discountAuthorizationsData: resolvedDiscountAuthorizations,
            linePriceMetaData: resolvedLinePriceMeta,
            scaleReadingLinesData: resolvedScaleReadingLines,
            addedOrderData: resolvedAddedOrder,
            orderDiscountAmountData: resolvedOrderDiscountAmount,
            orderDiscountPercentageData: resolvedOrderDiscountPercentage,
        });
        const invalidQuantityItem = findInvalidPurchaseItemQuantity(items);
        if (invalidQuantityItem) {
            notify(buildPurchaseItemQuantityLimitMessage(invalidQuantityItem));
            return;
        }

        try {
            const { response } = await persistPurchasePayload(
                {
                    purchaseItems: items,
                    totalAmount: resolvedTotalAmount,
                    clientId: clientIdRef.current,
                    order: resolvedAddedOrder,
                    documentType: isRegisterlessSave ? null : documentType,
                    orderDiscountAmount: resolvedOrderDiscountAmount,
                    orderDiscountPercentage: resolvedOrderDiscountPercentage,
                    orderDiscountAuthorizationId: resolvedOrderDiscountAuthorizationId,
                    ...(!isRegisterlessSave ? { saleStatus: 'open' } : {}),
                    ...saleScopePayload,
                    ...taxExclusionPayload,
                },
                { resolveClientId: true }
            );
            setDocumentType(response?.purchase?.documentType || documentType || null);
            showInventoryWarnings(response?.inventoryWarnings);
        } catch (error) {
            notify(getApiErrorMessage(error, 'No se pudo guardar la venta.'));
        }
    }

    const handlePurchase = async (overrideClientId, overrideDocumentType) => {
        if (!requireSaleItems()) return false;
        try {
            let idPurchase = await savePurchase(overrideClientId, overrideDocumentType);
            if (idPurchase) {
                navigate(`/branches/${branchId}/purchases/${idPurchase}/receipt`); // ✅ Redirect to PDF view
                return true;
            } else {
                notify("Error al generar el recibo.");
                return false;
            }
        } catch (error) {
            notify(getApiErrorMessage(error, 'No se pudo generar el recibo.'));
            return false;
        }
    };

    const handleOpenClientModal = () => {
        if (!canReadClient && !canCreateClient) {
            notify('No tienes permisos para seleccionar clientes.');
            return;
        }
        setShowPurchaseCard(true);
    };

    const handleSaveClient = async (selectedClientId) => {
        pendingClientTypeRef.current = null;
        const previousClientId = clientIdRef.current;
        clientIdRef.current = selectedClientId;
        const restorePendingClientId = () => {
            if (clientIdRef.current === selectedClientId) {
                clientIdRef.current = previousClientId;
            }
        };
        const currentPurchaseId = purchaseIdRef.current;
        if (currentPurchaseId) {
            if (!canUpdateSale) {
                clientIdRef.current = previousClientId;
                return null;
            }
            const isRegisterlessSave = Boolean(isRegisterlessMode && activeRegisterlessProfile?.id);
            const resolvedSaleName = String(saleName || '').trim();
            if (isRegisterlessMode) {
                if (!activeRegisterlessProfile?.id || !resolvedSaleName) {
                    clientIdRef.current = previousClientId;
                    return null;
                }
            } else if (!cashRegisterSessionId || !isActiveSessionOwnedByUser) {
                clientIdRef.current = previousClientId;
                setShowPosSessionModal(true);
                if (!isActiveSessionOwnedByUser && activeSession) {
                    return null;
                }
                return null;
            }
            const saleScopePayload = isRegisterlessSave
                ? {
                    registerlessProfileId: activeRegisterlessProfile.id,
                    saleName: resolvedSaleName,
                }
                : (
                    cashRegisterId
                        ? { cashRegisterId, cashRegisterSessionId }
                        : {}
                );
            const taxExclusionPayload = canExcludeTaxes ? { excludedTaxIds } : {};
            const isEmptyDraftAssignment = !isRegisterlessMode
                && !hasPosSaleItems(buildPurchaseItemsPayload());
            const emptyDraftLifecyclePayload = isEmptyDraftAssignment
                ? {
                    saleStatus: 'open',
                    paymentConfirmed: false,
                    isPaid: false,
                    documentType: null,
                }
                : {};
            try {
                const persisted = await persistPurchasePayload(
                    {
                        clientId: selectedClientId,
                        ...emptyDraftLifecyclePayload,
                        ...saleScopePayload,
                        ...taxExclusionPayload,
                    },
                    { onError: restorePendingClientId }
                );
                if (!persisted.purchaseId) {
                    restorePendingClientId();
                    return null;
                }
                const response = persisted.response;
                if (isEmptyDraftAssignment) setDocumentType(null);
                setPosError('');
                return persisted.purchaseId;
            } catch (error) {
                restorePendingClientId();
                const message =
                    error?.response?.data?.message ||
                    error?.response?.data?.error ||
                    error?.message ||
                    'No se pudo actualizar el cliente de la venta.';
                setPosError(message);
                return null;
            }
        }

        if (isRegisterlessMode) {
            clientIdRef.current = previousClientId;
            return null;
        }

        const savedPurchaseId = await savePurchase(
            selectedClientId,
            undefined,
            {
                clientAssignment: true,
                onPersistenceError: restorePendingClientId,
            }
        );
        if (!savedPurchaseId) restorePendingClientId();
        return savedPurchaseId;
    };

    const orderLabel = documentType === 'boleta'
        ? 'Boleta'
        : documentType === 'factura'
            ? 'Factura'
            : 'Orden';
    const editorActivePurchaseId = Number.isFinite(Number(purchaseId)) ? Number(purchaseId) : null;
    const resolveProductNameForVariety = useCallback((varietyId) => {
        const matchingProduct = products.find((product) =>
            (product.Varieties || []).some((entry) => Number(entry?.id) === Number(varietyId))
        );
        return matchingProduct?.productName || null;
    }, [products]);
    const activeScaleWeighing = useMemo(() => {
        const activeVariety = activeScaleSelection?.variety;
        const activePrice = activeScaleSelection?.price;
        if (!isEditorPosView || !activeVariety || !activePrice) return null;
        if (!isScaleInputPrice(activePrice)) return null;
        const resolvedPriceIndex = Number.isInteger(activeScaleSelection?.priceIndex)
            ? activeScaleSelection.priceIndex
            : (activeVariety?.VarietyPrices || []).findIndex(
                (entry) => Number(entry?.id) === Number(activePrice?.id)
            );
        const unitDiscount = resolvedPriceIndex >= 0
            ? Number(discounts?.[activeVariety?.id]?.[resolvedPriceIndex] || 0)
            : 0;
        const display = resolveVarietyPriceDisplay(activeVariety, activePrice, unitDiscount);
        const weightKg = Number(scaleDisplayWeightKg);
        const meta = linePriceMeta[lineKey(activeVariety.id, resolvedPriceIndex)] || {};
        const preview = !getFormatOfferId(activePrice) && weightKg > 0 ? previewPosWeighing({
            items: buildPurchaseItemsPayload(),
            item: buildPromotionLineItem({ variety: activeVariety, price: activePrice, priceIndex: resolvedPriceIndex,
                quantity: weightKg, discount: unitDiscount, extra: { ...meta,
                    manualDiscount: meta.manualDiscountCandidate ?? (meta.discountSource === 'manual' ? unitDiscount : 0) } }),
            promotions, now: promotionNow,
        }) : null;
        const previewSubtotal = preview ? resolveCartItemPricing(preview, activeVariety, weightKg).subtotal : null;
        return {
            activePurchaseId: editorActivePurchaseId,
            varietyId: activeVariety?.id,
            varietyPriceId: activePrice?.parentVarietyPriceId ?? activePrice?.id,
            ...(getFormatOfferId(activePrice) ? { formatOfferId: getFormatOfferId(activePrice) } : {}),
            productName: resolveProductNameForVariety(activeVariety?.id),
            varietyName: activeVariety?.varietyName || null,
            metricUnit: activePrice?.metricUnit || null,
            unitPrice: preview ? previewSubtotal / weightKg : Number.isFinite(Number(display?.price)) ? Number(display.price) : null,
            ...(preview ? { weightKg, subtotal: previewSubtotal, promotionName: preview.promotionName } : {}),
        };
    }, [
        quantities, scaleReadingLines, products, promotions, promotionNow, linePriceMeta, barcodePricingRule, clientId,
        scaleDisplayWeightKg, resolveCartItemPricing,
        activeScaleSelection,
        discounts,
        editorActivePurchaseId,
        isEditorPosView,
        resolveProductNameForVariety,
        resolveVarietyPriceDisplay,
    ]);
    const cartWrapperClassName = `hidden md:flex md:flex-col fixed right-0 top-14 bottom-[96px] z-30 overflow-visible transition-[width,background-color] duration-200 ease-out ${
        showDesktopCartPane
            ? 'w-[max(440px,33.333333vw)] rounded-l-xl border-l border-t border-gray-200 bg-slate-50 shadow-2xl'
            : 'w-3 border-l border-gray-200 bg-gray-100/70 shadow-none'
    }`;

    const cartPanelClassName = 'rounded-xl border border-gray-100 bg-white p-3 shadow-sm md:min-h-0 md:flex-1 md:overflow-y-auto';

    const mainContentClassName = 'flex-1 p-4 md:pb-28 overflow-auto';

    useEffect(() => {
        if (!posSyncEnabled || !isEditorPosView) return;
        publishActivePurchaseId(editorActivePurchaseId);
    }, [
        editorActivePurchaseId,
        isEditorPosView,
        posSyncConnectionStatus,
        posSyncEnabled,
        publishActivePurchaseId,
    ]);

    useEffect(() => {
        if (!posSyncEnabled || !isEditorPosView) return;
        publishSharedPreview(purchaseDetails);
    }, [
        isEditorPosView,
        posSyncConnectionStatus,
        posSyncEnabled,
        publishSharedPreview,
        purchaseDetails,
    ]);

    useEffect(() => {
        if (!posSyncEnabled || !isEditorPosView) return;
        publishSharedWeighing(activeScaleWeighing);
    }, [
        activeScaleWeighing,
        isEditorPosView,
        posSyncConnectionStatus,
        posSyncEnabled,
        publishSharedWeighing,
    ]);

    useEffect(() => {
        setActiveVarietyGroupId(ALL_VARIETY_GROUP_ID);
    }, [modalProduct?.id]);

    useEffect(() => {
        if (!posSyncEnabled || !isEditorPosView) return undefined;
        return () => {
            publishActivePurchaseId(null);
            publishSharedPreview(null);
            publishSharedWeighing(null);
        };
    }, [isEditorPosView, posSyncEnabled, publishActivePurchaseId, publishSharedPreview, publishSharedWeighing]);

    const findVarietyForItem = (item) =>
        products.flatMap((product) => product.Varieties).find((variety) => variety.id === item.varietyId);

    const resolveItemPriceIndex = (variety, item) => {
        if (!variety?.VarietyPrices?.length || !item) return -1;
        if (getFormatOfferId(item)) return variety.VarietyPrices.findIndex((price) => Number(price.id) === getSaleOptionId(item));
        if (Number.isInteger(item.sourcePriceIndex)) return item.sourcePriceIndex;
        if (Number.isInteger(item.priceIndex)) return item.priceIndex;
        if (Number.isInteger(item.varietyPriceId)) {
            const byId = variety.VarietyPrices.findIndex((price) => price.id === item.varietyPriceId);
            if (byId !== -1) return byId;
        }
        if (item.metricUnit) {
            const byMetric = variety.VarietyPrices.findIndex(
                (price) => price.metricUnit === item.metricUnit && (item.price == null || price.price === item.price)
            );
            if (byMetric !== -1) return byMetric;
        }
        if (item.price != null) {
            const byPrice = variety.VarietyPrices.findIndex((price) => price.price === item.price);
            if (byPrice !== -1) return byPrice;
        }
        return -1;
    };

    const cartItems = useMemo(() => {
        const baseItems = getPurchaseItems(purchaseDetails);
        if (!baseItems.length) return [];
        return baseItems.map((item) => {
            if (Number.isInteger(item.priceIndex) && !getFormatOfferId(item)) return item;
            const resolvedIndex = resolveItemPriceIndex(findVarietyForItem(item), item);
            if (Number.isInteger(resolvedIndex) && resolvedIndex >= 0) {
                return { ...item, priceIndex: resolvedIndex };
            }
            return item;
        });
    }, [purchaseDetails, products]);
    const cartPaneHeader = (
        <div className="-mx-3 -mt-3 mb-1 flex items-center justify-between gap-3 rounded-t-xl border-b border-gray-100 bg-white px-3 py-3">
            <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">Carrito</div>
                <div className="truncate text-xs text-gray-500">
                    {cartItems.length} {cartItems.length === 1 ? 'producto' : 'productos'}
                </div>
            </div>
            <button
                type="button"
                onClick={() => setShowDesktopCartPane(false)}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Ocultar carrito"
                title="Ocultar carrito"
            >
                <FiChevronRight size={20} />
            </button>
        </div>
    );

    const displayQuantities = useMemo(() => {
        const next = { ...(quantities || {}) };
        (scaleReadingLines || []).forEach((line) => {
            if (!Number.isInteger(line?.varietyId) || !Number.isInteger(line?.priceIndex)) return;
            next[line.varietyId] = [...(next[line.varietyId] || [])];
            next[line.varietyId][line.priceIndex] = normalizeQuantity(
                normalizeQuantity(next[line.varietyId][line.priceIndex] || 0) + normalizeQuantity(line.quantity || 0),
                3
            );
        });
        return next;
    }, [quantities, scaleReadingLines]);

    const getItemQuantity = (item) => {
        if (isScaleReadingLine(item)) {
            return normalizeQuantity(item.quantity ?? 0);
        }
        const resolvedIndex = resolveItemPriceIndex(findVarietyForItem(item), item);
        if (!Number.isInteger(resolvedIndex) || resolvedIndex < 0) {
            return normalizeQuantity(item.quantity ?? 0);
        }
        return normalizeQuantity(quantities[item.varietyId]?.[resolvedIndex] || 0);
    };

    const openScaleReadingLineQuantityModal = (item, variety) => {
        if (!item?.cartLineId || !variety) return false;
        const resolvedPriceIndex = resolveItemPriceIndex(variety, item);
        const price = variety.VarietyPrices?.[resolvedPriceIndex];
        if (!price) return false;
        setActiveScaleSelection(null);
        setShowPurchaseModal(false);
        setModalProduct(null);
        setModalVariety(variety);
        setModalVarietyPrice(price);
        setSlotModalState({
            source: 'scaleReadingLine',
            cartLineId: item.cartLineId,
            initialQuantity: normalizeQuantity(item.quantity || 0),
            baseQuantity: 0,
            quantityMode: 'set',
        });
        return true;
    };

    const getItemPriceTypeOptions = (item, variety) => {
        if (!variety) return [];
        if (isScaleReadingLine(item)) return [];
        if (!varietySupportsBarcodePriceTypeSelection(variety)) return [];
        const seen = new Set();
        const options = [];
        (variety.VarietyPrices || []).forEach((price) => {
            if (!isPriceAllowedByRegister(price)) return;
            const type = normalizePriceTypeValue(resolvePriceClientType(price));
            if (!type || seen.has(type)) return;
            seen.add(type);
            options.push({ value: type, label: getPriceTypeLabel(type) });
        });
        return options.length > 1 ? options : [];
    };

    const handleItemPriceTypeChange = (item, nextTypeValue) => {
        if (!canOverrideLinePriceType || !canEditSale) return;
        const variety = findVarietyForItem(item);
        if (!variety) return;
        if (!varietySupportsBarcodePriceTypeSelection(variety)) {
            notify('Solo se puede cambiar el formato cuando la variedad comparte el mismo codigo de barras en todos sus precios.');
            return;
        }
        const currentIndex = resolveItemPriceIndex(variety, item);
        if (!Number.isInteger(currentIndex) || currentIndex < 0) return;

        const currentKey = lineKey(variety.id, currentIndex);
        const normalizedType = normalizePriceTypeValue(nextTypeValue);
        if (!normalizedType) {
            const nextLineMeta = {
                ...linePriceMeta,
                [currentKey]: {
                    priceType: normalizePriceTypeValue(item?.priceType)
                        || resolvePriceClientType(variety.VarietyPrices?.[currentIndex]),
                    priceTypeOverride: false,
                    autoPriceSelection: Boolean(linePriceMeta[currentKey]?.autoPriceSelection),
                },
            };
            setLinePriceMeta(nextLineMeta);
            handlePurchaseSave(
                quantities,
                totalAmount,
                discounts,
                discountAuthorizations,
                orderDiscountAuthorizationId,
                undefined,
                undefined,
                nextLineMeta
            );
            return;
        }

        const targetCandidates = (variety.VarietyPrices || [])
            .map((price, index) => ({ price, index }))
            .filter(({ price }) =>
                !isFormatOfferPrice(price) && isFormatOfferSelectable(price)
                && isPriceAllowedByRegister(price)
                && normalizePriceTypeValue(resolvePriceClientType(price)) === normalizedType
            )
            .sort((a, b) => Number(a.price?.price || 0) - Number(b.price?.price || 0));
        const targetIndex = targetCandidates[0]?.index;
        if (!Number.isInteger(targetIndex) || targetIndex < 0) return;

        if (targetIndex === currentIndex) {
            const nextLineMeta = {
                ...linePriceMeta,
                [currentKey]: {
                    priceType: normalizedType,
                    priceTypeOverride: true,
                    autoPriceSelection: false,
                },
            };
            setLinePriceMeta(nextLineMeta);
            handlePurchaseSave(
                quantities,
                totalAmount,
                discounts,
                discountAuthorizations,
                orderDiscountAuthorizationId,
                undefined,
                undefined,
                nextLineMeta
            );
            return;
        }

        const currentQty = normalizeQuantity(
            quantities?.[variety.id]?.[currentIndex] || item?.quantity || 0
        );
        if (currentQty <= 0) return;

        const nextQuantities = { ...quantities };
        const nextDiscounts = { ...discounts };
        const nextAuthorizations = { ...discountAuthorizations };
        const nextOrder = [...addedOrder];
        const targetKey = lineKey(variety.id, targetIndex);

        nextQuantities[variety.id] = [...(nextQuantities[variety.id] || [])];
        nextDiscounts[variety.id] = [...(nextDiscounts[variety.id] || [])];
        nextAuthorizations[variety.id] = [...(nextAuthorizations[variety.id] || [])];

        const sourceDiscount = Number(nextDiscounts[variety.id][currentIndex] || 0);
        const sourceAuthorizationId = nextAuthorizations[variety.id][currentIndex] ?? null;

        nextQuantities[variety.id][targetIndex] = normalizeQuantity(
            (nextQuantities[variety.id][targetIndex] || 0) + currentQty
        );
        nextQuantities[variety.id][currentIndex] = 0;

        if (nextDiscounts[variety.id][targetIndex] == null) {
            nextDiscounts[variety.id][targetIndex] = sourceDiscount;
        }
        nextDiscounts[variety.id][currentIndex] = 0;

        if (nextAuthorizations[variety.id][targetIndex] == null) {
            nextAuthorizations[variety.id][targetIndex] = sourceAuthorizationId;
        }
        nextAuthorizations[variety.id][currentIndex] = null;

        const currentOrderIndex = nextOrder.findIndex(
            (entry) => entry.varietyId === variety.id && entry.priceIndex === currentIndex
        );
        if (currentOrderIndex !== -1) {
            nextOrder[currentOrderIndex] = { varietyId: variety.id, priceIndex: targetIndex };
        } else if (!nextOrder.some((entry) => entry.varietyId === variety.id && entry.priceIndex === targetIndex)) {
            nextOrder.unshift({ varietyId: variety.id, priceIndex: targetIndex });
        }

        setQuantities(nextQuantities);
        setDiscounts(nextDiscounts);
        setDiscountAuthorizations(nextAuthorizations);
        setAddedOrder(nextOrder);
        const nextLineMeta = { ...linePriceMeta };
        delete nextLineMeta[currentKey];
        nextLineMeta[targetKey] = {
            priceType: normalizedType,
            priceTypeOverride: true,
            autoPriceSelection: false,
        };
        setLinePriceMeta(nextLineMeta);

        const nextTotal = calculateTotal(nextQuantities, nextDiscounts);
        handlePurchaseSave(
            nextQuantities,
            nextTotal,
            nextDiscounts,
            nextAuthorizations,
            orderDiscountAuthorizationId,
            undefined,
            undefined,
            nextLineMeta
        );
    };

    const isVarietySoldOut = (variety) => {
        return isVarietySoldOutForFilter(variety);
    };

    const isProductSoldOut = (product) => {
        return isProductSoldOutForFilter(product);
    };

    const renderCartActionButtons = (className = '') => (
        <div className={`flex items-center gap-2 flex-nowrap overflow-x-auto max-w-full ${className}`}>
            <div className="flex items-center gap-2 flex-nowrap shrink-0">
                {canEditSale && canSell && (
                    <button
                        onClick={() => setShowOrderDiscountModal(true)}
                        className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 px-3 rounded text-sm flex items-center gap-2"
                        title="Descuento total"
                        type="button"
                    >
                        <FiPercent />
                        <span className="hidden md:inline">Descuento</span>
                    </button>
                )}
                {canEditSale && canSell && canExcludeTaxes && (
                    <button
                        onClick={() => setShowTaxModal(true)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold p-3 rounded text-sm flex items-center justify-center disabled:opacity-60"
                        title="Excluir impuestos"
                        type="button"
                        disabled={taxesLoading || !!taxesError || taxes.length === 0}
                    >
                        <FiPercent />
                    </button>
                )}
            </div>
            <div className="ml-auto flex items-center gap-2 flex-nowrap shrink-0">
                {purchaseId && (
                    <button
                        onClick={handlePrint}
                        className="bg-slate-700 hover:bg-slate-800 text-white font-bold py-3 px-4 rounded text-sm flex items-center"
                        title="Imprimir"
                        type="button"
                    >
                        <FiPrinter />
                    </button>
                )}
                {canEditSale && canUpdateSale && !isRegisterlessMode && (
                    <button
                        onClick={handleSuspendSale}
                        className="bg-orange-500 hover:bg-orange-600 text-white font-bold p-3 min-w-[44px] rounded text-sm flex items-center justify-center"
                        title="Suspender"
                        type="button"
                    >
                        <FiPauseCircle />
                    </button>
                )}
                {canEditSale && (
                    <button
                        onClick={handleFinishPurchase}
                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-3 rounded text-sm flex items-center gap-2"
                        title="Finalizar"
                        type="button"
                    >
                        <FiCheckCircle />
                        <span className="hidden md:inline">Finalizar</span>
                    </button>
                )}
            </div>
        </div>
    );
    const cartFooterActions = renderCartActionButtons('mt-2');
    const desktopCartActionBar = (
        <div className="hidden md:block fixed bottom-0 app-fixed-full-width z-20 border-t border-gray-200 bg-white/95 px-4 py-3 shadow-[0_-8px_24px_rgba(15,23,42,0.12)] backdrop-blur">
            <div className="mx-auto max-w-7xl">
                {renderCartActionButtons()}
            </div>
        </div>
    );

    const renderVarietyResultCard = ({ product, variety }) => {
        const imageSrc = variety.varietyImage || product.productImage || 'https://placehold.co/200';
        const isSoldOut = isVarietySoldOut(variety);
        const isSelectable = !isSoldOut;

        return (
            <PosProductCard
                key={`${product.id}-${variety.id}`}
                productName={product.productName}
                subtitle={variety.varietyName}
                imageSrc={imageSrc}
                disabled={!isSelectable}
                badge={isSoldOut ? 'Agotado' : ''}
                onClick={() => {
                    handleVarietySelection({ product, variety });
                }}
            />
        );
    };

    const renderProductCard = (product) => {
        const imageSrc = product.productImage || 'https://placehold.co/200';
        const isSoldOut = isProductSoldOut(product);

        return (
            <PosProductCard
                key={product.id}
                productName={product.productName}
                imageSrc={imageSrc}
                badge={isSoldOut ? 'Agotado' : ''}
                onClick={() => setModalProduct(product)}
            />
        );
    };

    const renderCategoryPicker = (itemsWrapperClassName = '') => {
        if (selectedCategory !== 'all') {
            return (
                <CategoryFilterBar
                    categories={displaySubcategories}
                    selectedCategory={selectedSubcategory}
                    onSelect={handleSubcategorySelect}
                    includeAll
                    allItem={{ id: 'back', label: 'Volver', initial: <FiArrowLeft size={18} aria-hidden="true" /> }}
                    leadingItems={[{ id: 'all', label: 'Todos', initial: 'T' }]}
                    loading={categoriesLoading}
                    loadingMessage={<InlineLoading label="Cargando subcategorías" />}
                    error={categoriesError}
                    emptyMessage="No hay subcategorías para esta categoría."
                    isEmpty={false}
                    itemsWrapperClassName={itemsWrapperClassName}
                />
            );
        }
        return (
            <CategoryFilterBar
                categories={displayCategories}
                selectedCategory={selectedCategory}
                onSelect={handleCategorySelect}
                includeAll
                allItem={{ id: 'all', label: 'Todos', initial: 'T' }}
                loading={categoriesLoading}
                loadingMessage={<InlineLoading label="Cargando categorías" />}
                error={categoriesError}
                emptyMessage="No hay categorías disponibles para esta caja."
                isEmpty={displayCategories.length === 0}
                itemsWrapperClassName={itemsWrapperClassName}
            />
        );
    };

    const renderHelperContent = () => (
        <div className="px-3 py-3 sm:px-4 sm:py-4">
            <PosClientView
                purchase={helperSharedPurchase}
                loading={helperSharedPurchaseLoading}
                error={helperSharedPurchaseError}
                connectionStatus={posSyncConnectionStatus}
                hasEditor={posSyncHasEditor}
                scaleState={scaleState}
                weighing={posSyncWeighing}
            />
        </div>
    );

    return (
        <>
            {isLoading && <LoadingOverlay label={loadingProcess} />}
            <PosSessionModal
                isOpen={showPosSessionModal}
                mode="open"
                registers={cashRegisters}
                registersByBranch={posModalRegistersByBranch}
                categoriesByBranch={posModalCategoriesByBranch}
                branches={posModalBranches}
                activeBranchId={branchId}
                loading={posModalLoading}
                error={posModalError}
                activeRegisterId={cashRegisterId}
                onSelectSession={handleSelectSession}
                onSelectRegisterless={handleSelectRegisterless}
                onOpenSession={handleOpenSession}
                onClose={handleCancelOpenSession}
                onGoToRegisterSettings={() => navigate(buildSettingsPath('branches', 'registers-cash'))}
                onGoHome={() => navigate('/home')}
                canOpenSession={canOpenPos}
                canManageRegisters={canManageRegisters}
                currentUserId={user?.id}
                registerlessProfile={registerlessProfile}
                registerlessAvailable={Boolean(registerlessProfile?.id)}
                openingSession={isOpeningSession}
            />
            <PosSessionModal
                isOpen={showCloseSessionModal}
                mode="close"
                registers={cashRegisters}
                activeRegisterId={cashRegisterId}
                activeSession={activeSession}
                onCloseSession={handleCloseSession}
                expectedCash={expectedCash}
                expectedCashLoading={expectedCashLoading}
                expectedCashError={expectedCashError}
                openSales={openSessionSales}
                openSalesLoading={openSessionSalesLoading}
                openSalesError={openSessionSalesError}
                onOpenSale={handleOpenSaleFromClose}
                onCancelSale={handleCancelOpenSale}
                onClose={() => setShowCloseSessionModal(false)}
                canCloseSession={canClosePos}
                canOpenSale={canUpdateSale}
                canCancelSale={canDeleteSale}
                currentUserId={user?.id}
            />
            <CancelPendingSaleModal
                isOpen={!!cancelSaleTarget}
                onClose={() => setCancelSaleTarget(null)}
                saleLabel={
                    cancelSaleTarget
                        ? `${resolvePendingSaleLabel(cancelSaleTarget)} #${cancelSaleTarget.id}`
                        : ''
                }
                onConfirm={handleConfirmCancelOpenSale}
            />
            <DocumentTypeModal
                isOpen={showDocumentTypeModal}
                onClose={handleCloseDocumentTypeModal}
                onSelect={handleDocumentTypeSelect}
                canConvert={canConvertToQuotation}
            />
            <ModalClientRegistration
                isOpen={showPurchaseCard}
                searchInputRef={searchInputRef}
                onClose={handleCloseClientModal}
                name={name} setName={setName}
                setClientId={setClientId}
                rut={rut} setRut={setRut}
                email={email} setEmail={setEmail}
                address={address} setAddress={setAddress}
                city={city} setCity={setCity}
                phone={phone} setPhone={setPhone}
                savePurchase={handleSaveClient}
                setModalVarietyPrice={setModalVarietyPrice}
                onClientSelected={handleClientSelected}
                clientType={clientType}
                setClientType={(type) => {
                    const resolvedType = resolveAllowedClientType(type);
                    setClientType(resolvedType);
                    setTypeSelection(resolvedType);
                }}
                clientTypeOptions={posFormatFilterOptions}
                requireClient={pendingDocumentType === 'factura' || pendingConversionAfterClient}
                clientSelectionHandlesPurchase={pendingFinishAfterClient || pendingConversionAfterClient}
                canCreateClient={canCreateClient}
                canReadClient={canReadClient}
            />
            <PosSlotPickerModal
                isOpen={showSlotPickerModal}
                onClose={() => setShowSlotPickerModal(false)}
                entries={posSlotPickerEntries}
                onSelect={handleSlotAddSelection}
                categories={displayCategories}
                selectedCategory={selectedCategory}
                onCategorySelect={handleCategorySelect}
                categoriesLoading={categoriesLoading}
                categoriesError={categoriesError}
            />

            {isEditorPosView && (catalogLoad.message || catalogLoad.error) && (
                <div className="mx-3 my-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-900" role="status">
                    {catalogLoad.message ? <InlineLoading label={catalogLoad.message} /> : <>
                        {catalogLoad.error} <button type="button" className="ml-2 underline" onClick={() => refreshProducts()}>Reintentar</button>
                    </>}
                </div>
            )}
            {isEditorPosView ? (
                <>
                    <div className="flex flex-col min-h-[90vh] bg-gray-100">
                        {catalogPriceNotice && <p role="status" className="mb-2 rounded bg-blue-50 px-3 py-2 text-sm text-blue-800">{catalogPriceNotice} <button type="button" onClick={() => setCatalogPriceNotice('')} className="underline">Cerrar</button></p>}
                        {posError && (
                            <div className="mx-4 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                                <div>
                                    <div>{posError}</div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowCloseSessionModal(true);
                                            setPosError('');
                                        }}
                                        className="text-red-700 underline mt-1"
                                    >
                                        Ver ventas abiertas
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setPosError('')}
                                    className="text-red-500 hover:text-red-700 self-end md:self-auto"
                                    aria-label="Cerrar"
                                >
                                    ✕
                                </button>
                            </div>
                        )}

                        {canReadSales && (
                            <PendingSalesTabs
                                branchId={branchId}
                                activePurchaseId={purchaseId}
                                activePurchaseLiveId={editorActivePurchaseId}
                                activePurchaseLiveTotal={totalAmount}
                                activePurchaseLiveClientId={clientId}
                                activePurchaseLiveClientName={name}
                                onSelect={handleTabSelect}
                                tone="navbar"
                                cashRegisterId={cashRegisterId}
                                cashRegisterSessionId={cashRegisterSessionId}
                                registerlessProfileId={isRegisterlessMode ? registerlessProfile?.id : null}
                                scopeReady={pendingTabsScopeReady}
                                maxOpenSales={activeCashRegister?.maxOpenSales ?? 5}
                                canDelete={canDeleteSale}
                                canCreate={canCreateSale}
                                canSelect={canUpdateSale}
                            />
                        )}

                        <DocumentHeader
                            stickyOffsetClassName="top-14"
                            title={`${orderLabel} N° ${purchaseId || 'Nueva'}`}
                            onTitleClick={openPurchaseModal}
                            showCartButton={false}
                            headerActions={
                                <div className={mobileSearchVisible ? 'hidden md:block' : ''}>
                                    <div className="flex items-center gap-3">
                                        <ProductViewToggle
                                            value={productView}
                                            onChange={setProductView}
                                            allowedViews={POS_PRODUCT_VIEWS}
                                        />
                                        <PosModeTabs onChange={handlePosViewChange} hasClientConnected={posSyncHasClient} />
                                    </div>
                                </div>
                            }
                            totalAmount={cartSaleRounding.totalAmount}
                            totalAmountClassName="hidden md:inline-flex text-blue-500"
                            onTotalClick={openNavbarCart}
                            showTotalCartIcon
                            clientName={name}
                            clientRut={rut}
                            clientGiro={giro}
                            clientType={clientType}
                            emptyClientLabel="Venta Detalle"
                            canChangeClientType={canChangeClientType}
                            clientTypeOptions={posFormatFilterOptions}
                            allowClientTypeToggleOff
                            clientTypeTitle="Filtro de formato:"
                            emptyClientTypeValueLabel="Todos"
                            onSelectClientType={handleClientTypeChange}
                            onChangeClientType={() => {
                                setTypeSelection(clientType || '');
                                setShowTypeModal(true);
                            }}
                            clientActions={
                                <button
                                    type="button"
                                    onClick={handleOpenClientModal}
                                    disabled={!canReadClient && !canCreateClient}
                                    className={`text-xs px-2 py-1 rounded border ${
                                        !canReadClient && !canCreateClient
                                            ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                                            : 'border-blue-500 text-blue-600 hover:bg-blue-50'
                                    }`}
                                >
                                    {name ? 'Cambiar cliente' : 'Agregar cliente'}
                                </button>
                            }
                            clientSectionClassName={
                                mobileSearchVisible
                                    ? 'hidden md:block min-w-0 lg:min-w-[18rem] lg:flex-1'
                                    : 'min-w-0 lg:min-w-[18rem] lg:flex-1'
                            }
                            middleContent={
                                <div className="w-full max-w-[560px]">
                                    {renderCategoryPicker()}
                                </div>
                            }
                            rightContent={
                                <>
                                    <div className={`${mobileSearchVisible ? 'block' : 'hidden'} md:block`}>
                                        <PosBarcodeSearchInput
                                            ref={searchInputRef}
                                            placeholder="Buscar producto..."
                                            value={searchTerm}
                                            onChange={handleSearch}
                                            onKeyDown={handleQuickEntryKeyDown}
                                            onOpenScanner={() => setShowBarcodeScanner(true)}
                                            products={displayProducts}
                                            suggestionVisibility={posSearchSuggestionVisibility}
                                            onFocus={(event) => {
                                                event.target.select();
                                            }}
                                            inputClassName="w-full px-3 py-2.5 text-base md:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    {mobileSearchVisible && !name && (
                                        <div className="mt-2 flex justify-end md:hidden">
                                            <button
                                                type="button"
                                                onClick={handleOpenClientModal}
                                                disabled={!canReadClient && !canCreateClient}
                                                className={`text-xs px-2 py-1 rounded border ${
                                                    !canReadClient && !canCreateClient
                                                        ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                                                        : 'border-blue-500 text-blue-600 hover:bg-blue-50'
                                                }`}
                                            >
                                                Agregar cliente
                                            </button>
                                        </div>
                                    )}
                                    <div className={mobileSearchVisible ? 'hidden md:block' : 'block'}>
                                        <div className="mt-2 text-xs text-gray-700 flex items-center justify-between">
                                            <span>
                                                <span className="font-semibold">Caja:</span>{' '}
                                                {isRegisterlessMode
                                                    ? 'Venta sin caja'
                                                    : (activeCashRegister ? activeCashRegister.name : 'Sin caja')}
                                            </span>
                                            <div className="flex items-center gap-2">
                                                {!isRegisterlessMode && (
                                                    <ScaleStatusPanel
                                                        branchId={branchId}
                                                        cashRegisterId={cashRegisterId}
                                                        cashRegisterSessionId={cashRegisterSessionId}
                                                        isSessionOwnedByUser={isActiveSessionOwnedByUser}
                                                        scaleState={scaleState}
                                                    />
                                                )}
                                                {isRegisterlessMode ? (
                                                    <button
                                                        className="text-blue-600 hover:text-blue-800"
                                                        onClick={() => setShowPosSessionModal(true)}
                                                        type="button"
                                                    >
                                                        Cambiar caja
                                                    </button>
                                                ) : cashRegisterSessionId ? (
                                                    canClosePos && (
                                                        <button
                                                            className="text-blue-600 hover:text-blue-800"
                                                            onClick={() => setShowCloseSessionModal(true)}
                                                            type="button"
                                                        >
                                                            Cerrar caja
                                                        </button>
                                                    )
                                                ) : (
                                                    canOpenPos && (
                                                        <button
                                                            className="text-blue-600 hover:text-blue-800"
                                                            onClick={() => setShowPosSessionModal(true)}
                                                            type="button"
                                                        >
                                                            Abrir caja
                                                        </button>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            }
                        />

                        <div
                            ref={desktopCartPaneRef}
                            className={cartWrapperClassName}
                            onMouseEnter={() => setShowDesktopCartPane(true)}
                        >
                            {!showDesktopCartPane && (
                                <button
                                    type="button"
                                    onClick={() => setShowDesktopCartPane(true)}
                                    className="absolute left-[-3rem] top-1/2 z-10 flex h-14 w-12 -translate-y-1/2 flex-col items-center justify-center gap-0.5 rounded-l-lg border border-r-0 border-gray-200 bg-white text-blue-600 shadow-lg hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    aria-label="Mostrar carrito"
                                    title="Mostrar carrito"
                                >
                                    <FiChevronLeft size={16} />
                                    <FiShoppingCart size={18} />
                                </button>
                            )}
                            {showDesktopCartPane && (
                                <div className="flex h-full min-w-0 flex-col gap-3 px-4 py-4">
                                <CartPanel
                                    items={cartItems}
                                    getVariety={findVarietyForItem}
                                    getItemQuantity={getItemQuantity}
                                    onItemClick={(item, variety) => {
                                        if (!variety) return;
                                        if (isScaleReadingLine(item) && openScaleReadingLineQuantityModal(item, variety)) {
                                            return;
                                        }
                                        const price = variety.VarietyPrices?.[resolveItemPriceIndex(variety, item)];
                                        if (isScaleInputPrice(price)) {
                                            setActiveScaleSelection(null);
                                        }
                                        setSlotModalState(null);
                                        setModalVariety(variety);
                                        setModalVarietyPrice(null);
                                    }}
                                    onRemoveItem={handleRemoveItem}
                                    canEdit={canEditSale}
                                    pendingItemDiscounts={pendingDiscountRequests.items}
                                    pendingOrderDiscount={pendingDiscountRequests.order}
                                    onResumePendingItemDiscount={resumeDiscountAuthorization}
                                    onResumePendingOrderDiscount={resumeDiscountAuthorization}
                                    canOverridePriceType={canEditSale && canOverrideLinePriceType}
                                    getItemPriceTypeOptions={getItemPriceTypeOptions}
                                    onItemPriceTypeChange={handleItemPriceTypeChange}
                                    getItemPricing={resolveCartItemPricing}
                                    getItemValidationError={(item) => getFormatOfferLineIssue(item, findVarietyForItem(item))}
                                    headerContent={cartPaneHeader}
                                    panelClassName={cartPanelClassName}
                                    footerClassName="flex flex-col gap-1 mt-4 bottom-0 pb-1"
                                    itemsSubtotal={itemsSubtotal}
                                    orderDiscountPercentage={orderDiscountPercentage}
                                    orderDiscountAmount={orderDiscountAmount}
                                    totalAmount={cartSaleRounding.totalAmount}
                                    roundingDiscountAmount={cartSaleRounding.roundingDiscountAmount}
                                    roundingPendingMethod={cartSaleRounding.requiresPaymentMethod}
                                />
                                </div>
                            )}
                        </div>

                        <div className={mainContentClassName}>
                            <div className="max-w-7xl mx-auto">
                                {!mobileSearchVisible && (
                                    <div className="mb-4 lg:hidden">
                                        {renderCategoryPicker()}
                                    </div>
                                )}

                                {isSlotProductView ? (
                                    <div className="space-y-2">
                                        <PosFavGestureGuide
                                            isActive={isSlotProductView}
                                            preferencesReady={!preferencesLoading}
                                            seen={myPreferences?.posFavGesturesSeen}
                                            onAcknowledge={handlePosFavGuideAcknowledge}
                                        />
                                        <div className="text-xs font-medium text-gray-500">
                                            Precios favoritos guardados para la caja actual.
                                        </div>
                                        <PosSlotGrid
                                            slots={filteredResolvedPosSlots}
                                            onIncrement={handleFavoritePriceIncrement}
                                            onDecrement={handleFavoritePriceDecrement}
                                            onKeypad={handleFavoritePriceKeypad}
                                            onAddSlot={handleAddPosSlot}
                                            showAddSlot
                                            scaleWeightKg={getLatestScaleWeightKg()}
                                            scaleConnected={posFavScaleConnected}
                                            actionsDisabled={!canEditSale || !canSell}
                                            emptyLabel={selectedCategory === 'all'
                                                ? 'No hay favs configurados todavía.'
                                                : selectedSubcategory === 'all'
                                                    ? 'No hay favs configurados para esta categoría.'
                                                    : 'No hay favs configurados para esta subcategoría.'}
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex items-center justify-between mb-4 text-sm text-gray-600">
                                            <div>
                                                Mostrando {totalVisibleResults} resultados
                                            </div>
                                        </div>

                                        {searchTerm && searchVarietyResults.length > 0 && (
                                            <div className="space-y-4 mb-4">
                                                <ProductGrid view={productView}>
                                                    {searchVarietyResults.map(renderVarietyResultCard)}
                                                </ProductGrid>
                                            </div>
                                        )}

                                        {searchTerm && suggestedSearchVarietyResults.length > 0 && (
                                            <div className="space-y-4 mb-4">
                                                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                    Sugeridos
                                                </div>
                                                <ProductGrid view={productView}>
                                                    {suggestedSearchVarietyResults.map(renderVarietyResultCard)}
                                                </ProductGrid>
                                            </div>
                                        )}

                                        <div className="space-y-8">
                                            {categoriesToRender.map((category) => {
                                                const productsInCategory = prepareProductsForSoldOutDisplay(
                                                    filteredProducts.filter((p) => resolveProductCategoryId(p) === category.id),
                                                    soldOutDisplayMode,
                                                    isProductSoldOutForFilter,
                                                    (a, b) => a.productName.localeCompare(b.productName)
                                                );

                                                if (productsInCategory.length === 0) return null;

                                                return (
                                                    <div key={category.id}>
                                                        <div className="flex items-center gap-4 my-2">
                                                            <div className="flex-grow border-t border-gray-300"></div>
                                                            <h2 className="text-lg font-bold text-gray-700 whitespace-nowrap">
                                                                {category.name || 'Categoria'}
                                                            </h2>
                                                            <div className="flex-grow border-t border-gray-300"></div>
                                                        </div>

                                                        <ProductGrid view={productView}>
                                                            {productsInCategory.map(renderProductCard)}
                                                        </ProductGrid>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                <div className="min-h-screen bg-gray-100">
                    {renderHelperContent()}
                </div>
            )}
            {isEditorPosView && desktopCartActionBar}
            {isEditorPosView && !showPurchaseModal && (
                <button
                    type="button"
                    onClick={toggleMobileSearch}
                    disabled={mobileSearchVisible && hasActiveSearch}
                    className={`md:hidden fixed bottom-6 right-4 z-40 rounded-full shadow-lg p-3 ${
                        mobileSearchVisible && hasActiveSearch
                            ? 'bg-blue-300 text-white cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                    aria-label={mobileSearchVisible ? 'Ocultar búsqueda' : 'Mostrar búsqueda'}
                >
                    {mobileSearchVisible && !hasActiveSearch ? <FiX size={20} /> : <FiSearch size={20} />}
                </button>
            )}

            <BarcodeCameraScanner
                isOpen={showBarcodeScanner}
                onClose={() => setShowBarcodeScanner(false)}
                onDetected={handleBarcodeScannerDetected}
            />

            {/* Modal Component */}
            {modalVariety && (
                <VarietyDetailModal
                    variety={modalVariety}
                    promotionNow={promotionNow}
                    scalePricingPreview={activeScaleWeighing}
                    onClose={closeModalVariety}
                    priceFilter={(price) => isPriceAllowedForType(clientType, price) && (soldOutDisplayMode !== 'hidden' || !price.isSoldOut)}
                    formatOptions={allowedClientTypeOptions}
                    sortByFormat
                    resolveDisplayPrice={resolveVarietyPriceDisplay}
                    quantities={displayQuantities}
                    discounts={discounts}
                    linePriceMeta={linePriceMeta}
                    activeScaleSelection={activeScaleSelection}
                    scaleWeightKg={scaleDisplayWeightKg ?? lastStableScaleReading?.weightKg ?? null}
                    onPriceIncrement={canEditSale && canSell ? handlePriceCardIncrement : null}
                    onPriceDecrement={canEditSale && canSell ? handlePriceCardDecrement : null}
                    onPriceKeypad={canEditSale && canSell ? handlePriceCardKeypad : null}
                    onBackToVarieties={modalProduct ? () => {
                        setModalVariety(null);
                        setModalVarietyPrice(null);
                        setSlotModalState(null);
                        setActiveScaleSelection(null);
                    } : null}
                    onConfirmClose={closeAllProductModals}
                />
            )}
            <DiscountModal
                isOpen={showDiscountModal}
                onClose={() => setShowDiscountModal(false)}
                variety={modalVariety}
                varietyPrice={modalVarietyPrice}
                handleDiscountChange={handleDiscountChange}
                discounts={discounts}
                closeEverything={
                    () => {
                        setModalVarietyPrice(null);
                        setModalVariety(null);
                        setModalProduct(null);
                        setSlotModalState(null);
                        setShowDiscountModal(false);
                        setTimeout(() => {
                            searchInputRef?.current?.focus();
                        }, 100);
                    }
                }
            />
            <OrderDiscountModal
                isOpen={showOrderDiscountModal}
                onClose={() => setShowOrderDiscountModal(false)}
                itemsSubtotal={itemsSubtotal}
                discountableSubtotal={Math.max(itemsSubtotal - computeCartNonDiscountableSubtotal({
                    items: cartItems,
                    getVariety: findVarietyForItem,
                    getItemQuantity,
                    getItemPricing: resolveCartItemPricing,
                }), 0)}
                orderDiscountAmount={orderDiscountAmount}
                orderDiscountPercentage={orderDiscountPercentage}
                onApply={handleOrderDiscountApply}
            />
            <TaxExclusionModal
                isOpen={showTaxModal}
                onClose={() => setShowTaxModal(false)}
                taxes={taxes}
                excludedTaxIds={excludedTaxIds}
                onChange={(ids) => setExcludedTaxIds(ids)}
                disabled={!canExcludeTaxes || taxesLoading || !!taxesError}
            />
            <DiscountAuthorizationModal
                isOpen={showDiscountAuthorizationModal}
                onClose={() => {
                    setShowDiscountAuthorizationModal(false);
                    setPendingDiscountAuthorization(null);
                    setDiscountAuthorizationInitialState(null);
                }}
                branchId={branchId}
                cashRegisterId={cashRegisterId}
                registerlessMode={isRegisterlessMode}
                registerlessProfileId={activeRegisterlessProfile?.id || null}
                discountContext={pendingDiscountAuthorization}
                initialState={discountAuthorizationInitialState}
                onRequested={handleDiscountRequested}
                onAuthorized={(response) => {
                    if (!pendingDiscountAuthorization) return;
                    const authorizationId = response?.id ?? null;
                    if (pendingDiscountAuthorization.type === 'item') {
                        applyItemDiscount(
                            pendingDiscountAuthorization.varietyId,
                            pendingDiscountAuthorization.priceIndex,
                            pendingDiscountAuthorization.discountAmount || 0,
                            authorizationId
                        );
                    } else {
                        applyOrderDiscount(
                            pendingDiscountAuthorization.orderAmount ?? null,
                            pendingDiscountAuthorization.orderPercent ?? null,
                            authorizationId
                        );
                    }
                    clearPendingDiscount(pendingDiscountAuthorization);
                    setPendingDiscountAuthorization(null);
                    setShowDiscountAuthorizationModal(false);
                }}
            />
            {showTypeModal && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    onClick={(event) => event.stopPropagation()}
                >
                    <div
                        className="bg-white rounded-lg shadow max-w-md w-full p-4 mx-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center mb-3">
                            <h3 className="text-lg font-bold">Seleccionar tipo de venta</h3>
                            <button onClick={handleCloseTypeModal} className="text-gray-600 hover:text-black">✕</button>
                        </div>
                        <div className="flex flex-col gap-3">
                            <div className="flex flex-wrap gap-1 border-b border-gray-200">
                                {posFormatFilterOptions.length === 0 ? (
                                    <span className="text-sm text-gray-500">No definido</span>
                                ) : (
                                    [{ value: '', label: 'Todos' }, ...posFormatFilterOptions].map((option) => {
                                        const isActive = typeSelection === option.value;
                                        return (
                                            <button
                                                key={option.value}
                                                type="button"
                                                aria-pressed={isActive}
                                                onClick={() => setTypeSelection(option.value)}
                                                className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px rounded-t-md transition ${
                                                    isActive
                                                        ? 'bg-blue-50 text-blue-700 border-blue-600'
                                                        : 'bg-transparent text-gray-600 border-transparent hover:text-gray-800 hover:bg-gray-50'
                                                }`}
                                            >
                                                {option.label}
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                            <div className="flex justify-end gap-2 mt-2">
                                <button
                                    onClick={handleCloseTypeModal}
                                    className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 text-sm"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={() => {
                                        handleClientTypeChange(typeSelection);
                                        setShowTypeModal(false);
                                    }}
                                    className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                                >
                                    Confirmar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <Modal
                isOpen={!!modalVarietyPrice}
                onClose={closeModal}
                closeModalVariety={closeModalVariety}
                closeModalProduct={() => {
                    setModalProduct(null); setTimeout(() => {
                        searchInputRef?.current?.focus();
                    }, 100);
                }}
                variety={modalVariety}
                edit={canEditSale}
                quantities={quantities}
                varietyPrice={modalVarietyPrice}
                handleQuantityChange={handleModalQuantityChange}
                handleQuantityIncrement={handleQuantityIncrement}
                setShowDiscountModal={setShowDiscountModal}
                discounts={discounts}
                canDiscount={canEditSale && canSell}
                resolveDisplayPrice={resolveVarietyPriceDisplay}
                onConfirmQuantity={slotModalState ? handleConfirmSlotQuantity : null}
                initialQuantity={slotModalState?.initialQuantity ?? null}
                confirmLabel={slotModalState?.quantityMode === 'set' ? 'Actualizar' : 'Agregar'}
                updateOnChange={Boolean(slotModalState) && slotModalState?.source !== 'scaleReadingLine'}
            />
            <PurchaseDetailsModal
                isOpen={showPurchaseModal}
                onClose={() => setShowPurchaseModal(false)}
                purchase={
                    purchaseDetails
                        ? { ...purchaseDetails, purchaseItems: cartItems }
                        : purchaseDetails
                }
                products={products}
                openProductModal={(variety, priceEntry, item) => {
                    setShowPurchaseModal(false);
                    if (isScaleReadingLine(item) && openScaleReadingLineQuantityModal(item, variety)) {
                        return;
                    }
                    setSlotModalState(null);
                    setModalVariety(variety);
                    setModalVarietyPrice(null);
                }}
                handleRemoveItem={handleRemoveItem} // ✅ Pass the function
                canEditItems={canEditSale}
                pendingItemDiscounts={pendingDiscountRequests.items}
                pendingOrderDiscount={pendingDiscountRequests.order}
                onResumePendingItemDiscount={resumeDiscountAuthorization}
                onResumePendingOrderDiscount={resumeDiscountAuthorization}
                getItemPricing={resolveCartItemPricing}
                                    getItemValidationError={(item) => getFormatOfferLineIssue(item, findVarietyForItem(item))}
                footerActions={cartFooterActions}
            />
            {/* Product Modal */}
            {modalProduct && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-30" onClick={(event) => event.stopPropagation()}>
                    <div
                        className="bg-white p-6 rounded-lg shadow-lg max-w-3xl w-full mx-4 relative flex flex-col"
                        style={{ maxHeight: "80vh", display: "flex" }} // Ensures it doesn't exceed 80% of screen height
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Scrollable Content */}
                        <div className="overflow-y-auto px-2" style={{ maxHeight: "70vh" }}>
                            {/* Close Button */}
                            <button onClick={() => {
                                setModalProduct(null); setTimeout(() => {
                                    searchInputRef?.current?.focus();
                                }, 100);
                            }} className="absolute top-2 right-2 text-gray-600 hover:text-black bg-white p-1 rounded-full">
                                ✕
                            </button>

                            {/* Centered Image with Increased Size */}
                            <div className="flex justify-center items-center mb-4">
                                <img
                                    src={modalProduct.productImage || "https://placehold.co/300"}
                                    alt={modalProduct.productName}
                                    className="w-32 h-32 object-cover rounded-lg"
                                />
                            </div>

                            <h2 className="text-xl font-bold text-center">Variedades de {modalProduct.productName}</h2>

                            {(() => {
                                const visibleVarieties = filterVarietiesByGroup(
                                    prepareVarietiesForSoldOutDisplay(
                                        (modalProduct.Varieties || []).filter((variety) => hasSellablePrice(variety, clientType)),
                                        soldOutDisplayMode,
                                        isVarietySoldOutForFilter
                                    ),
                                    modalProduct.VarietyGroups,
                                    activeVarietyGroupId
                                );
                                return (
                                    <>
                                        <VarietyGroupTabs
                                            groups={modalProduct.VarietyGroups}
                                            activeGroupId={activeVarietyGroupId}
                                            onSelect={setActiveVarietyGroupId}
                                            className="mt-4"
                                        />
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                            {visibleVarieties.map((variety) => {
                                                const sellablePrices = variety.VarietyPrices.filter((price) =>
                                                    isPriceAllowedForType(clientType, price)
                                                );
                                                const pricedEntries = sellablePrices.map((entry) => {
                                                    const display = resolveVarietyPriceDisplay(variety, entry, 0);
                                                    return {
                                                        entry,
                                                        shownPrice: Number(display?.price ?? entry.price),
                                                        taxFreeApplied: Boolean(display?.taxFreeApplied),
                                                    };
                                                });
                                                const shownPrices = pricedEntries.map((entry) => entry.shownPrice);
                                                const highestPrice = Math.max(...shownPrices);
                                                const lowestPrice = Math.min(...shownPrices);
                                                const highestPriceUnit = pricedEntries.find((entry) => entry.shownPrice === highestPrice)?.entry?.metricUnit || '';
                                                const lowestPriceUnit = pricedEntries.find((entry) => entry.shownPrice === lowestPrice)?.entry?.metricUnit || '';
                                                const hasTaxFreePrices = pricedEntries.some((entry) => entry.taxFreeApplied);
                                                const isSoldOut = isVarietySoldOut(variety);
                                                const isSelectable = !isSoldOut;

                                                return (
                                                    <div
                                                        key={variety.id}
                                                        className={`relative bg-white border rounded-lg shadow p-4 ${
                                                            isSelectable ? 'cursor-pointer hover:bg-gray-50' : 'cursor-not-allowed'
                                                        } ${
                                                            isSoldOut ? 'opacity-70' : ''
                                                        }`}
                                                        onClick={() => {
                                                            if (!isSelectable) return;
                                                            handleVarietySelection({ variety });
                                                        }}
                                                    >
                                                        {isSoldOut && (
                                                            <span className="absolute top-2 right-2 z-10 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-red-700">
                                                                Agotado
                                                            </span>
                                                        )}
                                                        <div className="flex items-center gap-4 md:gap-8 mb-2">
                                                            <img
                                                                src={variety.varietyImage || "https://placehold.co/200"}
                                                                alt={variety.varietyName}
                                                                className="w-16 h-16 object-cover rounded-lg shadow"
                                                            />
                                                            <p className="text-sm font-semibold break-words mt-2">{variety.varietyName}</p>
                                                        </div>

                                                        <div className="flex justify-between text-sm">
                                                            {highestPrice === lowestPrice ? (
                                                                <p className="text-gray-700 text-sm mt-1 inline-flex items-center">
                                                                    ${formatPesoAmount(highestPrice)} {highestPriceUnit}
                                                                    {hasTaxFreePrices && (
                                                                        <span
                                                                            className="ml-1 inline-flex text-blue-700"
                                                                            title="Precio ajustado sin impuestos excluidos."
                                                                            aria-label="Precio ajustado sin impuestos excluidos."
                                                                        >
                                                                            <FiInfo size={13} />
                                                                        </span>
                                                                    )}
                                                                </p>
                                                            ) : shownPrices.length > 0 && (
                                                                <>
                                                                    <p className="text-gray-700 text-sm mt-1 inline-flex items-center">
                                                                        ${formatPesoAmount(highestPrice)} {highestPriceUnit}
                                                                        {hasTaxFreePrices && (
                                                                            <span
                                                                                className="ml-1 inline-flex text-blue-700"
                                                                                title="Precio ajustado sin impuestos excluidos."
                                                                                aria-label="Precio ajustado sin impuestos excluidos."
                                                                            >
                                                                                <FiInfo size={13} />
                                                                            </span>
                                                                        )}
                                                                    </p>
                                                                    <p className='text-gray-700 text-sm mt-1 inline-flex items-center'>
                                                                        ${formatPesoAmount(lowestPrice)} {lowestPriceUnit}
                                                                        {hasTaxFreePrices && (
                                                                            <span
                                                                                className="ml-1 inline-flex text-blue-700"
                                                                                title="Precio ajustado sin impuestos excluidos."
                                                                                aria-label="Precio ajustado sin impuestos excluidos."
                                                                            >
                                                                                <FiInfo size={13} />
                                                                            </span>
                                                                        )}
                                                                    </p>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {visibleVarieties.length === 0 && (
                                            <p className="mt-4 text-center text-sm text-gray-500">
                                                No hay variedades para este grupo.
                                            </p>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}
        </>
    );

};

export default OnlineShop;
